/**
 * CCNP ENCOR 350-401 Starter Deck
 * 391 cards covering high-frequency exam topics across all 6 domains.
 * All content audited for accuracy against IOS-XE and the ENCOR 350-401 v1.1 blueprint
 * (see docs/encor-v1.1-blueprint.md). Off-blueprint topics removed in v6.
 */

// Increment this when adding/modifying cards so auto-merge picks up changes
export const CONTENT_VERSION = 6;

export function getStarterDeck() {
  return [

    // =========================================================
    // DOMAIN 1.0 — ARCHITECTURE (~9 cards, 15%)
    // =========================================================
    {
      id: "starter-001",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "In Cisco SD-Access, which plane is responsible for mapping endpoint IDs (EIDs) to routing locators (RLOCs)?",
      back: "The **Control Plane** — implemented by LISP (Locator/ID Separation Protocol). The Map-Server/Map-Resolver function runs on the SD-Access control-plane node (typically the Border Node or dedicated server).",
      tags: ["sd-access", "lisp", "control-plane"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-002",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "Which encapsulation protocol does SD-Access use in the **data plane** to carry traffic across the fabric underlay?",
      back: "**VXLAN** (Virtual Extensible LAN) — SD-Access uses VXLAN with a VXLAN GPE (Generic Protocol Extension) header to carry SGT (Scalable Group Tag) information across the fabric.",
      tags: ["sd-access", "vxlan", "data-plane"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-003",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "In Cisco SD-WAN (Viptela), which component is responsible for **centralized routing policy** and distributes routes/policies to vEdge/cEdge routers?",
      back: "**vSmart Controller** — it maintains the OMP (Overlay Management Protocol) sessions with all WAN Edge routers and distributes routing, security, and QoS policies. vManage is the management plane (GUI/API); vBond is the orchestration plane.",
      tags: ["sd-wan", "vsmart", "omp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-004",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "What is the role of the **vBond** orchestrator in Cisco SD-WAN?",
      back: "vBond authenticates all SD-WAN components, facilitates **NAT traversal**, and helps WAN Edge routers discover the vSmart and vManage controllers. It is the only component that must have a public IP address (or be reachable via static NAT).",
      tags: ["sd-wan", "vbond", "nat-traversal"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-005",
      domain: "1.0",
      subdomain: "High Availability",
      front: "What is the difference between **NSF (Non-Stop Forwarding)** and **NSR (Non-Stop Routing)**?",
      back: "**NSF** relies on the neighbor router (helper) to maintain forwarding during a RP switchover — requires neighbor support. **NSR** keeps the standby RP fully synchronized so routing state is maintained internally without any neighbor awareness or cooperation.",
      tags: ["ha", "nsf", "nsr", "graceful-restart"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-006",
      domain: "1.0",
      subdomain: "High Availability",
      front: "In a Cisco StackWise stack, which switch becomes the **active** (master) switch after an election?",
      back: "Election order: **(1) Highest stack member priority** (default 1, range 1–15, set with `switch N priority`), **(2) if tied: the switch that was previously the active**, **(3) if still tied: longest uptime**, **(4) lowest MAC address** as final tiebreaker.",
      tags: ["ha", "stackwise", "election"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-009",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "In SD-Access, what is the role of the **Fabric Edge Node**?",
      back: "The Fabric Edge Node is where **endpoints connect**. It registers endpoints with the control-plane node (LISP MS/MR), encapsulates/decapsulates VXLAN traffic, and enforces SGT-based policy. It is the 'access layer' of the SD-Access fabric.",
      tags: ["sd-access", "fabric-edge", "lisp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 2.0 — VIRTUALIZATION (~6 cards, 10%)
    // =========================================================
    {
      id: "starter-010",
      domain: "2.0",
      subdomain: "VRF",
      front: "What is **VRF-Lite** and how does it differ from MPLS VRF (VRF with MPLS)?",
      back: "**VRF-Lite** uses VRFs without MPLS — traffic isolation is achieved purely through separate routing tables and physical/sub-interfaces between PE routers. No Label Distribution Protocol (LDP) or MPLS label switching is involved. MPLS VRF uses label-switched paths between PE routers for scalable L3VPN.",
      tags: ["vrf", "vrf-lite", "mpls"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-011",
      domain: "2.0",
      subdomain: "GRE",
      front: "What is the **IP protocol number** for GRE, and what is the default GRE tunnel overhead (IPv4)?",
      back: "GRE uses **IP protocol 47**. Default overhead = **24 bytes** (20-byte IP outer header + 4-byte GRE header). With IPv4 transport and no optional GRE fields.",
      tags: ["gre", "tunnel", "protocol-number", "overhead"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-012",
      domain: "2.0",
      subdomain: "DMVPN",
      front: "In DMVPN, what protocol enables **spoke-to-spoke dynamic tunnels** and what are the three DMVPN phases?",
      back: "**NHRP** (Next Hop Resolution Protocol) resolves spoke NBMA (public) addresses for dynamic tunnel building. **Phase 1**: hub-and-spoke only, all traffic through hub. **Phase 2**: spoke-to-spoke tunnels, but hub must be in routing path (no summarization). **Phase 3**: spoke-to-spoke with NHRP redirect/shortcut routing, hub can summarize.",
      tags: ["dmvpn", "nhrp", "phases"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-013",
      domain: "2.0",
      subdomain: "LISP",
      front: "In LISP, what are **EID** and **RLOC**, and which device performs the **ITR** function?",
      back: "**EID** (Endpoint Identifier) = the endpoint's IP address (identity). **RLOC** (Routing Locator) = the router's IP address used for routing across the core. **ITR** (Ingress Tunnel Router) = the router that receives a packet from an EID source, looks up the RLOC in the mapping system, and encapsulates the packet for transport.",
      tags: ["lisp", "eid", "rloc", "itr"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-014",
      domain: "2.0",
      subdomain: "GRE",
      front: "What is **mGRE** (Multipoint GRE) and how does it differ from point-to-point GRE?",
      back: "**mGRE** allows a single tunnel interface to connect to **multiple remote destinations**, using NHRP to dynamically resolve peer addresses. Point-to-point GRE requires a separate tunnel interface per remote site. mGRE is the foundation of DMVPN hub interfaces.",
      tags: ["gre", "mgre", "dmvpn"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-015",
      domain: "2.0",
      subdomain: "VRF",
      front: "What IOS-XE command creates a VRF and what command assigns an interface to it?",
      back: "Create: `vrf definition RED` (modern syntax) or `ip vrf RED` (legacy). Assign interface: `ip vrf forwarding RED` under the interface. **Warning**: assigning `ip vrf forwarding` removes the existing IP address — re-configure it afterward.",
      tags: ["vrf", "ios-xe", "configuration"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 3.0 — INFRASTRUCTURE (~18 cards, 30%)
    // =========================================================
    {
      id: "starter-016",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What are the default OSPF **Hello** and **Dead** timers on a broadcast (Ethernet) interface vs. a point-to-point serial interface?",
      back: "**Broadcast / point-to-point**: Hello = **10 s**, Dead = **40 s**. **NBMA (Frame Relay multipoint)**: Hello = **30 s**, Dead = **120 s**. Dead = 4× Hello in both cases. Mismatched timers prevent adjacency.",
      tags: ["ospf", "timers", "hello", "dead"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-017",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is the OSPF **DR/BDR election** process and which network types trigger it?",
      back: "DR/BDR election occurs on **broadcast** and **NBMA** network types only. Election: **(1) Highest OSPF priority** (default 1, range 0–255; priority 0 = ineligible). **(2) Highest Router ID** as tiebreaker. Election is **non-preemptive** — changing priority does not remove an existing DR/BDR until it fails.",
      tags: ["ospf", "dr", "bdr", "election"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-018",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What LSA types does an **OSPF NSSA (Not-So-Stubby Area)** allow, and what does an **NSSA ABR** do with Type-7 LSAs?",
      back: "NSSA allows **Type-1, 2, 3, 7** LSAs. Type-7 (NSSA External) is generated by the NSSA ASBR for redistributed routes. At the **NSSA ABR**, Type-7 LSAs are **translated to Type-5** LSAs before being flooded into the backbone (Area 0). Type-4 and Type-5 LSAs are blocked from entering the NSSA.",
      tags: ["ospf", "nssa", "lsa-types"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-019",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What are the five **EIGRP metric components** (K-values), and which ones are used by default?",
      back: "K1=Bandwidth, K2=Load, K3=Delay, K4=Reliability, K5=MTU. **Default**: K1=1, K2=0, K3=1, K4=0, K5=0 — so only **Bandwidth** and **Delay** are used in the default composite metric formula. All routers in an EIGRP AS must have matching K-values or adjacency fails.",
      tags: ["eigrp", "metric", "k-values"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-020",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "In EIGRP, what is the **Feasibility Condition (FC)** and what does it guarantee?",
      back: "FC: A neighbor's **Reported Distance (RD)** must be **strictly less than** the local router's **Feasible Distance (FD)** to the same destination. A route meeting FC is a **Feasible Successor** and is guaranteed to be **loop-free**. It is held in the topology table as a backup path and can be used immediately without going Active.",
      tags: ["eigrp", "feasibility-condition", "feasible-successor"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-021",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is the **BGP administrative distance** for eBGP routes vs. iBGP routes on Cisco IOS?",
      back: "**eBGP = 20** | **iBGP = 200**. (OSPF internal = 110, EIGRP internal = 90 for comparison.) iBGP's high AD means it rarely wins over IGP routes in the RIB — by design.",
      tags: ["bgp", "administrative-distance", "ebgp", "ibgp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-022",
      domain: "3.0",
      subdomain: "BGP",
      front: "List the BGP **best-path selection** order (first 8 attributes in Cisco's algorithm).",
      back: "**We Love Oranges As Oranges Mean Pure Refreshment**\n1. **W**eight (highest; Cisco proprietary, local only)\n2. **L**ocal Preference (highest; iBGP scope)\n3. **O**riginate locally (prefer locally originated)\n4. **A**S-Path (shortest)\n5. **O**rigin (IGP < EGP < Incomplete)\n6. **M**ED (lowest; compared only between same AS)\n7. **P**eer type (eBGP > iBGP)\n8. **R**IGP metric to next-hop (lowest)",
      tags: ["bgp", "best-path", "attributes"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-023",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is the BGP **NEXT_HOP** behavior when a route is sent from an eBGP peer to an iBGP peer?",
      back: "By default, a Cisco router does **not** change the NEXT_HOP when re-advertising an eBGP-learned route to iBGP peers. iBGP peers must be able to reach the eBGP next-hop via the IGP, or `next-hop-self` must be configured on the route-reflector/iBGP neighbor to set the next-hop to the advertising router's own IP.",
      tags: ["bgp", "next-hop", "ibgp", "next-hop-self"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-024",
      domain: "3.0",
      subdomain: "STP",
      front: "What are the **STP port states** in 802.1D (classic STP), and how long does the default convergence take?",
      back: "802.1D states: **Blocking → Listening (15 s) → Learning (15 s) → Forwarding**. Also: **Disabled**. Total convergence = up to **50 seconds** (20 s max-age + 15 s listening + 15 s learning). RSTP (802.1w) converges in seconds by using proposal/agreement handshake.",
      tags: ["stp", "802.1d", "port-states", "timers"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-025",
      domain: "3.0",
      subdomain: "STP",
      front: "In Rapid PVST+ (802.1w), what are the **four port roles** and **three port states**?",
      back: "**Roles**: Root Port, Designated Port, Alternate Port (backup for root path), Backup Port (backup for designated). **States**: **Discarding, Learning, Forwarding**. (Blocking and Listening are collapsed into Discarding in RSTP.) Edge ports (PortFast) go directly to Forwarding.",
      tags: ["stp", "rstp", "802.1w", "port-roles"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-026",
      domain: "3.0",
      subdomain: "FHRP",
      front: "Compare **HSRP, VRRP, and GLBP**: which is Cisco-proprietary, which is the standard, and what is the key GLBP advantage?",
      back: "**HSRP** = Cisco proprietary (RFC not standards-based). **VRRP** = IEEE standard (RFC 5798). **GLBP** = Cisco proprietary — unique advantage is **Active/Active load balancing** across up to 4 gateways using a single virtual IP with multiple virtual MACs (AVG assigns different vmac to each host).",
      tags: ["fhrp", "hsrp", "vrrp", "glbp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-027",
      domain: "3.0",
      subdomain: "FHRP",
      front: "What are the default **HSRP Hello and Hold timers**, and what multicast address does HSRPv1 use?",
      back: "Default timers: Hello = **3 seconds**, Hold = **10 seconds**. **HSRPv1** multicast = **224.0.0.2** (all routers), UDP **1985**. **HSRPv2** multicast = **224.0.0.102**, same UDP port. HSRPv2 supports IPv6 and more groups (0–4095 vs. 0–255).",
      tags: ["hsrp", "timers", "multicast"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-029",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What is the difference between **PBR (Policy-Based Routing)** and normal destination-based routing, and how is PBR applied?",
      back: "Normal routing uses the **destination IP** to look up the next-hop in the RIB. PBR can override this based on **source IP, protocol, port, packet size, or other match criteria** using a route-map. PBR is applied **inbound** on an interface (`ip policy route-map`) and is processed **before** the routing table lookup.",
      tags: ["pbr", "policy-routing", "route-map"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-030",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What are the **NTP stratum levels**, and what stratum is a Cisco router that syncs to a stratum-2 server?",
      back: "Stratum 0 = atomic/GPS reference clock (not a network device). Stratum 1 = directly connected to stratum 0. Each hop adds 1. A Cisco router syncing to a **stratum-2** NTP server becomes **stratum 3**. Maximum usable stratum = **15**; stratum 16 = unsynchronized.",
      tags: ["ntp", "stratum"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-031",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is the OSPF **Router ID** selection order on a Cisco router?",
      back: "**(1) Manually configured** `router-id` under `router ospf`. **(2) Highest IP on a loopback interface**. **(3) Highest IP on any active physical interface**. The router-id is chosen at OSPF process startup and does **not** change unless the process is cleared or reset.",
      tags: ["ospf", "router-id"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-032",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What is the default **EIGRP Hello** timer and **Hold** timer on a LAN interface vs. a WAN (T1/multipoint) interface?",
      back: "**LAN (≥1.544 Mbps)**: Hello = **5 s**, Hold = **15 s**. **WAN multipoint / low-bandwidth (<1.544 Mbps)**: Hello = **60 s**, Hold = **180 s**. Hold time = 3× Hello. Mismatched hold timers are allowed but mismatched Hello intervals can cause flapping.",
      tags: ["eigrp", "timers", "hello"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-033",
      domain: "3.0",
      subdomain: "IP Multicast",
      front: "What is the **PIM Sparse Mode** Designated Router (DR) election rule, and what does the DR do?",
      back: "**DR election**: highest configured `ip pim dr-priority` (default 1); tiebreaker = highest IP address. The DR on the **source segment** sends PIM Register messages to the RP. The DR on the **receiver segment** sends PIM Join messages toward the RP. On a LAN with no multicast traffic, the DR is still elected.",
      tags: ["multicast", "pim", "dr", "sparse-mode"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 4.0 — NETWORK ASSURANCE (~6 cards, 10%)
    // =========================================================
    {
      id: "starter-034",
      domain: "4.0",
      subdomain: "SNMP",
      front: "What are the key differences between **SNMPv2c** and **SNMPv3**, and which SNMPv3 security level provides both authentication and encryption?",
      back: "SNMPv2c: community-string auth (cleartext), no encryption. SNMPv3 security levels: **noAuthNoPriv** (no auth, no encryption), **authNoPriv** (MD5/SHA auth, no encryption), **authPriv** (MD5/SHA auth + DES/AES encryption). **`authPriv`** provides both. SNMPv3 also introduces users, groups, and views.",
      tags: ["snmp", "snmpv3", "security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-035",
      domain: "4.0",
      subdomain: "NetFlow",
      front: "What are the **seven key fields** that define a NetFlow flow (the flow key tuple)?",
      back: "A NetFlow v5/v9 flow is uniquely identified by: **(1) Source IP, (2) Destination IP, (3) Source Port, (4) Destination Port, (5) IP Protocol, (6) Type of Service (ToS/DSCP), (7) Input interface (ifIndex)**. Any difference in these 7 fields = a different flow.",
      tags: ["netflow", "flow-key", "monitoring"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-036",
      domain: "4.0",
      subdomain: "Syslog",
      front: "List the **syslog severity levels** from 0 to 7 and their names.",
      back: "**0=Emergency, 1=Alert, 2=Critical, 3=Error, 4=Warning, 5=Notice, 6=Informational, 7=Debug**. Mnemonic: **Every Awesome Cisco Engineer Will Need Ice Daily**. Lower number = higher severity. `logging trap warnings` (level 4) sends 0–4 to the syslog server.",
      tags: ["syslog", "severity", "logging"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-038",
      domain: "4.0",
      subdomain: "IP SLA",
      front: "What is **Cisco IP SLA** and what two things is it commonly used for in combination with another feature?",
      back: "IP SLA generates synthetic test traffic (ICMP echo, UDP jitter, TCP connect, HTTP, etc.) to measure **network performance** (delay, jitter, packet loss, MOS). Commonly used with: **(1) Tracking objects + floating static routes** for path failover (track IP SLA reachability). **(2) RSVP/QoS** validation for voice/video paths.",
      tags: ["ip-sla", "tracking", "static-route"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-039",
      domain: "4.0",
      subdomain: "Span",
      front: "What is the difference between **SPAN**, **RSPAN**, and **ERSPAN**?",
      back: "**SPAN** (Switched Port Analyzer): copies traffic to a local monitor port on the **same switch**. **RSPAN** (Remote SPAN): extends SPAN across multiple switches using a dedicated RSPAN VLAN (traffic carried over trunk). **ERSPAN** (Encapsulated Remote SPAN): encapsulates mirrored traffic in **GRE** and routes it across Layer 3 to a remote collector — requires IOS-XE.",
      tags: ["span", "rspan", "erspan", "monitoring"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 5.0 — SECURITY (~12 cards, 20%)
    // =========================================================
    {
      id: "starter-040",
      domain: "5.0",
      subdomain: "AAA",
      front: "What are the key differences between **TACACS+** and **RADIUS** for AAA?",
      back: "| | TACACS+ | RADIUS |\n|---|---|---|\n| Transport | TCP 49 | UDP 1645/1646 or 1812/1813 |\n| Encryption | Full packet | Password only |\n| AAA | Separated | Combined Auth+Authz |\n| Origin | Cisco proprietary | IETF standard (RFC 2865) |\n| Best for | Device admin (CLI) | Network access (802.1X) |",
      tags: ["aaa", "tacacs+", "radius"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-041",
      domain: "5.0",
      subdomain: "802.1X",
      front: "In 802.1X, identify the three roles: **Supplicant, Authenticator, and Authentication Server**. What protocol runs between the Authenticator and the Authentication Server?",
      back: "**Supplicant** = the client device (runs EAP). **Authenticator** = the switch/AP (runs EAPoL toward client, RADIUS toward server). **Authentication Server** = typically Cisco ISE (runs RADIUS). Protocol between Authenticator and Auth Server = **RADIUS** (UDP 1812/1813). EAPoL (EAP over LAN) runs between Supplicant and Authenticator.",
      tags: ["802.1x", "eap", "radius", "ise"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-042",
      domain: "5.0",
      subdomain: "ACL",
      front: "What is the difference between a **standard ACL** and an **extended ACL** in terms of matching criteria and recommended placement?",
      back: "**Standard ACL**: matches **source IP only** (no ports/protocol). Place **close to the destination** (since you can't match destination). **Extended ACL**: matches source IP, destination IP, protocol, source/dest ports, ToS, etc. Place **close to the source** to drop traffic early. Standard ACLs: numbered 1–99, 1300–1999. Extended: 100–199, 2000–2699.",
      tags: ["acl", "standard", "extended", "placement"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-043",
      domain: "5.0",
      subdomain: "TrustSec",
      front: "What is a **Scalable Group Tag (SGT)** in Cisco TrustSec, and how is policy enforced?",
      back: "An SGT is a **16-bit tag** (value 1–65535) assigned to traffic based on identity (user, device, location) at ingress. Policy enforcement is done at egress via **SGACL (Scalable Group ACL)** — a matrix that defines what SGT→SGT communication is permitted. Tags are carried in the **CMD (Cisco Meta Data)** header or via SXP (SGT Exchange Protocol) for non-TrustSec-capable devices.",
      tags: ["trustsec", "sgt", "sgacl"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-045",
      domain: "5.0",
      subdomain: "Control Plane",
      front: "What is **CoPP (Control Plane Policing)** and why is it important?",
      back: "CoPP uses MQC (Modular QoS CLI) to **rate-limit or drop** traffic destined to the router's **control plane** (CPU). It protects against DoS attacks and CPU overload from excessive routing protocol packets, management traffic, or malicious floods. Applied with `control-plane` configuration and a `service-policy input` statement.",
      tags: ["copp", "security", "control-plane"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-046",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What does **DHCP Snooping** do, and what are **trusted** vs. **untrusted** ports?",
      back: "DHCP Snooping validates DHCP messages to prevent rogue DHCP servers. **Trusted ports**: uplinks to legitimate DHCP servers — all DHCP messages allowed. **Untrusted ports**: client-facing ports — only DHCP **Discover** and **Request** messages are allowed (DHCP **Offer** and **Ack** from untrusted ports are dropped). Builds a binding table used by DAI and IP Source Guard.",
      tags: ["dhcp-snooping", "security", "binding-table"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-047",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What is **Dynamic ARP Inspection (DAI)** and what table does it rely on?",
      back: "DAI intercepts and validates ARP packets on untrusted ports. It compares the ARP sender MAC/IP against the **DHCP Snooping binding table** (or a static ARP ACL). Packets where ARP IP/MAC doesn't match the binding are dropped, preventing ARP spoofing/poisoning attacks. Requires DHCP Snooping to be enabled first.",
      tags: ["dai", "arp-inspection", "dhcp-snooping"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-048",
      domain: "5.0",
      subdomain: "VPN",
      front: "What are the two **IPsec modes** (Transport vs. Tunnel) and when is each used?",
      back: "**Transport Mode**: encrypts only the **payload** (upper-layer data); original IP header preserved. Used for **host-to-host** communication (e.g., GRE over IPsec). **Tunnel Mode**: encrypts the **entire original IP packet** and adds a new IP header. Used for **gateway-to-gateway** VPNs (site-to-site). Tunnel mode is the default for most VPN deployments.",
      tags: ["ipsec", "transport-mode", "tunnel-mode"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-049",
      domain: "5.0",
      subdomain: "AAA",
      front: "What is the difference between **authentication**, **authorization**, and **accounting** in AAA?",
      back: "**Authentication** (AuthN): verifies *who you are* — validates identity (username/password, certificate). **Authorization** (AuthZ): determines *what you can do* — assigns privileges, VLAN, ACL after successful authentication. **Accounting**: records *what you did* — logs commands, session duration, bytes transferred for audit/billing.",
      tags: ["aaa", "authentication", "authorization", "accounting"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-051",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What is **uRPF (Unicast Reverse Path Forwarding)** and what are its two modes?",
      back: "uRPF validates that packets arriving on an interface have a matching source IP route pointing **back out that same interface** (preventing IP spoofing). **Strict mode** (`ip verify unicast source reachable-via rx`): source must be reachable via the exact ingress interface — for single-homed links. **Loose mode** (`... any`): source just needs to exist in the routing table — for multi-homed or asymmetric routing environments.",
      tags: ["urpf", "security", "antispoofing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 6.0 — AUTOMATION (~9 cards, 15%)
    // =========================================================
    {
      id: "starter-052",
      domain: "6.0",
      subdomain: "REST APIs",
      front: "What are the standard **HTTP methods** used in REST APIs and what CRUD operation does each map to?",
      back: "**GET** = Read (retrieve resource, no body). **POST** = Create (create new resource, body contains data). **PUT** = Update/Replace (replace entire resource). **PATCH** = Update/Modify (partial update). **DELETE** = Delete. REST is stateless — each request contains all needed info; no session state on server.",
      tags: ["rest", "http", "api", "crud"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-053",
      domain: "6.0",
      subdomain: "NETCONF/RESTCONF",
      front: "What is **NETCONF**, what transport does it use, and what data modeling language does it use?",
      back: "NETCONF (RFC 6241) is a network management protocol for installing, manipulating, and deleting configuration on network devices. Transport: **SSH (TCP 830)**. Data format: **XML**. Data models: **YANG** (RFC 6020). Uses RPC calls (`<get>`, `<get-config>`, `<edit-config>`, `<commit>`, `<lock>`). Supports candidate, running, and startup datastores.",
      tags: ["netconf", "yang", "xml", "ssh"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-054",
      domain: "6.0",
      subdomain: "NETCONF/RESTCONF",
      front: "How does **RESTCONF** differ from NETCONF, and what port/format does it use?",
      back: "RESTCONF (RFC 8040) exposes YANG-modeled data via **HTTP/HTTPS REST** operations instead of SSH+XML. Uses **HTTPS (TCP 443)**. Data format: **JSON or XML**. Maps HTTP methods to NETCONF operations (GET=get-config, PUT=edit-config, DELETE=delete). RESTCONF does **not** support transactions/rollback like NETCONF's candidate datastore.",
      tags: ["restconf", "yang", "json", "https"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-055",
      domain: "6.0",
      subdomain: "YANG",
      front: "What is a **YANG module** and what are the four key YANG statement types?",
      back: "YANG (Yet Another Next Generation, RFC 6020/7950) is a data modeling language for NETCONF/RESTCONF. Key statement types: **(1) `container`** — groups related nodes (no value itself). **(2) `leaf`** — single value node. **(3) `leaf-list`** — list of values. **(4) `list`** — list of container instances, identified by a `key`. YANG models define the schema; actual data is carried as XML (NETCONF) or JSON/XML (RESTCONF).",
      tags: ["yang", "data-model", "netconf"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-056",
      domain: "6.0",
      subdomain: "Ansible",
      front: "What are the key components of an **Ansible** automation setup: inventory, playbook, play, task, and module?",
      back: "**Inventory**: list of managed hosts (static file or dynamic). **Playbook**: YAML file containing one or more plays. **Play**: maps a group of hosts to a set of tasks. **Task**: a single unit of work that calls a module. **Module**: reusable unit of code that performs an action (e.g., `ios_config`, `uri`, `copy`). Ansible is **agentless** — uses SSH or HTTP APIs; no agent on managed devices.",
      tags: ["ansible", "automation", "playbook"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-057",
      domain: "6.0",
      subdomain: "DNA Center",
      front: "What is **Cisco DNA Center** and what are its four main functional areas?",
      back: "Cisco DNA Center (now Catalyst Center) is the **centralized network management and automation platform** for campus and branch. Four main areas: **(1) Design** — site hierarchy, network settings, IP pools. **(2) Policy** — group-based access policy, application policy. **(3) Provision** — zero-touch provisioning, template deployment. **(4) Assurance** — AI/ML-driven insights, health dashboards, troubleshooting.",
      tags: ["dna-center", "catalyst-center", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-058",
      domain: "6.0",
      subdomain: "Automation Concepts",
      front: "What is the difference between **imperative** and **declarative** configuration models in network automation?",
      back: "**Imperative**: specifies *how* to achieve the desired state — a sequence of commands/steps (e.g., CLI scripts, traditional Ansible `ios_command`). **Declarative**: specifies *what* the desired end state should be; the system figures out how to get there (e.g., YANG+NETCONF edit-config, Terraform, Ansible `ios_config` with `state: present`). Declarative is idempotent by nature — applying the same config multiple times yields the same result.",
      tags: ["automation", "declarative", "imperative", "idempotent"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-059",
      domain: "6.0",
      subdomain: "REST APIs",
      front: "What does an **HTTP 200, 201, 400, 401, 403, 404, and 500** status code mean in the context of REST API calls?",
      back: "**200 OK**: success, response body present. **201 Created**: resource successfully created (POST). **400 Bad Request**: malformed request syntax or invalid parameters. **401 Unauthorized**: authentication required or failed. **403 Forbidden**: authenticated but not authorized. **404 Not Found**: resource doesn't exist. **500 Internal Server Error**: server-side error.",
      tags: ["rest", "http", "status-codes"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-060",
      domain: "6.0",
      subdomain: "Automation Concepts",
      front: "What is the difference between **Puppet**, **Chef**, and **Ansible** in terms of architecture (agent vs. agentless)?",
      back: "**Puppet**: agent-based, pull model (agents poll Puppet Master), uses declarative DSL. **Chef**: agent-based, pull model (Chef Client polls Chef Server), uses Ruby DSL (recipes/cookbooks). **Ansible**: **agentless**, push model (control node pushes via SSH/API), uses YAML playbooks. For network devices: Ansible is most common since it requires no agent installation on network gear.",
      tags: ["ansible", "puppet", "chef", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // BONUS CARDS — Mixed domains, high-frequency exam traps
    // =========================================================
    {
      id: "starter-061",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is **BGP Route Reflector** and what problem does it solve?",
      back: "iBGP requires a **full mesh** between all iBGP speakers (n*(n-1)/2 sessions). A **Route Reflector (RR)** relaxes the iBGP split-horizon rule: the RR reflects iBGP-learned routes to **RR Clients**, eliminating the need for full mesh. Clients only need sessions to the RR. The RR adds `ORIGINATOR_ID` and `CLUSTER_LIST` attributes to prevent loops.",
      tags: ["bgp", "route-reflector", "ibgp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-062",
      domain: "3.0",
      subdomain: "STP",
      front: "What is **BPDU Guard** and where should it be enabled?",
      back: "BPDU Guard places a port in **err-disabled** state if a BPDU is received on that port. It should be enabled on **PortFast (edge) ports** — ports connected to end devices that should never receive BPDUs. Can be enabled globally (`spanning-tree portfast bpduguard default`) or per-interface. Protects against rogue switches being connected to access ports.",
      tags: ["stp", "bpdu-guard", "portfast"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-063",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What OSPF **area types** block Type-5 LSAs, and which also block Type-3 LSAs?",
      back: "**Stub Area**: blocks Type-4 and Type-5 LSAs (external routes); ABR injects a default route (Type-3). **Totally Stubby Area** (Cisco extension): blocks Type-3, Type-4, and Type-5; only default route (Type-3) enters. **NSSA**: blocks Type-4 and Type-5 but allows Type-7 (NSSA external) from local ASBR. **Totally NSSA**: blocks Type-3, Type-4, Type-5; allows Type-7 locally; ABR injects default.",
      tags: ["ospf", "stub", "totally-stubby", "area-types"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-064",
      domain: "3.0",
      subdomain: "QoS",
      front: "What is **DSCP EF (Expedited Forwarding)** and what decimal/binary value does it correspond to?",
      back: "**DSCP EF** (Expedited Forwarding, RFC 3246) is used for **VoIP/real-time traffic** requiring low latency, low jitter, and low loss. DSCP binary: **101110** = decimal **46** = hex 0x2E. In older IP Precedence terms, this maps to CS5 (Precedence 5). Often written as DSCP 46 or PHB EF. Typically placed in a **priority queue** in LLQ (Low Latency Queuing).",
      tags: ["qos", "dscp", "ef", "voip"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-065",
      domain: "1.0",
      subdomain: "High Availability",
      front: "What is **SSO (Stateful Switchover)** and what is required for it to work?",
      back: "SSO synchronizes the **line card and protocol state** between the active and standby RPs so that a switchover is transparent to adjacent devices — no protocol re-convergence. Requirements: **(1) Redundant supervisor/RP**, **(2) NSF (Non-Stop Forwarding)** on the switching/routing processes, **(3) `redundancy mode sso`** configured. Combines with NSF to maintain both forwarding and routing continuity.",
      tags: ["ha", "sso", "redundancy"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-066",
      domain: "4.0",
      subdomain: "NetFlow",
      front: "What is the difference between **NetFlow v5**, **NetFlow v9**, and **IPFIX**?",
      back: "**NetFlow v5**: fixed 7-tuple flow key, IPv4 only, fixed export format. **NetFlow v9** (RFC 3954): template-based, extensible, supports IPv6/MPLS/BGP fields, variable record formats. **IPFIX** (RFC 7011): IETF standard based on NetFlow v9 concepts — template-based, supports variable-length fields, enterprise-defined information elements. IPFIX is the standards-based evolution of NetFlow v9.",
      tags: ["netflow", "ipfix", "v9", "monitoring"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },


    // =========================================================
    // DOMAIN 1.0 — ARCHITECTURE (additional cards)
    // =========================================================
    {
      id: "starter-067",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "What are the three **node roles** in an SD-Access fabric?",
      back: "**Edge Node**: connects endpoints to the fabric, performs host tracking and VXLAN encapsulation. **Border Node**: connects the fabric to external networks (transit, WAN, internet); two types: Internal Border (known prefixes) and External Border (default route). **Control Plane Node**: runs LISP Map-Server/Map-Resolver for EID-to-RLOC mapping.",
      tags: ["sd-access", "fabric-roles", "border-node", "edge-node"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-068",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "What protocol does Cisco SD-WAN use to distribute routes and policies between the vSmart and WAN Edge routers, and over what transport?",
      back: "**OMP (Overlay Management Protocol)** — a Cisco-proprietary routing protocol similar to BGP. OMP runs inside the secure control-plane tunnel between each WAN Edge router and the vSmart controller. Default transport is **DTLS on UDP 12346**; **TLS on TCP 23456** is optional. OMP advertises OMP routes (vRoutes), service routes, and TLOCs (Transport Location identifiers = WAN interface color + IP).",
      tags: ["sd-wan", "omp", "vsmart", "dtls"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-069",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "What is a **TLOC (Transport Location)** in Cisco SD-WAN?",
      back: "A TLOC identifies a specific WAN transport attachment point on a WAN Edge router. A TLOC is a three-tuple: **(system IP, color, encapsulation)**. **Color** is a label for the transport link (e.g., mpls, public-internet, biz-internet). **Encapsulation** is IPsec or GRE. TLOCs are advertised via OMP and used to build the data-plane IPsec tunnels between WAN Edge devices.",
      tags: ["sd-wan", "tloc", "omp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-071",
      domain: "1.0",
      subdomain: "QoS",
      front: "What are the three **QoS models**: Best Effort, IntServ, and DiffServ, and what is the key difference?",
      back: "**Best Effort**: no QoS — all traffic treated equally, FIFO queuing. **IntServ** (Integrated Services): per-flow reservation using **RSVP** — hard guarantees but does not scale (requires state in every router). **DiffServ** (Differentiated Services): marks packets with **DSCP** at ingress and applies per-hop behaviors (PHB) at each router — scalable, no per-flow state. DiffServ is the standard enterprise QoS model.",
      tags: ["qos", "diffserv", "intserv", "rsvp", "dscp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-072",
      domain: "1.0",
      subdomain: "QoS",
      front: "What is the **DSCP AF (Assured Forwarding)** class naming convention and how are drop probabilities encoded?",
      back: "AF PHBs are named **AFxy** where x = class (1–4) and y = drop precedence (1=low, 2=medium, 3=high). DSCP value formula: **(8x + 2y)**. Examples: AF11=10, AF12=12, AF13=14; AF41=34, AF42=36, AF43=38. Higher drop precedence = more likely to be dropped during congestion (used with WRED). 4 classes × 3 drop levels = 12 AF codepoints total.",
      tags: ["qos", "dscp", "af", "assured-forwarding"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-073",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "What is the **DNA Center (Catalyst Center)** role in SD-Access and what are the three main fabric management functions it provides?",
      back: "DNA Center is the **management and orchestration** plane for SD-Access. It provides: **(1) Fabric provisioning** — onboards devices, creates fabric domains, assigns roles (edge/border/control-plane). **(2) Policy management** — creates Scalable Group policies and maps users/devices to SGTs via ISE integration. **(3) Assurance** — AI/ML analytics, client health, network health scores, path trace. DNA Center communicates with devices via NETCONF/RESTCONF and SSH.",
      tags: ["dna-center", "sd-access", "fabric", "policy"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-074",
      domain: "1.0",
      subdomain: "High Availability",
      front: "What is **Cisco VSS (Virtual Switching System)** and how does it differ from StackWise?",
      back: "**VSS** combines two Catalyst 6500/6800 chassis into a **single logical switch** using a Virtual Switch Link (VSL). One chassis is active, one is standby — they share a single management plane, control plane, and appear as one switch to the network. **StackWise** (for access/distribution switches) uses a backplane ring cable and supports up to 8–9 members. VSS is for high-end chassis; StackWise for fixed switches. Both enable active/active uplinks via MEC (Multichassis EtherChannel).",
      tags: ["ha", "vss", "stackwise", "mec"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-076",
      domain: "1.0",
      subdomain: "High Availability",
      front: "What is **StackWise Virtual** (on Catalyst 9000) and how does it differ from traditional StackWise?",
      back: "**StackWise Virtual** connects two Catalyst 9000 switches via dedicated **StackWise Virtual Links (SVL)** using standard 40G/100G interfaces (not proprietary backplane). They form a single logical switch supporting **Dual-Active Detection (DAD)** via StackWise Virtual Fast Hello or BFD. Unlike traditional StackWise (up to 8 members via ring cable), StackWise Virtual is always **exactly 2 switches** and is designed for distribution/core layers. Supports Multichassis EtherChannel (MEC) for active/active uplinks.",
      tags: ["ha", "stackwise-virtual", "catalyst9000", "dad"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-077",
      domain: "1.0",
      subdomain: "LISP",
      front: "In LISP, what are the roles of the **Map-Server (MS)**, **Map-Resolver (MR)**, **ETR**, and **PETR**?",
      back: "**ETR** (Egress Tunnel Router): receives LISP-encapsulated packets, decapsulates them, and delivers to local EIDs; registers EIDs with the Map-Server. **Map-Server (MS)**: accepts EID registrations from ETRs and stores the EID-to-RLOC mappings. **Map-Resolver (MR)**: receives Map-Requests from ITRs and returns Map-Replies from the mapping database. **PETR** (Proxy ETR): provides a path for LISP sites to reach non-LISP destinations — the ITR sends traffic to the PETR which forwards it natively.",
      tags: ["lisp", "ms", "mr", "etr", "petr"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 2.0 — VIRTUALIZATION (additional cards)
    // =========================================================
    {
      id: "starter-078",
      domain: "2.0",
      subdomain: "VXLAN",
      front: "What is **VXLAN** and what are its key parameters: VNI range, UDP port, and header overhead?",
      back: "VXLAN (Virtual Extensible LAN, RFC 7348) extends Layer 2 over a Layer 3 network by encapsulating Ethernet frames in UDP. **VNI (VXLAN Network Identifier)**: 24-bit, providing ~16 million segments (vs. 4096 VLANs). **UDP destination port: 4789** (IANA standard). **Overhead**: 50 bytes (14 Ethernet outer + 20 IP + 8 UDP + 8 VXLAN header). VTEPs (VXLAN Tunnel Endpoints) originate/terminate VXLAN tunnels.",
      tags: ["vxlan", "vni", "vtep", "overlay"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-079",
      domain: "2.0",
      subdomain: "VXLAN",
      front: "What is a **VTEP** in VXLAN and what two methods can be used for VTEP discovery (MAC/IP learning)?",
      back: "A **VTEP** (VXLAN Tunnel Endpoint) is the device that performs VXLAN encapsulation/decapsulation. VTEPs can be physical switches (hardware VTEP) or hypervisors (software VTEP). Two discovery methods: **(1) Multicast underlay** — BUM (Broadcast, Unknown-unicast, Multicast) traffic is flooded to a multicast group; VTEPs learn remote MACs from ingress traffic. **(2) BGP EVPN control plane** — MP-BGP with EVPN (AFI 25, SAFI 70) distributes MAC/IP binding information proactively, eliminating multicast requirement.",
      tags: ["vxlan", "vtep", "evpn", "bgp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-080",
      domain: "2.0",
      subdomain: "DMVPN",
      front: "In DMVPN **Phase 3**, what two NHRP messages enable spoke-to-spoke shortcut routing, and what does the hub do differently from Phase 2?",
      back: "Phase 3 uses **NHRP Redirect** (hub sends to source spoke when it receives spoke-to-spoke traffic) and **NHRP Shortcut** (spoke installs a shortcut route based on NHRP resolution). The hub can now **summarize routes** (unlike Phase 2 which required specific /32 host routes per spoke). When a spoke receives an NHRP Redirect, it sends an NHRP Resolution Request directly to the destination spoke to build a direct tunnel.",
      tags: ["dmvpn", "nhrp", "phase3", "shortcut"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-081",
      domain: "3.0",
      subdomain: "Multicast",
      front: "What is the difference between **PIM Dense Mode (PIM-DM)** and **PIM Sparse Mode (PIM-SM)**?",
      back: "**PIM-DM**: assumes receivers are **everywhere** — floods traffic to all interfaces, then prunes branches with no receivers. Uses push model; no RP. Suited for high-density receiver environments (rarely used today). **PIM-SM**: assumes receivers are **sparse** — traffic is only sent where receivers have explicitly joined via PIM Join messages toward the RP. Uses pull model with shared tree (RPT, rooted at RP) and source tree (SPT). Default mode for enterprise.",
      tags: ["multicast", "pim", "dense-mode", "sparse-mode"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-082",
      domain: "3.0",
      subdomain: "Multicast",
      front: "What does **IGMP** do and what are the differences between IGMPv1, IGMPv2, and IGMPv3?",
      back: "**IGMP** (Internet Group Management Protocol) is used by hosts to signal multicast group membership to the **local router**. **IGMPv1**: join only, no leave message (waits for query timeout). **IGMPv2**: adds explicit **Leave Group** message (reduces leave latency), introduces Group-Specific Query. **IGMPv3**: adds **Source-Specific Multicast (SSM)** support — hosts can specify which sources they want to receive (used with PIM-SSM). IGMPv3 is backward compatible.",
      tags: ["multicast", "igmp", "ssm"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-083",
      domain: "2.0",
      subdomain: "VRF",
      front: "What is **route leaking** between VRFs, and what IOS-XE feature enables it?",
      back: "Route leaking imports routes from one VRF into another VRF's routing table, allowing selective inter-VRF communication without full VRF merging. On IOS-XE, this is done with **`import ipv4 unicast map <route-map>`** under `vrf definition` (using address-family import), or by using the `ip route vrf` command to add static routes that point to another VRF's interface. In MPLS L3VPN, route leaking uses **Route Distinguishers (RD)** and **Route Targets (RT)** — import/export communities control which prefixes are shared.",
      tags: ["vrf", "route-leaking", "mpls", "route-target"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-084",
      domain: "2.0",
      subdomain: "GRE",
      front: "What is **IPsec over GRE** vs. **GRE over IPsec**, and why does the order matter?",
      back: "**GRE over IPsec** (most common): GRE tunnel is created first, then IPsec encrypts the GRE-encapsulated packet. Allows routing protocols (OSPF/EIGRP) and multicast to run inside GRE; IPsec only sees GRE as transport. **IPsec over GRE**: IPsec encrypts the original packet first, then GRE encapsulates it — rare and more complex. The order matters because IPsec (in tunnel mode) cannot carry multicast or broadcast — GRE provides that capability when it is the inner tunnel.",
      tags: ["gre", "ipsec", "tunnel", "vpn"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 3.0 — INFRASTRUCTURE (additional cards)
    // =========================================================
    {
      id: "starter-085",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is an **OSPF virtual link** and when is it required?",
      back: "A virtual link is a logical point-to-point connection that **extends Area 0 (backbone)** through a non-backbone transit area. Required when: **(1) an area is not physically contiguous with Area 0** (discontiguous backbone). **(2) A new area is attached only through another non-backbone area**. Configured under `router ospf` with `area <transit-area> virtual-link <remote-router-id>`. Virtual links cannot traverse stub or NSSA areas.",
      tags: ["ospf", "virtual-link", "backbone", "area0"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-086",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What IOS-XE command configures **OSPF route summarization** at an ABR, and what happens to the component routes?",
      back: "At an ABR: `area <area-id> range <network> <mask>` — summarizes Type-3 LSAs sent into the backbone or other areas. At an ASBR for redistributed routes: `summary-address <network> <mask>` under `router ospf`. When summarization is configured, the component (more-specific) routes are **suppressed** from being advertised individually. The ABR generates a **Null0 route** (discard route) for the summary to prevent routing loops.",
      tags: ["ospf", "summarization", "abr", "area-range"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-087",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is the OSPF **stub router** (max-metric) feature and what LSA value does it use?",
      back: "The OSPF stub router feature (`max-metric router-lsa`) causes a router to advertise a metric of **0xFFFF (65535)** in its Router LSA for all transit links (except stub networks). This makes the router's transit paths unattractive so traffic is rerouted around it — useful during graceful maintenance or startup. `max-metric router-lsa on-startup wait-period 300` prevents the router from attracting traffic for 300 seconds after boot while BGP/IGP converges.",
      tags: ["ospf", "stub-router", "max-metric", "maintenance"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-088",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What are the two OSPF **route filtering** tools and at which point in the topology do they work?",
      back: "**(1) `distribute-list`**: filters routes in/out of the OSPF routing process (affects RIB installation only, not LSA flooding — OSPF database still contains the routes). **(2) `area <x> filter-list prefix <name> in|out`**: applied at an ABR — filters Type-3 LSAs being **injected into** an area (in) or **generated from** an area (out). This actually prevents the LSA from being created, providing true topology filtering. For intra-area routes, only `distribute-list in` works (no ABR filtering within same area).",
      tags: ["ospf", "filtering", "distribute-list", "filter-list"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-089",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What is **EIGRP Named Mode** and what advantages does it provide over classic mode?",
      back: "EIGRP Named Mode (`router eigrp <name>`) consolidates all EIGRP configuration under a single hierarchy: address families (`address-family ipv4/ipv6 unicast autonomous-system <asn>`) and per-interface settings under `af-interface`. Advantages: **(1) Single configuration for IPv4 and IPv6** under one process. **(2) Wide metrics** (64-bit) for high-bandwidth interfaces. **(3) Per-AF summarization and filtering**. **(4) `default-metric` and `variance` per AF**. Named mode is the recommended configuration on IOS-XE 15.x+.",
      tags: ["eigrp", "named-mode", "address-family", "wide-metrics"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-090",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What is **EIGRP Stub routing** and what are the stub route advertisement options?",
      back: "EIGRP Stub (`eigrp stub`) prevents a router from being used as a transit router — it will not forward traffic between its EIGRP neighbors. The hub will not query a stub router during an Active state (reducing query scope). Stub advertisement options: **connected** (default), **summary**, **static** (redistributed statics), **redistributed** (all redistributed), **receive-only** (no routes advertised — pure receiver). Most branch deployments use `eigrp stub connected summary`.",
      tags: ["eigrp", "stub", "query-scope"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-091",
      domain: "3.0",
      subdomain: "BGP",
      front: "What are **BGP Communities** and what are the four well-known community values?",
      back: "BGP Communities (RFC 1997) are optional transitive attributes (32-bit values) attached to prefixes to signal routing policy. Format: `AA:NN` (AS:value). **Well-known communities**: **(1) `no-export`** (0xFFFFFF01) — do not advertise to eBGP peers. **(2) `no-advertise`** (0xFFFFFF02) — do not advertise to any BGP peer. **(3) `local-AS`** (0xFFFFFF03) — do not advertise outside the local AS (also called no-export-subconfed). **(4) `internet`** (0x00000000) — advertise to all peers (default). Extended communities (64-bit) are used for MPLS VPN route targets.",
      tags: ["bgp", "community", "no-export", "no-advertise"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-094",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is **BGP Conditional Advertisement** and how is it configured?",
      back: "BGP Conditional Advertisement (`neighbor x advertise-map <MAP> exist-map <MAP>` or `non-exist-map`) advertises or withdraws a prefix **conditionally** based on the presence or absence of another prefix in the BGP table. Use case: advertise a backup prefix only when the primary is gone. `exist-map` = advertise when the condition prefix EXISTS. `non-exist-map` = advertise when the condition prefix does NOT exist. The BGP process checks the condition every 60 seconds by default.",
      tags: ["bgp", "conditional-advertisement", "policy"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-095",
      domain: "3.0",
      subdomain: "STP",
      front: "What is **MST (Multiple Spanning Tree, 802.1s)** and what is an MST region?",
      back: "MST maps multiple VLANs to a smaller number of spanning tree instances (**MSTIs**), reducing CPU/memory vs. Per-VLAN STP. An **MST region** is a group of switches that share: **(1) same MST configuration name**, **(2) same revision number**, **(3) same VLAN-to-instance mapping table**. All three must match exactly. MST always runs **IST (Internal Spanning Tree, Instance 0)** which interacts with external 802.1D/RSTP regions. Up to 64 MSTIs (0–63) per region.",
      tags: ["stp", "mst", "802.1s", "msti"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-096",
      domain: "3.0",
      subdomain: "STP",
      front: "What is **BPDU Filter** and how does it differ from BPDU Guard?",
      back: "**BPDU Filter**: **suppresses** sending and processing BPDUs on a port. When enabled globally (`spanning-tree portfast bpduguard default` is different — BPDU Filter: `spanning-tree portfast bpdufilter default`), a PortFast port that receives a BPDU will **lose PortFast status** and revert to normal STP — effectively disabling the filter on that port. When enabled **per-interface**, it permanently stops sending/receiving BPDUs — use with extreme caution as it can create loops. **BPDU Guard** err-disables the port; BPDU Filter silently drops BPDUs.",
      tags: ["stp", "bpdu-filter", "bpdu-guard", "portfast"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-097",
      domain: "3.0",
      subdomain: "STP",
      front: "What is **Loop Guard** in STP and what problem does it prevent?",
      back: "Loop Guard prevents a port from **transitioning to Forwarding** if it stops receiving BPDUs (rather than assuming the port should become Designated). Without Loop Guard, a unidirectional link failure (BPDUs stop arriving) causes the Alternate/Root port to transition to Forwarding — creating a loop. With Loop Guard, the port enters **Loop-inconsistent** state and blocks traffic. Configured per-interface or globally (`spanning-tree loopguard default`). **Cannot** be used with PortFast/BPDU Guard (conflicting purpose).",
      tags: ["stp", "loop-guard", "unidirectional"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-098",
      domain: "3.0",
      subdomain: "EtherChannel",
      front: "What are the differences between **LACP** and **PAgP** for EtherChannel negotiation?",
      back: "**LACP** (Link Aggregation Control Protocol): IEEE **802.3ad/802.1ax** standard, works between multi-vendor devices. Modes: **active** (initiates) and **passive** (responds). **PAgP** (Port Aggregation Protocol): **Cisco proprietary**, only between Cisco devices. Modes: **desirable** (initiates) and **auto** (responds). Both require at least one side to be in the active/desirable mode — two passive or two auto = no EtherChannel. LACP supports up to **16 member links** (8 active, 8 standby); PAgP supports up to 8.",
      tags: ["etherchannel", "lacp", "pagp", "802.3ad"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-099",
      domain: "3.0",
      subdomain: "EtherChannel",
      front: "What **load-balancing methods** are available for EtherChannel and which is the Cisco default?",
      back: "Common methods: `src-mac`, `dst-mac`, **`src-dst-mac`**, `src-ip`, `dst-ip`, **`src-dst-ip`** (default on most platforms), `src-port`, `dst-port`, `src-dst-port`. The default is platform-dependent — typically **`src-dst-ip`** on Layer 3 switches. Configured globally: `port-channel load-balance <method>`. The algorithm hashes the selected fields to select the outgoing member link — traffic to/from the same flow always uses the same link.",
      tags: ["etherchannel", "load-balancing", "port-channel"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-100",
      domain: "3.0",
      subdomain: "FHRP",
      front: "What are the HSRP **states** in order, and which state forwards traffic for the virtual IP?",
      back: "HSRP states: **Initial → Learn → Listen → Speak → Standby → Active**. Only the **Active** router forwards traffic for the virtual IP. The **Standby** router monitors the Active and takes over if the Active fails. All other routers are in Listen state. The Active router sends Hellos every 3 seconds; if the Standby misses 3 Hellos (Hold timer = 10 s), it transitions to Active.",
      tags: ["hsrp", "fhrp", "states"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-101",
      domain: "3.0",
      subdomain: "FHRP",
      front: "What is **HSRP preemption** and how is it configured?",
      back: "By default, HSRP is **non-preemptive** — if the original Active router comes back online, it will NOT reclaim Active status from the current Active (to prevent unnecessary failover). **Preemption** allows a router with higher priority to reclaim Active status: `standby <group> preempt [delay minimum <seconds>]`. The delay is recommended (e.g., 60–180 s) to allow routing protocols to converge before taking over forwarding.",
      tags: ["hsrp", "preemption", "fhrp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-104",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What are common **DHCP Option codes** that are exam-relevant: Option 43, 60, 66, 82, 150?",
      back: "**Option 43** (Vendor-Specific Info): vendor-specific data for device provisioning (Cisco-specific encoding). **Option 60** (Vendor Class ID): string sent by client to identify device type. **Option 66** (TFTP Server Name): TFTP server for IP phones/TFTP boot. **Option 82** (DHCP Relay Agent Info): inserted by relay agent with circuit-id/remote-id for policy decisions. **Option 150** (Cisco TFTP): Cisco-proprietary alternative to Option 66 for IP phone TFTP server; preferred for Cisco phones.",
      tags: ["dhcp", "options", "option-43", "option-82", "option-150"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-105",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What is **DNS** and what is the default **TTL behavior** when a Cisco router acts as a DNS server or forwarder?",
      back: "On IOS-XE, `ip domain-lookup` enables DNS resolution (enabled by default). `ip name-server <ip>` sets the DNS server. The router can act as a **DNS forwarder** with `ip dns server` — caches and forwards DNS queries. For hosts, the default DNS port is **UDP/TCP 53**. The router's DNS cache respects the TTL in the DNS response. `no ip domain-lookup` is commonly configured in labs to prevent long delays from typos in exec mode being resolved as hostnames.",
      tags: ["dns", "ip-services", "name-server"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-106",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What is **NTP authentication** and what hash algorithm does it use by default?",
      back: "NTP authentication verifies that time updates come from a trusted server. Configuration: `ntp authenticate`, `ntp authentication-key <id> md5 <password>`, `ntp trusted-key <id>`, and on the server/peer: `ntp server <ip> key <id>`. Default hash: **MD5** (Cisco IOS default). NTP uses **UDP port 123**. Without authentication, any device can masquerade as an NTP server — enabling authentication is a security best practice.",
      tags: ["ntp", "authentication", "md5", "udp-123"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-107",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What is **SNMP** and what are the key differences between SNMP **Get**, **GetNext**, **GetBulk**, **Set**, **Trap**, and **Inform**?",
      back: "**Get**: retrieve a single OID value. **GetNext**: retrieve next OID in MIB tree (for table traversal). **GetBulk** (v2+): retrieve multiple OIDs in one request (efficient table reads). **Set**: write/modify an OID value on the device. **Trap**: **unsolicited** notification from agent to manager — no acknowledgment (UDP, unreliable). **Inform** (v2+): like Trap but **acknowledged** — manager sends a Response; more reliable. Traps use UDP **162** (manager side); SNMP queries use UDP **161** (agent side).",
      tags: ["snmp", "trap", "inform", "get", "ports"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-108",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is **BGP Multipath** and what conditions must be met for iBGP and eBGP multipath?",
      back: "BGP Multipath installs multiple equal-cost BGP paths in the RIB for load balancing. **eBGP multipath** (`maximum-paths <n>`): paths must have same AS-PATH length, same neighbor AS, same MED, same origin. **iBGP multipath** (`maximum-paths ibgp <n>`): same rules but next-hops must be different and IGP cost to next-hop must be equal. `bgp bestpath as-path multipath-relax` allows eBGP multipath even if AS-paths have different AS numbers (but same length). Does not affect BGP best-path selection — just adds more paths to RIB.",
      tags: ["bgp", "multipath", "load-balancing", "maximum-paths"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-109",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is the OSPF **cost** formula and what is the default reference bandwidth?",
      back: "OSPF cost = **Reference Bandwidth / Interface Bandwidth**. Default reference bandwidth = **100 Mbps (10^8)**. A FastEthernet (100 Mbps) interface = cost **1**. A 10 Mbps Ethernet = cost **10**. A 1 Gbps interface would also calculate to cost 1 with default settings (rounded down). **Best practice**: change reference bandwidth with `auto-cost reference-bandwidth 1000` (for Gigabit) or `10000` (for 10G) on **all OSPF routers** to ensure accurate cost calculation.",
      tags: ["ospf", "cost", "reference-bandwidth"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-110",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What is **EIGRP Variance** and what condition must a route meet to use unequal-cost load balancing?",
      back: "`variance <multiplier>` enables unequal-cost load balancing in EIGRP. A Feasible Successor's total metric must be ≤ **(FD × variance)** to be installed in the routing table. The route must also be a **Feasible Successor** (meet the Feasibility Condition: RD < FD of the Successor). Default variance = 1 (equal-cost only). Traffic is load-balanced **proportionally** to the inverse of each path's metric (lower metric = more traffic).",
      tags: ["eigrp", "variance", "unequal-cost", "load-balancing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-111",
      domain: "3.0",
      subdomain: "IP Multicast",
      front: "What is **IGMP Snooping** and why is it important on a Layer 2 switch?",
      back: "IGMP Snooping allows a switch to **listen to (snoop) IGMP messages** between hosts and routers to build a multicast forwarding table. Without it, the switch floods multicast frames out all ports in the VLAN (treating multicast like broadcast). With IGMP Snooping enabled (default on Cisco switches), multicast traffic is only forwarded to ports where IGMP Join messages have been received. The **IGMP Querier** (typically the router) sends periodic queries; IGMP Snooping tracks responses to maintain the table.",
      tags: ["multicast", "igmp-snooping", "layer2"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-112",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What are the **OSPF adjacency requirements** — what parameters must match between neighbors?",
      back: "OSPF neighbors will not form adjacency if these mismatch: **(1) Area ID** — must be same. **(2) Hello/Dead timers** — must match. **(3) Stub area flag** — must both agree the area is stub (or not). **(4) Authentication** — type and credentials must match. **(5) MTU** (for database exchange — `ip ospf mtu-ignore` can override). **(6) Network type** — affects DR/BDR election but doesn't prevent adjacency directly. Note: Router ID and subnet mask do NOT need to match (on point-to-point links, subnet mask is not checked).",
      tags: ["ospf", "adjacency", "requirements", "troubleshooting"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 4.0 — NETWORK ASSURANCE (additional cards)
    // =========================================================
    {
      id: "starter-114",
      domain: "4.0",
      subdomain: "SNMP",
      front: "What are the three **SNMPv3 security levels** and which algorithms are supported for authentication and privacy?",
      back: "**noAuthNoPriv**: no authentication, no encryption — only username check. **authNoPriv**: HMAC-based authentication (MD5 or SHA/SHA-224/256/384/512), no encryption. **authPriv**: authentication + encryption (DES, 3DES, AES-128/192/256). SNMPv3 also introduces: **Users** (USM — User-based Security Model), **Groups** (VACM — View-based Access Control Model), and **Views** (MIB subtree access control). The engine ID uniquely identifies the SNMP entity.",
      tags: ["snmp", "snmpv3", "usm", "vacm", "security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-115",
      domain: "4.0",
      subdomain: "NetFlow",
      front: "What are the **four configuration steps** of Flexible NetFlow (FNF) and how do they relate?",
      back: "**(1) Flow Record**: defines what to **match** (key fields, e.g., src/dst IP) and what to **collect** (non-key fields, e.g., byte count, packet count, timestamps). **(2) Flow Exporter**: defines **where** to send flow data (collector IP/port, transport UDP, source interface, export version 9 or IPFIX). **(3) Flow Monitor**: ties Record + Exporter together. **(4) Apply Flow Monitor to interface**: `ip flow monitor <name> input` (and/or `output`). FNF allows fully customized flow definitions vs. the fixed 7-tuple of NetFlow v5.",
      tags: ["netflow", "flexible-netflow", "flow-record", "flow-monitor"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-116",
      domain: "4.0",
      subdomain: "IP SLA",
      front: "What are the **IP SLA operation types** and what metrics can be measured with the **UDP Jitter** operation?",
      back: "IP SLA operation types: **ICMP Echo** (RTT, availability), **UDP Jitter** (jitter, RTT, packet loss, MOS for VoIP), **TCP Connect** (TCP reachability), **HTTP** (web response), **DNS** (resolution time), **Path Echo** (hop-by-hop). **UDP Jitter** requires an **IP SLA Responder** on the target device. Metrics: round-trip time, one-way delay (requires NTP sync), jitter (delay variation), packet loss percentage, MOS score, ICPIF. UDP Jitter is the most comprehensive operation for voice quality measurement.",
      tags: ["ip-sla", "udp-jitter", "responder", "voip"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-119",
      domain: "4.0",
      subdomain: "Span",
      front: "What are the **limitations of RSPAN** compared to ERSPAN, and what VLAN is used?",
      back: "**RSPAN** requires a **dedicated RSPAN VLAN** (configured with `remote-span` under vlan config) that must be allowed on all trunk links between the source and destination switches. It cannot carry **VLAN 1 traffic** as RSPAN VLAN. Limitations: **(1) Layer 2 only** — cannot route across L3 boundaries. **(2) No filtering on destination switch**. **(3) The RSPAN VLAN must be pruned from all non-RSPAN trunks**. **ERSPAN** overcomes L3 limitation by using GRE encapsulation — can span across routed networks to any collector.",
      tags: ["rspan", "erspan", "span", "monitoring"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-120",
      domain: "4.0",
      subdomain: "DNA Center",
      front: "What is **DNA Center Assurance** and what are the three health score categories it provides?",
      back: "DNA Center Assurance uses AI/ML to collect and analyze telemetry data from network devices and clients. Three health categories: **(1) Network Health** — device health scores (CPU, memory, link errors, neighbor issues). **(2) Client Health** — client experience scores (authentication, DHCP, DNS). **(3) Application Health** — application performance using sensor data or NetFlow. Scores are 1–10 (10=best). The **360-degree view** correlates client issues with network events using AI-driven root cause analysis.",
      tags: ["dna-center", "assurance", "health-score", "ai-ml"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-121",
      domain: "4.0",
      subdomain: "IP SLA",
      front: "How do you configure **IP SLA tracking with a floating static route** for path failover?",
      back: "1. Define operation: `ip sla 1` → `icmp-echo 10.0.0.1` → `frequency 10`. 2. Schedule: `ip sla schedule 1 life forever start-time now`. 3. Create track object: `track 1 ip sla 1 reachability`. 4. Primary static route: `ip route 0.0.0.0 0.0.0.0 10.1.1.1` (AD=1, default). 5. Backup floating static: `ip route 0.0.0.0 0.0.0.0 10.2.2.1 track 1` (or higher AD). When the tracked IP SLA fails, the route is removed from the RIB.",
      tags: ["ip-sla", "tracking", "floating-static", "failover"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-122",
      domain: "4.0",
      subdomain: "Syslog",
      front: "What is the difference between `logging buffered`, `logging console`, `logging monitor`, and `logging host` on IOS-XE?",
      back: "**`logging buffered <level>`**: stores syslog messages in RAM (circular buffer, viewed with `show log`). **`logging console <level>`**: sends messages to the console port — can impact performance if too verbose. **`logging monitor <level>`**: sends messages to VTY (SSH/Telnet) sessions — requires `terminal monitor` to be active in the session. **`logging host <ip>`**: sends messages to an external syslog server (UDP **514** by default). Best practice: `logging buffered informational` and `logging host` for production; limit console logging to warnings or higher.",
      tags: ["syslog", "logging", "ios-xe", "configuration"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-123",
      domain: "4.0",
      subdomain: "NetFlow",
      front: "What is **Sampled NetFlow** and why is it used on high-speed interfaces?",
      back: "Sampled NetFlow analyzes only **1 out of every N packets** (e.g., 1:1000 or 1:512) rather than all packets. This dramatically reduces the **CPU and memory overhead** of flow tracking on high-speed (1G+, 10G) interfaces where processing every packet would be impractical. The sampling ratio is configured in the Flow Sampler and attached to the Flow Monitor. The collector must account for the sampling rate when computing traffic volumes (multiply sampled counts by N). Accuracy decreases with higher sampling ratios.",
      tags: ["netflow", "sampling", "flexible-netflow", "high-speed"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-124",
      domain: "4.0",
      subdomain: "Syslog",
      front: "What is the **syslog message format** on Cisco IOS and what do the fields mean?",
      back: "Format: `%FACILITY-SEVERITY-MNEMONIC: message`. Example: `%OSPF-5-ADJCHG: Process 1, Nbr 10.0.0.2 on Gi0/0 from FULL to DOWN, Neighbor Down: Dead timer expired`. **FACILITY** = software component (OSPF, SYS, LINK). **SEVERITY** = 0–7 digit. **MNEMONIC** = short code identifying the specific event. Timestamps are prepended if `service timestamps log datetime msec` is configured. **Critical**: always configure `service timestamps` for accurate event correlation.",
      tags: ["syslog", "message-format", "facility", "mnemonic"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 5.0 — SECURITY (additional cards)
    // =========================================================
    {
      id: "starter-125",
      domain: "5.0",
      subdomain: "AAA",
      front: "What are the exact **RADIUS UDP port numbers** for authentication vs. accounting, and what changed from legacy to modern?",
      back: "**Modern (RFC 2865/2866)**: Authentication = **UDP 1812**, Accounting = **UDP 1813**. **Legacy (Cisco original)**: Authentication = UDP **1645**, Accounting = UDP **1646**. Most deployments use 1812/1813. RADIUS encrypts only the **password** in the Access-Request (using MD5 and a shared secret); all other attributes are sent in cleartext. TACACS+ encrypts the **entire packet body** using TCP 49.",
      tags: ["radius", "ports", "udp-1812", "aaa"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-126",
      domain: "5.0",
      subdomain: "802.1X",
      front: "What is **MAB (MAC Authentication Bypass)** and when is it used in 802.1X?",
      back: "MAB is a fallback mechanism for devices that **do not support 802.1X** (e.g., printers, IP cameras, VoIP phones). When 802.1X times out (no EAPoL response from client), the switch sends a **RADIUS Access-Request** with the device's **MAC address** as both the username and password. ISE/RADIUS checks the MAC against its database and returns an Access-Accept or Reject. MAB provides identity-based access for non-supplicant devices but is less secure than 802.1X (MAC spoofing is possible).",
      tags: ["802.1x", "mab", "mac-authentication", "ise"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-127",
      domain: "5.0",
      subdomain: "802.1X",
      front: "What is the **802.1X Guest VLAN** vs. **Auth-Fail VLAN** vs. **Critical VLAN**?",
      back: "**Guest VLAN**: assigned when no 802.1X supplicant responds within the configured timeout — for non-802.1X capable devices (limited internet access). **Auth-Fail VLAN**: assigned when a device **fails** authentication (wrong credentials) — provides quarantine access. Different from Guest VLAN. **Critical VLAN** (Critical Authentication): assigned when the **RADIUS server is unreachable** — allows access based on last known state or a defined VLAN. Configured: `authentication event no-response action authorize vlan <guest>`, `authentication event fail action authorize vlan <fail>`, `authentication event server dead action authorize vlan <critical>`.",
      tags: ["802.1x", "guest-vlan", "auth-fail-vlan", "critical-vlan"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-128",
      domain: "5.0",
      subdomain: "ACL",
      front: "What is a **time-based ACL** and how is it configured on IOS-XE?",
      back: "A time-based ACL applies permit/deny rules only during specific **time windows**. Steps: **(1)** Define a time-range: `time-range BUSINESS-HOURS` with `periodic weekdays 8:00 to 18:00` or `absolute start 00:00 1 Jan 2026 end 23:59 31 Dec 2026`. **(2)** Reference it in an ACE: `ip access-list extended LIMIT-ACCESS` → `permit tcp any any eq 80 time-range BUSINESS-HOURS`. **(3)** Apply ACL to interface. Requires **NTP synchronization** on the router for accurate time — without it, the time-range defaults to inactive.",
      tags: ["acl", "time-based", "time-range", "ios-xe"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-129",
      domain: "5.0",
      subdomain: "ACL",
      front: "What is a **reflexive ACL** on IOS-XE and how does it differ from a stateful firewall?",
      back: "A reflexive ACL (`ip access-list extended` with `permit tcp ... reflect <name>`) **dynamically creates temporary inbound permit entries** for return traffic matching outbound sessions. It uses the `evaluate <name>` keyword in the inbound ACL to check the reflexive entries. Supports TCP, UDP, ICMP. **Limitations vs. stateful firewall**: **(1)** Does not track full TCP state (just 5-tuple). **(2)** No application-layer inspection. **(3)** No timeout override for established sessions. **(4)** Not supported on all interface types. ZBFW (Zone-Based Firewall) is Cisco's modern stateful alternative.",
      tags: ["acl", "reflexive", "stateful", "zbfw"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-130",
      domain: "5.0",
      subdomain: "TrustSec",
      front: "What is **SXP (SGT Exchange Protocol)** and why is it needed?",
      back: "SXP (SGT Exchange Protocol) propagates **SGT-to-IP address bindings** to network devices that are **not TrustSec-capable** (cannot natively tag/read SGTs in frames). SXP is a TCP-based protocol that carries IP address ↔ SGT mappings between devices. Example: a TrustSec-capable switch knows a host's SGT from 802.1X authentication; an adjacent non-capable router learns the binding via SXP and can apply policy. SXP uses **TCP port 64999** and can be configured with speaker/listener roles.",
      tags: ["trustsec", "sxp", "sgt", "ip-binding"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-132",
      domain: "5.0",
      subdomain: "VPN",
      front: "What is **MACsec (802.1AE)** and how does it differ from IPsec?",
      back: "**MACsec** (IEEE 802.1AE) encrypts and authenticates **Layer 2 frames** — operates at the Ethernet MAC sublayer. Provides **hop-by-hop** encryption (each link segment is independently encrypted/decrypted). Uses AES-GCM-128 or AES-GCM-256. Identified by **EtherType 0x88E5**. **IPsec** operates at **Layer 3** and provides **end-to-end** encryption across multiple hops (tunnel or transport mode). MACsec is better for high-speed LAN/campus links (hardware-offloaded, low latency); IPsec is better for WAN VPN across the internet. MACsec uses **MKA (MACsec Key Agreement Protocol)** for key exchange.",
      tags: ["macsec", "ipsec", "layer2", "encryption", "802.1ae"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-133",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What is **IPv6 RA Guard** and what attack does it prevent?",
      back: "**IPv6 RA Guard** (RFC 6105) filters **IPv6 Router Advertisement (RA)** messages on switch ports. In IPv6, hosts use RA messages from routers to learn their default gateway and network prefix (SLAAC). A rogue device sending fake RA messages can perform a **man-in-the-middle attack** by making hosts use it as the default gateway. RA Guard is configured on switch ports (`ipv6 nd raguard policy`) to only allow RA messages from trusted (uplink/router) ports and drop them on host-facing ports. Works similarly to DHCP Snooping for IPv4.",
      tags: ["ipv6", "ra-guard", "security", "slaac"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-134",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What is **IPv6 SEND (Secure Neighbor Discovery)** and what cryptographic mechanism does it use?",
      back: "SEND (RFC 3971) secures IPv6 Neighbor Discovery Protocol (NDP) messages by requiring nodes to use **Cryptographically Generated Addresses (CGAs)**. A CGA is an IPv6 address where the interface identifier is derived from a public key hash — proving address ownership. SEND messages are signed with the node's private key and include: **CGA option**, **RSA Signature option**, **Timestamp/Nonce** (anti-replay). SEND prevents NDP spoofing (IPv6 equivalent of ARP spoofing). RA Guard is simpler and more commonly deployed; SEND provides stronger cryptographic guarantees.",
      tags: ["ipv6", "send", "cga", "ndp", "security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-135",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What is **IP Source Guard** and what does it rely on to work?",
      back: "IP Source Guard filters IP packets on **untrusted switch ports** to only allow traffic matching the **DHCP Snooping binding table** (IP + MAC + VLAN + port). It prevents **IP address spoofing** at Layer 2. Two modes: **(1) IP-only** (`ip verify source`) — checks source IP against binding table. **(2) IP + MAC** (`ip verify source port-security`) — checks both IP and MAC. Requires **DHCP Snooping** to be enabled to populate the binding table. Static bindings can be added manually for static-IP devices.",
      tags: ["ip-source-guard", "dhcp-snooping", "antispoofing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-136",
      domain: "5.0",
      subdomain: "CoPP",
      front: "What is the recommended **CoPP class hierarchy** for common traffic types?",
      back: "Typical CoPP classes (from most to least critical): **(1) Routing protocols** (BGP, OSPF, EIGRP) — police to high rate with conforming action = transmit. **(2) Management protocols** (SSH, SNMP, NTP, RADIUS) — police to moderate rate. **(3) ICMP** — police to low rate (prevent ping floods). **(4) Default class** — police remaining traffic to very low rate. CoPP uses `policy-map type control-plane` with `police rate bps burst bytes conform-action transmit exceed-action drop`. Applied: `control-plane` → `service-policy input <name>`.",
      tags: ["copp", "control-plane", "policy-map", "qos"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-137",
      domain: "5.0",
      subdomain: "VPN",
      front: "What are the two phases of **IKE (Internet Key Exchange)** in IPsec VPN negotiation?",
      back: "**IKE Phase 1**: establishes a secure, authenticated channel (ISAKMP SA) between peers. Negotiates: encryption algorithm (AES/3DES), hash (SHA/MD5), authentication (pre-shared key/certificates), DH group (key exchange), lifetime. Two modes: **Main Mode** (6 messages, more secure) or **Aggressive Mode** (3 messages, faster but less secure). **IKE Phase 2 (Quick Mode)**: uses the Phase 1 channel to negotiate the IPsec SAs (encryption/auth for data traffic), PFS (Perfect Forward Secrecy), transform sets, and selectors. Results in two unidirectional IPsec SAs.",
      tags: ["ipsec", "ike", "phase1", "phase2", "vpn"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-138",
      domain: "5.0",
      subdomain: "VPN",
      front: "What is **IKEv2** and how does it improve on IKEv1?",
      back: "IKEv2 (RFC 7296) improvements over IKEv1: **(1) Fewer messages** — 4 messages (vs. 9 for IKEv1 Main Mode) to establish SA. **(2) Built-in NAT traversal** (NAT-T encapsulates IPsec in UDP 4500). **(3) EAP authentication** support for remote-access VPNs. **(4) MOBIKE** (RFC 4555) — supports IP address changes and multi-homing. **(5) Better DDoS resistance** — responder can use cookies. **(6) Asymmetric authentication** — peers can use different auth methods. IKEv2 is mandatory for **FlexVPN** on Cisco IOS-XE.",
      tags: ["ipsec", "ikev2", "flexvpn", "nat-traversal"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-140",
      domain: "5.0",
      subdomain: "TrustSec",
      front: "What is a **TrustSec SGACL** and how is it enforced in the network?",
      back: "An **SGACL** (Security Group ACL) is a policy that controls traffic between **source SGT and destination SGT** pairs. The policy is defined as a **permit/deny matrix** in Cisco ISE. Example: SGT 10 (Employees) → SGT 20 (Servers): permit tcp dst-port 80,443. Enforcement occurs at the **egress** device (where the destination endpoint connects). The enforcement device downloads SGACLs from ISE via RADIUS CoA or NDAC (Network Device Admission Control). **`cts role-based enforcement`** enables SGACL enforcement on the switch.",
      tags: ["trustsec", "sgacl", "sgt", "enforcement", "ise"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-141",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What is **DHCP Snooping** rate limiting and why is it needed?",
      back: "DHCP Snooping rate limiting (`ip dhcp snooping limit rate <pps>`) restricts the number of DHCP messages per second on an untrusted port, preventing **DHCP starvation attacks** (an attacker sends thousands of DHCP Discovers to exhaust the IP pool). Default rate limit is **not set** (unlimited) — it should be configured on all untrusted ports. Recommended: 10–100 pps on access ports. When the rate is exceeded, the port is typically **err-disabled** (if `errdisable recovery` is not configured). The `no ip dhcp snooping information option` disables Option 82 insertion if causing issues with downstream relay agents.",
      tags: ["dhcp-snooping", "rate-limiting", "starvation", "errdisable"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-142",
      domain: "5.0",
      subdomain: "AAA",
      front: "What is the **TACACS+ packet structure** and how does authorization work in TACACS+?",
      back: "TACACS+ separates AAA into three independent services (unlike RADIUS which combines Auth+Authz). Each service uses separate packet types: **Authentication** (START/REPLY/CONTINUE), **Authorization** (REQUEST/RESPONSE), **Accounting** (REQUEST/REPLY). For device admin: after authentication, the switch sends an **Authorization REQUEST** with the service (shell) and command to ISE/TACACS server. The server responds with **PASS** (including any attribute restrictions like privilege level, auto-command) or **FAIL**. Command authorization checks every command before execution.",
      tags: ["tacacs+", "aaa", "authorization", "command-authorization"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 6.0 — AUTOMATION (additional cards)
    // =========================================================
    {
      id: "starter-143",
      domain: "6.0",
      subdomain: "REST APIs",
      front: "What is **idempotency** in REST APIs and which HTTP methods are idempotent?",
      back: "An operation is **idempotent** if performing it multiple times produces the **same result** as performing it once. **Idempotent**: GET, PUT, DELETE, HEAD, OPTIONS. **Non-idempotent**: POST (each POST may create a new resource). PATCH is technically not guaranteed idempotent. PUT is idempotent because replacing a resource with the same data is the same as doing it once. This matters for retry logic — if a PUT or DELETE request times out, it is safe to retry; retrying a POST may create duplicate resources.",
      tags: ["rest", "idempotency", "http", "api"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-145",
      domain: "6.0",
      subdomain: "NETCONF/RESTCONF",
      front: "What are the NETCONF **`<lock>`** and **`<commit>`** operations and when are they used?",
      back: "**`<lock>`**: locks a datastore (running, candidate, or startup) to prevent concurrent modifications from other NETCONF sessions or CLI. The lock must be released with `<unlock>` after changes. Best practice: always lock before editing. **`<commit>`**: applies the **candidate** datastore to the **running** configuration — only available when the `candidate` capability is supported. Allows staged, validated configuration changes before activation. **`<discard-changes>`** reverts the candidate to match running without committing. `<validate>` checks the candidate for errors without committing.",
      tags: ["netconf", "lock", "commit", "candidate-datastore"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-146",
      domain: "6.0",
      subdomain: "NETCONF/RESTCONF",
      front: "What is the **NETCONF `<get-config>`** vs. **`<get>`** operation difference?",
      back: "**`<get-config>`**: retrieves **configuration data** from a specific datastore (running, candidate, startup). Returns only configuration state. **`<get>`**: retrieves **both configuration and operational (state) data** from the running datastore. Use `<get>` for interface statistics, BGP neighbor state, etc. Both support **filters**: subtree filter (XML-based) or XPath filter to limit the response to specific nodes. `<get-config source='running'/>` is the most common operation for configuration backup/verification.",
      tags: ["netconf", "get-config", "get", "operational-data"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-147",
      domain: "6.0",
      subdomain: "Ansible",
      front: "What is an **Ansible Role** and how does the directory structure work?",
      back: "An Ansible Role is a **standardized directory structure** for reusable automation content. Structure: `roles/<role-name>/` with subdirectories: **`tasks/`** (main.yml — task list), **`handlers/`** (triggered by notify), **`vars/`** (high-priority variables), **`defaults/`** (low-priority default variables), **`files/`** (static files), **`templates/`** (Jinja2 templates), **`meta/`** (dependencies). Roles are called in a playbook with `roles: - <role-name>`. Ansible Galaxy (`ansible-galaxy`) is the community repository for sharing roles.",
      tags: ["ansible", "roles", "directory-structure", "galaxy"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-148",
      domain: "6.0",
      subdomain: "Ansible",
      front: "What is the difference between **`ios_command`** and **`ios_config`** Ansible modules?",
      back: "**`ios_command`**: executes **show/exec-mode commands** and returns output as text — not idempotent, no configuration changes. Used for data gathering. **`ios_config`**: applies **configuration lines** to IOS devices — idempotent (checks if line exists before applying), supports `match`, `replace`, `backup`, `save_when` options. Newer equivalent: `cisco.ios.ios_config` (FQCN with collection). For structured data, use **`ios_facts`** (gathers device facts into Ansible variables) or resource modules like `ios_interfaces`, `ios_bgp_global` which use YANG-like structured input.",
      tags: ["ansible", "ios_command", "ios_config", "modules"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-149",
      domain: "6.0",
      subdomain: "Python Networking",
      front: "What is **Netmiko** and how does it differ from **Paramiko**?",
      back: "**Paramiko**: low-level Python SSH library implementing SSHv2 — requires manual channel management, command sending, and output parsing. **Netmiko**: built on top of Paramiko, adds **network-device-specific abstractions**: auto-handling of SSH prompts, paging (terminal length 0), privilege escalation (enable), multi-vendor support (Cisco IOS/XE/XR/NX-OS, Junos, EOS, etc.). Key methods: `connect_handler()`, `send_command()` (uses `expect_string` patterns), `send_config_set()` (enters config mode, sends lines, exits). Netmiko dramatically simplifies SSH automation for network engineers.",
      tags: ["python", "netmiko", "paramiko", "ssh", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-150",
      domain: "6.0",
      subdomain: "Python Networking",
      front: "How do you make a **REST API call** to DNA Center using Python `requests`, including authentication?",
      back: "DNA Center uses **token-based authentication** (JWT). Steps: **(1)** POST to `/dna/system/api/v1/auth/token` with Basic Auth (username:password, base64 encoded). Response contains `Token`. **(2)** Use the token in subsequent requests: `headers = {'X-Auth-Token': token, 'Content-Type': 'application/json'}`. **(3)** GET/POST to API endpoints: `requests.get('https://dnac/dna/intent/api/v1/network-device', headers=headers, verify=False)`. **`verify=False`** skips TLS certificate validation (not recommended in production — import the CA cert instead).",
      tags: ["python", "requests", "dna-center", "rest-api", "token-auth"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-151",
      domain: "6.0",
      subdomain: "DNA Center",
      front: "What are the **DNA Center API categories** and what does the Intent API provide?",
      back: "DNA Center APIs: **(1) Intent API** (Northbound REST): simplified, abstracted API for automation and orchestration — `GET /dna/intent/api/v1/network-device`, `POST /dna/intent/api/v1/template-programmer/deploy`. Used by external systems. **(2) Integration API** (Northbound): for ITSM/IPAM integration (ServiceNow, Infoblox). **(3) Multivendor SDK / Event API**: southbound to non-Cisco devices. **(4) RBAC API**: manages users and roles. The Intent API uses **token auth** (X-Auth-Token) and returns JSON. Rate limited — responses may include task IDs for async operations checked with `/dna/intent/api/v1/task/{taskId}`.",
      tags: ["dna-center", "intent-api", "rest-api", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-152",
      domain: "6.0",
      subdomain: "EEM",
      front: "What is **Cisco EEM (Embedded Event Manager)** and what are the key event detector types?",
      back: "EEM is an IOS/IOS-XE framework that **detects events** and **executes actions** (applets or TCL policies) in response. Key event detectors: **`syslog`** (match a syslog pattern), **`timer`** (cron-like or countdown), **`track`** (object tracking state change), **`interface`** (interface state change — up/down), **`snmp`** (SNMP OID threshold), **`cli`** (CLI command match), **`none`** (manual trigger). EEM Applets are configured inline in IOS config; EEM TCL policies are uploaded scripts. Common use: auto-remediation, custom alerting, backup on config change.",
      tags: ["eem", "event-manager", "applet", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-153",
      domain: "6.0",
      subdomain: "EEM",
      front: "Write a basic **EEM applet** that sends a syslog message when interface GigabitEthernet0/0 goes down.",
      back: "```\nevent manager applet INTF-DOWN\n  event syslog pattern \"%LINEPROTO-5-UPDOWN.*GigabitEthernet0/0.*down\"\n  action 1.0 syslog priority critical msg \"ALERT: Gi0/0 is DOWN - check physical link\"\n  action 2.0 cli command \"enable\"\n  action 3.0 cli command \"show interface GigabitEthernet0/0\"\n```\nKey clauses: `event syslog pattern <regex>` triggers on log match. `action <seq> syslog` sends a custom log. `action <seq> cli command` executes show/config commands. Actions execute in sequence-number order. Use `action <n> set <var> <value>` for variables and `action <n> if` for conditional logic.",
      tags: ["eem", "applet", "syslog", "interface-down"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-154",
      domain: "6.0",
      subdomain: "Automation Concepts",
      front: "What is **Terraform** in the context of network automation and how does its workflow differ from Ansible?",
      back: "Terraform (HashiCorp) is an **Infrastructure as Code (IaC)** tool using **declarative HCL** (HashiCorp Configuration Language). Workflow: **`terraform init`** (download providers), **`terraform plan`** (show what will change), **`terraform apply`** (make changes), **`terraform destroy`** (remove resources). Key concepts: **Providers** (plugins for Cisco NSO, DNA Center, IOS-XE), **Resources** (managed objects), **State file** (tracks current state of infrastructure). vs Ansible: Terraform maintains **state** (knows current vs desired), Ansible is typically stateless and procedural. Terraform is better for lifecycle management; Ansible for day-2 operations.",
      tags: ["terraform", "iac", "hcl", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-155",
      domain: "6.0",
      subdomain: "Automation Concepts",
      front: "What are the key **Git commands** for network automation workflows: init, clone, add, commit, push, pull, branch, merge?",
      back: "**`git init`**: initialize new local repo. **`git clone <url>`**: copy remote repo. **`git add <file>`**: stage changes. **`git commit -m 'msg'`**: commit staged changes to local repo. **`git push`**: upload commits to remote. **`git pull`**: fetch + merge from remote. **`git branch <name>`**: create branch. **`git checkout <branch>`**: switch branches. **`git merge <branch>`**: merge branch into current. **`git status`**: show working tree status. **`git diff`**: show unstaged changes. Network automation use: version control for configs, playbooks, scripts — enables rollback, audit trail, and collaboration.",
      tags: ["git", "version-control", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-156",
      domain: "6.0",
      subdomain: "REST APIs",
      front: "What is **JSON** structure and what are the key data types used in REST API responses?",
      back: "JSON (JavaScript Object Notation) is the primary data format for REST APIs. **Data types**: **(1) Object** `{}` — key-value pairs (keys are strings). **(2) Array** `[]` — ordered list of values. **(3) String** `\"value\"`. **(4) Number** (integer or float). **(5) Boolean** `true`/`false`. **(6) Null** `null`. Example: `{\"device\": {\"hostname\": \"R1\", \"interfaces\": [{\"name\": \"Gi0/0\", \"ip\": \"10.0.0.1\"}], \"managed\": true}}`. In Python, JSON maps to: dict↔object, list↔array, str↔string, int/float↔number. Use `json.loads()` to parse; `json.dumps()` to serialize.",
      tags: ["json", "rest", "api", "python"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-157",
      domain: "6.0",
      subdomain: "NETCONF/RESTCONF",
      front: "What is the **RESTCONF** path structure and how do you GET the running configuration of an interface?",
      back: "RESTCONF URL format: `https://<device>/restconf/data/<module>:<container>/<list-key>`. Example GET for interface: `GET https://192.168.1.1/restconf/data/ietf-interfaces:interfaces/interface=GigabitEthernet0%2F0`. Headers: `Content-Type: application/yang-data+json`, `Accept: application/yang-data+json`. Auth: **HTTP Basic Auth** (username:password base64). The `%2F` encodes `/` in the key. Response is JSON or XML YANG data. RESTCONF root: `/restconf/data` (config+state) or `/restconf/operations` (RPCs).",
      tags: ["restconf", "yang", "ios-xe", "rest"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-158",
      domain: "6.0",
      subdomain: "Automation Concepts",
      front: "What is **CI/CD** in network automation and what tools are commonly used?",
      back: "**CI/CD** (Continuous Integration / Continuous Deployment) applies software DevOps practices to network automation: **(1) CI**: automatically test network changes (lint playbooks, validate YANG config, run network simulations) when code is pushed to Git. **(2) CD**: automatically deploy validated changes to the network. Common tools: **Git** (version control), **GitLab CI / GitHub Actions / Jenkins** (pipeline orchestration), **Ansible** (deployment), **pytest/pyATS** (network testing), **VIRL/CML** (simulation environment for testing). Enables **automated regression testing** before deploying to production.",
      tags: ["ci-cd", "devops", "automation", "pipeline"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-159",
      domain: "6.0",
      subdomain: "Python Networking",
      front: "What is **pyATS (Python Automated Test Systems)** and what is **Genie** in the context of network automation?",
      back: "**pyATS** (Cisco open-source): a Python test automation framework for network engineers. Provides: topology abstraction, device connection management, test case structure. **Genie**: the network automation library built on pyATS that provides: **(1) Parsers** — converts show command output to structured Python dicts (e.g., `device.parse('show ip bgp summary')`). **(2) APIs** — high-level network abstractions. **(3) Models** — YANG-like data models. Key use: `Genie.parse()` eliminates regex-based output parsing. `pyats run job` executes test suites. Heavily used in network CI/CD pipelines.",
      tags: ["python", "pyats", "genie", "testing", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-160",
      domain: "6.0",
      subdomain: "YANG",
      front: "What is the difference between **IETF YANG models**, **OpenConfig YANG models**, and **Native (Cisco) YANG models**?",
      back: "**IETF YANG models**: standardized by IETF (e.g., `ietf-interfaces`, `ietf-routing`) — widely supported, limited to common features. **OpenConfig models**: defined by OpenConfig consortium (Google, Microsoft, etc.) — vendor-neutral, feature-rich, designed for operational data and telemetry (e.g., `openconfig-interfaces`, `openconfig-bgp`). **Native Cisco YANG models** (`Cisco-IOS-XE-*`): Cisco-specific models that cover all IOS-XE features not in IETF/OpenConfig. Cisco augments OpenConfig models with native extensions. Best practice: use OpenConfig or IETF for portability; use native models for Cisco-specific features.",
      tags: ["yang", "ietf", "openconfig", "native", "netconf"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // ADDITIONAL DOMAIN 1.0 — ARCHITECTURE
    // =========================================================
    {
      id: "starter-161",
      domain: "1.0",
      subdomain: "QoS",
      front: "What are the **three phases of MQC (Modular QoS CLI)** configuration and how are they applied?",
      back: "**(1) `class-map`**: defines traffic matching criteria — `match dscp`, `match access-group`, `match protocol`, `match ip precedence`. Use `match-any` (OR) or `match-all` (AND, default). **(2) `policy-map`**: references class-maps and specifies QoS actions — `bandwidth`, `priority` (LLQ), `shape`, `police`, `set dscp`, `queue-limit`. **(3) `service-policy`**: applies the policy-map to an **interface** (`service-policy input/output`) or a **class** (nested policy-map for hierarchical QoS). The hierarchy: interface → policy-map → class-map.",
      tags: ["qos", "mqc", "class-map", "policy-map"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-163",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "What is **vManage** in Cisco SD-WAN and what are its primary functions?",
      back: "**vManage** is the **centralized management and orchestration** component of Cisco SD-WAN. Primary functions: **(1) Single pane of glass** for device management (onboarding, templates, monitoring). **(2) Centralized policy configuration** — creates and pushes policies to vSmart controllers. **(3) Real-time monitoring** — bandwidth, application performance, link health dashboards. **(4) REST API** — all vManage functions are accessible via northbound REST API for automation. **(5) Certificate management** — manages device certificates for zero-touch provisioning (ZTP). vManage runs on-premises or in the cloud.",
      tags: ["sd-wan", "vmanage", "management-plane"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // ADDITIONAL DOMAIN 2.0 — VIRTUALIZATION
    // =========================================================
    {
      id: "starter-164",
      domain: "2.0",
      subdomain: "LISP",
      front: "What is **LISP Map-Notify** and how does the LISP registration process work?",
      back: "LISP registration: **(1)** ETR sends a **Map-Register** (UDP 4342) to the Map-Server with EID prefixes it is authoritative for. **(2)** Map-Server sends a **Map-Notify** back to the ETR as an acknowledgment. **(3)** When an ITR needs to reach an EID, it sends a **Map-Request** (via the Map-Resolver). **(4)** Map-Server either proxies the reply (Map-Server-Proxy-Reply) or forwards the request to the ETR, which sends a **Map-Reply** directly to the ITR. The ITR caches the EID-to-RLOC mapping (default TTL: 24 hours). UDP port **4342** is used for Map-Register/Notify; **4341** for data-plane encapsulation.",
      tags: ["lisp", "map-register", "map-notify", "map-request"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-165",
      domain: "3.0",
      subdomain: "Multicast",
      front: "What is **PIM Sparse Mode (*, G) vs. (S, G)** and what is SPT switchover?",
      back: "**(*, G)** = shared tree entry (any source, group G) — traffic flows via the RP (Rendezvous Point). All receivers join the shared tree rooted at the RP. **(S, G)** = source tree entry (specific source S, group G) — shortest-path tree directly from source to receiver. **SPT Switchover**: by default, Cisco routers switch from shared tree (*, G) to source tree (S, G) after receiving the **first packet** from a source (`ip pim spt-threshold 0`). SPT provides lower latency and more optimal path. Setting threshold to `infinity` keeps traffic on the shared tree.",
      tags: ["multicast", "pim", "spt", "shared-tree", "source-tree"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-166",
      domain: "2.0",
      subdomain: "VRF",
      front: "What is an **MPLS VPN Route Distinguisher (RD)** and a **Route Target (RT)**, and how do they differ?",
      back: "**Route Distinguisher (RD)**: a **64-bit prefix** (format: `AS:nn` or `IP:nn`) **prepended to IPv4 prefixes** to make them globally unique within the MPLS VPN backbone — creates VPNv4 addresses. Does NOT control which VRFs import the route. **Route Target (RT)**: a **BGP extended community** attached to VPNv4 routes that **controls import/export** between VRFs. Export RT = tag attached when exporting from a VRF. Import RT = filter applied when importing into a VRF. A VRF imports routes whose RT matches its import RT. One VRF can have multiple RTs; RDs must be unique per VRF per PE.",
      tags: ["mpls", "vpn", "route-distinguisher", "route-target", "vrf"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // ADDITIONAL DOMAIN 3.0 — INFRASTRUCTURE
    // =========================================================
    {
      id: "starter-167",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is **BGP AS-PATH prepending** and why is it used?",
      back: "AS-PATH prepending artificially **lengthens the AS-PATH** by repeating the local AS number multiple times in the path attribute when advertising routes to eBGP peers. This makes a path less preferred (BGP prefers shorter AS-PATH). Use cases: **(1) Influence inbound traffic** from external ASes to prefer one link over another. **(2) Traffic engineering** for multi-homed BGP connections. Configuration: route-map `set as-path prepend <ASN> [ASN ...]`. Example: prepending 3 times = 3 extra hops. Important: only prepend your **own AS** number (prepending other ASes is considered bad practice).",
      tags: ["bgp", "as-path", "prepending", "traffic-engineering"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-168",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What are the **OSPF LSA types 1–7** and which router generates each?",
      back: "**Type 1 (Router LSA)**: every OSPF router, flooded within an area. **Type 2 (Network LSA)**: DR on broadcast/NBMA segment, flooded within area. **Type 3 (Summary LSA)**: ABR, summarizes routes between areas. **Type 4 (ASBR Summary LSA)**: ABR, advertises reachability to ASBR. **Type 5 (AS External LSA)**: ASBR, flooded throughout OSPF domain (except stubs). **Type 6 (Group Membership LSA)**: MOSPF (rarely used). **Type 7 (NSSA External LSA)**: ASBR within an NSSA area; translated to Type 5 by NSSA ABR. Types 3, 4, 5 are blocked by stub areas; Type 7 is NSSA-specific.",
      tags: ["ospf", "lsa-types", "type1", "type3", "type5", "type7"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-169",
      domain: "3.0",
      subdomain: "STP",
      front: "What is the **STP Root Bridge election** process and what is the Bridge ID format?",
      back: "The **Bridge ID** = **Priority (16 bits) + MAC address (48 bits)**. In PVST+/RSTP, the priority field is split: **4-bit priority (0–61440 in steps of 4096)** + **12-bit System ID Extension (VLAN ID)**. Default priority = **32768**. Root bridge election: **(1) Lowest Bridge Priority** wins. **(2) Tie: Lowest MAC address**. To make a switch the root: `spanning-tree vlan <id> priority 0` (or use `spanning-tree vlan <id> root primary` which sets priority to 24576 or lower than current root). All non-root bridges elect a single root port (toward root) and one designated port per segment.",
      tags: ["stp", "root-bridge", "bridge-id", "priority"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-170",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What is **NAT overload (PAT)** and what are the three NAT translation types on Cisco IOS?",
      back: "**Static NAT**: one-to-one permanent mapping (inside local ↔ inside global). **Dynamic NAT**: maps inside hosts to a pool of global addresses (first-come, first-served). **NAT Overload (PAT)**: maps **many inside hosts to a single global IP** using unique **source port numbers** — allows thousands of simultaneous sessions per IP. Configured: `ip nat inside source list <acl> interface <intf> overload` (interface IP) or `ip nat inside source list <acl> pool <name> overload`. NAT terminology: **Inside local** = private source IP, **Inside global** = translated source IP, **Outside local** = destination as seen from inside, **Outside global** = real destination IP.",
      tags: ["nat", "pat", "overload", "ip-services"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-171",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is **BGP Weight** and how does it differ from Local Preference?",
      back: "**Weight** is a **Cisco-proprietary** BGP attribute. Key differences vs. Local Preference: **(1) Scope**: Weight is **local to the router only** (not advertised to any BGP peer — not even iBGP). Local Preference is **AS-wide** (advertised to all iBGP peers). **(2) Where applied**: Weight is set on the incoming Update from a neighbor (`neighbor x.x.x.x weight <value>` or via route-map on inbound). **(3) Default**: Weight = **0** for learned routes, **32768** for locally originated routes. Higher weight = preferred. **(4) Position in best-path**: Weight is the **FIRST** attribute checked — before all others including Local Preference.",
      tags: ["bgp", "weight", "local-preference", "best-path"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-172",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What is the **EIGRP DUAL (Diffusing Update Algorithm)** process when a route goes Active?",
      back: "When a Successor route is lost and no Feasible Successor exists: **(1)** Route enters **Active** state — router sends **Query** messages to all EIGRP neighbors. **(2)** Neighbors check their topology table: if they have a path, they reply with a **Reply**; otherwise, they forward the Query to their neighbors (diffusing). **(3)** Once ALL replies are received, the router re-computes the best path and returns the route to **Passive** state. If a reply is not received within the **Active Timer** (default **3 minutes**), the route is declared **Stuck-in-Active (SIA)** and the neighbor is reset. SIA is prevented by EIGRP stub routers.",
      tags: ["eigrp", "dual", "active", "query", "sia"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-174",
      domain: "3.0",
      subdomain: "QoS",
      front: "What is **WRED (Weighted Random Early Detection)** and how does it improve on tail drop?",
      back: "**Tail drop** (default): queue is filled until full, then ALL new packets are dropped — causes **TCP global synchronization** (all TCP flows slow down simultaneously then burst together). **WRED**: starts **randomly** dropping packets based on DSCP/IP Precedence **before** the queue is full. Higher-priority traffic (lower DSCP) has a higher minimum threshold (dropping starts later); lower-priority traffic drops earlier. Benefits: **(1) Avoids TCP global sync** by staggering drops across flows. **(2) Provides differentiated service** — better traffic survives longer. Configured under `policy-map` with `random-detect dscp-based`.",
      tags: ["qos", "wred", "tail-drop", "congestion-avoidance"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-175",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What is **IPv6 Stateless Address Autoconfiguration (SLAAC)** and what is EUI-64?",
      back: "**SLAAC** (RFC 4862): an IPv6 host configures its own address without DHCP by combining the **network prefix** (from Router Advertisement) with an **interface identifier**. **EUI-64**: a method to derive the 64-bit interface ID from a 48-bit MAC address by: **(1)** inserting `FF:FE` in the middle, **(2)** flipping bit 7 (universal/local bit) of the first octet. Example: MAC `00:1A:2B:3C:4D:5E` → `02:1A:2B:FF:FE:3C:4D:5E`. Modern systems may use **RFC 7217** (random stable addresses) instead of EUI-64 for privacy. SLAAC requires **`ipv6 unicast-routing`** on the router and sends RAs with M=0, A=1 flags.",
      tags: ["ipv6", "slaac", "eui-64", "autoconfiguration"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-176",
      domain: "3.0",
      subdomain: "STP",
      front: "What is **STP Path Cost** for modern interface speeds and how is it calculated for 802.1D vs. 802.1w?",
      back: "**802.1D (legacy) cost values**: 10 Mbps=100, 100 Mbps=19, 1 Gbps=4, 10 Gbps=2. **802.1w/802.1s (long path cost, recommended)**: uses a larger range — 10 Mbps=2,000,000; 100 Mbps=200,000; 1 Gbps=20,000; 10 Gbps=2,000; 100 Gbps=200. STP selects the root port based on: **(1) Lowest Root Path Cost** to the root bridge. **(2) Lowest sender Bridge ID**. **(3) Lowest sender Port ID**. Set manually: `spanning-tree [vlan <id>] cost <value>` per interface. Use `spanning-tree pathcost method long` to enable 32-bit cost values.",
      tags: ["stp", "path-cost", "802.1w", "root-port"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // ADDITIONAL DOMAIN 4.0 — NETWORK ASSURANCE
    // =========================================================
    {
      id: "starter-177",
      domain: "4.0",
      subdomain: "SNMP",
      front: "What is an **SNMP MIB (Management Information Base)** and what is an OID?",
      back: "An **MIB** is a hierarchical database of **managed objects** that an SNMP agent exposes. Each object is identified by an **OID (Object Identifier)** — a dotted numeric string (e.g., `1.3.6.1.2.1.1.1.0` = sysDescr). The MIB tree is rooted at `iso(1).org(3).dod(6).internet(1)`. Common MIBs: **MIB-II** (RFC 1213) — standard interfaces, IP, TCP, UDP stats. **IF-MIB** — interface counters. **ENTITY-MIB** — hardware inventory. Cisco-specific: `CISCO-PROCESS-MIB` (CPU), `CISCO-MEMORY-POOL-MIB`. OIDs are resolved by name using the MIB file; SNMP managers use MIB files to translate OIDs to human-readable names.",
      tags: ["snmp", "mib", "oid", "monitoring"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-178",
      domain: "4.0",
      subdomain: "Span",
      front: "What are the **SPAN limitations** on Cisco Catalyst switches?",
      back: "Key SPAN limitations: **(1)** Only **one SPAN destination port** per SPAN session (can have multiple SPAN sessions). **(2)** The destination port **does not forward normal traffic** during SPAN — it only receives mirrored frames (access/trunk config is suspended). **(3)** SPAN can **oversubscribe** the destination port (mirroring multiple high-speed sources to a single lower-speed destination causes drops). **(4)** Cannot capture **traffic on the destination port itself**. **(5)** On some platforms, **ingress + egress** SPAN doubles the load. **(6)** Maximum number of SPAN sessions varies by platform (typically 2–4 per switch).",
      tags: ["span", "monitoring", "limitations"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-180",
      domain: "4.0",
      subdomain: "IP SLA",
      front: "What is **IP SLA ICMP Path Echo** and how does it differ from a standard ICMP Echo operation?",
      back: "**ICMP Echo**: measures RTT to a single destination — simple reachability and round-trip latency. **ICMP Path Echo**: extends ICMP Echo by sending probes to **each hop along the path** to the destination (using IP Record Route or incrementing TTL, similar to traceroute). It measures RTT and reachability for **each intermediate hop** in addition to the final destination. Useful for identifying which hop in the path is introducing latency. Requires `ip sla <n>` → `path-echo <dest>`. Note: intermediate routers must respond to ICMP; rate-limited/filtered hops won't respond.",
      tags: ["ip-sla", "icmp", "path-echo", "traceroute"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // ADDITIONAL DOMAIN 5.0 — SECURITY
    // =========================================================
    {
      id: "starter-181",
      domain: "5.0",
      subdomain: "ACL",
      front: "What is the **implicit deny** in an ACL and what is the **established** keyword in a TCP ACE?",
      back: "**Implicit deny**: every ACL ends with an invisible `deny any` statement — any traffic not explicitly permitted is dropped. Always add an explicit permit or verify traffic flows before applying an ACL. **`established` keyword**: matches TCP packets that have the **ACK or RST bit set** — i.e., packets that are part of an **already-established TCP session** (return traffic). `permit tcp any any established` allows return TCP traffic without permitting new inbound connections. Only works for TCP (not UDP which is stateless). Used to permit return traffic without a full stateful firewall.",
      tags: ["acl", "implicit-deny", "established", "tcp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-182",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What is **Port Security** on a Cisco switch and what are the three violation modes?",
      back: "Port Security restricts which **MAC addresses** can send traffic on a switchport. `switchport port-security maximum <n>` sets max MACs. `switchport port-security mac-address sticky` dynamically learns and saves MACs. **Violation modes**: **(1) `shutdown`** (default): port is **err-disabled** on violation — most secure, requires manual recovery or `errdisable recovery`. **(2) `restrict`**: **drops** violating frames, **increments violation counter**, sends SNMP trap/syslog — port stays up. **(3) `protect`**: **drops** violating frames **silently** — no notification, port stays up. Recovery: `shutdown` then `no shutdown` or configure `errdisable recovery cause psecure-violation`.",
      tags: ["port-security", "mac", "errdisable", "violation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-183",
      domain: "5.0",
      subdomain: "VPN",
      front: "What is **SSL/TLS VPN** (Cisco AnyConnect) and how does it differ from IPsec VPN for remote access?",
      back: "**AnyConnect SSL/TLS VPN**: uses **HTTPS (TCP 443)** or **DTLS (UDP 443)** — can traverse most firewalls/NAT without special configuration. Uses TLS for authentication and encryption. Supports **clientless** (browser-based) and **full-tunnel** (AnyConnect client) modes. **IPsec remote access (IKEv2/L2TP)**: may be blocked by firewalls (UDP 500/4500, IP protocol 50). Requires client software. AnyConnect advantages: **(1) Port 443** is almost never blocked. **(2) DTLS** provides UDP-based transport for better performance (especially voice/video). **(3) Posture assessment** (ISE integration). Configured on Cisco ASA or IOS-XE with SSLVPN.",
      tags: ["vpn", "anyconnect", "ssl", "tls", "dtls"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-184",
      domain: "5.0",
      subdomain: "Security Features",
      front: "What is **Storm Control** on a Cisco switch and what types of traffic can it limit?",
      back: "Storm Control monitors **broadcast, multicast, and unknown unicast** traffic rates on switchports and takes action when rates exceed configured thresholds. Configuration: `storm-control broadcast level <rising-threshold> [falling-threshold]`. Thresholds can be specified as **percentage of interface bandwidth**, **packets per second (pps)**, or **bits per second (bps)**. Actions: **`shutdown`** (err-disables port), **`trap`** (SNMP notification only), default is to **drop** excess frames. Use case: prevents broadcast storms, multicast floods, and unknown unicast flooding from taking down a VLAN.",
      tags: ["storm-control", "broadcast", "flood", "switch-security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-185",
      domain: "5.0",
      subdomain: "802.1X",
      front: "What is **Multi-Auth** mode in 802.1X and how does it differ from Multi-Domain and Single-Host?",
      back: "**Single-Host**: only **one authenticated device** per port — any additional MAC causes violation. **Multi-Domain Authentication (MDA)**: allows **one data device + one IP phone** — separates voice (voice VLAN) and data domains, each authenticated independently. **Multi-Auth**: allows **multiple data devices** per port, each independently authenticated — common for small hubs/unmanaged switches on access ports. Each device gets its own RADIUS session. **Multi-Host**: one authentication authorizes the port for all devices — others 'ride' on the first authentication (least secure). Configured: `authentication host-mode <multi-auth|multi-domain|multi-host|single-host>`.",
      tags: ["802.1x", "multi-auth", "multi-domain", "host-mode"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-186",
      domain: "5.0",
      subdomain: "ACL",
      front: "What is the difference between **`ip access-group`** (numbered/named ACL) and **`ip access-class`** on a VTY line?",
      back: "`ip access-group <acl> in|out`: applied on a **physical interface** to filter **transit traffic** passing through the router or traffic to/from the router via that interface. **`ip access-class <acl> in|out`**: applied on a **VTY line** to filter **management access** (SSH/Telnet) to/from the router — `in` filters who can connect to the router, `out` filters where the router can connect. Important: ACLs on VTY lines only match **source IP** for inbound (standard ACL is sufficient). Using an extended ACL on VTY requires careful design — the source port is ephemeral.",
      tags: ["acl", "vty", "access-class", "management-access"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // ADDITIONAL DOMAIN 6.0 — AUTOMATION
    // =========================================================
    {
      id: "starter-188",
      domain: "6.0",
      subdomain: "REST APIs",
      front: "What is the difference between **REST** and **SOAP**, and why is REST preferred for network automation APIs?",
      back: "**SOAP** (Simple Object Access Protocol): XML-based messaging protocol with strict standards (WSDL, WS-Security) — verbose, heavyweight, stateful transactions possible. **REST** (Representational State Transfer): architectural style using HTTP verbs (GET/POST/PUT/DELETE) with JSON/XML — lightweight, stateless, human-readable, easy to debug with `curl`/Postman. REST preferred for network APIs because: **(1)** JSON is simpler than XML for most use cases. **(2)** Stateless design scales better. **(3)** Standard HTTP tools work natively. **(4)** Cisco DNA Center, vManage, APIC, and most modern NMS use REST. SOAP is legacy — still found in some NMS integrations.",
      tags: ["rest", "soap", "api", "json", "xml"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-189",
      domain: "6.0",
      subdomain: "Ansible",
      front: "What is **Ansible inventory** and what are the two formats supported?",
      back: "Ansible inventory defines the **hosts** (network devices) to manage. **INI format**: `[group-name]\\nhost1 ansible_host=10.0.0.1 ansible_user=admin\\nhost2`. Groups can nest: `[all:children]\\nrouters\\nswitches`. **YAML format**: `all: hosts: router1: ansible_host: 10.0.0.1 ansible_network_os: ios`. Network-device-specific variables: `ansible_network_os` (ios, eos, junos), `ansible_connection` (network_cli, netconf, httpapi), `ansible_user`, `ansible_password`, `ansible_become`. **Dynamic inventory**: scripts or plugins (e.g., DNA Center plugin) that query a source of truth to generate inventory at runtime.",
      tags: ["ansible", "inventory", "ini", "yaml", "network_cli"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-190",
      domain: "6.0",
      subdomain: "Python Networking",
      front: "What is **NAPALM** (Network Automation and Programmability Abstraction Layer with Multivendor support)?",
      back: "NAPALM is a Python library providing a **unified API** for interacting with multiple network OS vendors. It abstracts vendor-specific CLI/API differences behind a common interface. Key methods: `get_facts()`, `get_interfaces()`, `get_bgp_neighbors()`, `load_merge_candidate()`, `load_replace_candidate()`, `compare_config()`, `commit_config()`, `discard_config()`, `rollback()`. Supported drivers: ios, eos, junos, iosxr, nxos. NAPALM's **candidate config + diff + commit** workflow provides **configuration validation before deployment**. Used in Ansible (napalm collection), Salt, and standalone Python scripts.",
      tags: ["python", "napalm", "multivendor", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-191",
      domain: "6.0",
      subdomain: "Automation Concepts",
      front: "What is **Jinja2** templating and how is it used in Ansible for network device configuration?",
      back: "Jinja2 is a Python templating engine used to generate **text output from templates + variables**. In Ansible: template files (`.j2` extension) contain static text + Jinja2 expressions. Variables come from inventory, host_vars, group_vars, or playbook vars. Syntax: `{{ variable }}` (output), `{% if condition %}...{% endif %}` (conditional), `{% for item in list %}...{% endfor %}` (loop). Example: generate interface configs with `{% for intf in interfaces %}interface {{ intf.name }}\\n  ip address {{ intf.ip }} {{ intf.mask }}{% endfor %}`. The `template` module deploys rendered files to devices (or use `ios_config` with template input).",
      tags: ["jinja2", "ansible", "templating", "configuration"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-192",
      domain: "6.0",
      subdomain: "REST APIs",
      front: "What is **OAuth 2.0** and how does it relate to API authentication in Cisco platforms?",
      back: "OAuth 2.0 is an **authorization framework** (RFC 6749) that allows third-party applications to access resources without sharing credentials. Flow (Authorization Code): **(1)** Client redirects user to auth server → user authenticates. **(2)** Auth server returns **authorization code**. **(3)** Client exchanges code for **access token** (short-lived JWT) + refresh token. **(4)** Client uses access token in API calls: `Authorization: Bearer <token>`. Cisco Webex, Meraki, and some DNA Center integrations use OAuth 2.0. **API keys** (simpler, long-lived): used by Meraki (X-Cisco-Meraki-API-Key header). **Token-based auth** (JWT): used by DNA Center (X-Auth-Token).",
      tags: ["oauth", "api", "authentication", "jwt", "rest"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-194",
      domain: "6.0",
      subdomain: "Automation Concepts",
      front: "What is **NSO (Network Services Orchestrator)** and what is its role in network automation?",
      back: "Cisco NSO (formerly Tail-f) is a **network orchestration platform** that provides: **(1) Multi-vendor device abstraction** using NEDs (Network Element Drivers) — standardizes device interaction. **(2) Transaction-based configuration** with rollback — applies changes atomically and rolls back if any device fails. **(3) Service modeling** using YANG — define services (L3VPN, VLAN, etc.) as YANG models; NSO renders device-specific CLI. **(4) Northbound APIs** — REST, NETCONF, CLI for integration with OSS/BSS. **(5) Synchronization** — keeps NSO's CDB (Configuration Database) in sync with actual device configs. NSO is Cisco's enterprise network automation platform for complex multi-device workflows.",
      tags: ["nso", "automation", "orchestration", "ned", "transaction"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-195",
      domain: "6.0",
      subdomain: "NETCONF/RESTCONF",
      front: "What is the **NETCONF `<copy-config>`** operation and what datastores does it support?",
      back: "`<copy-config>` copies the entire contents of one datastore to another. Supported datastores: **running**, **candidate**, **startup**. Common uses: **(1)** `copy-config source=running target=startup` — save running config (equivalent to `write memory`). **(2)** `copy-config source=candidate target=running` — commit candidate (alternative to `<commit>`). **(3)** `copy-config source=<url> target=running` — load config from external file. **(4)** Backup: `copy-config source=running target=<url>`. This is a **replace** operation — the entire target datastore is replaced; use `<edit-config>` for partial changes.",
      tags: ["netconf", "copy-config", "datastore", "startup"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-197",
      domain: "6.0",
      subdomain: "Python Networking",
      front: "What is the **`ncclient`** Python library and what operations does it support for NETCONF?",
      back: "`ncclient` is a Python library for NETCONF client functionality. Connection: `manager.connect(host, port=830, username, password, hostkey_verify=False)`. Key methods: `m.get_config(source='running')`, `m.get(filter=('xpath', '/native'))`, `m.edit_config(target='running', config=xml_string)`, `m.commit()`, `m.lock('running')`, `m.unlock('running')`, `m.copy_config(source='running', target='startup')`, `m.dispatch(RPC_element)`. Responses are `lxml` Element objects — parse with XPath. `ncclient` handles SSH transport (TCP 830), NETCONF hello/capabilities exchange, and chunked framing automatically.",
      tags: ["python", "ncclient", "netconf", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-198",
      domain: "6.0",
      subdomain: "EEM",
      front: "What is an **EEM timer applet** and how do you configure a periodic task every 5 minutes?",
      back: "EEM timer events trigger applets at defined intervals. Types: **`timer cron`** (cron-style schedule), **`timer watchdog`** (fires at interval, re-arms automatically), **`timer countdown`** (fires once after delay). Example for every 5 minutes: `event manager applet PERIODIC-BACKUP` → `event timer watchdog time 300` (seconds) → `action 1.0 cli command \"enable\"` → `action 2.0 cli command \"copy running-config flash:backup.cfg\"` → `action 3.0 syslog msg \"Config backed up\"`. The `watchdog` timer re-triggers every 300 seconds continuously. Use `event manager run APPLET-NAME` to manually trigger for testing.",
      tags: ["eem", "timer", "watchdog", "cron", "applet"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-199",
      domain: "6.0",
      subdomain: "REST APIs",
      front: "What is **Postman** and how is it used for testing Cisco REST APIs?",
      back: "Postman is a **GUI API testing tool** for constructing and testing HTTP requests. For Cisco network APIs: **(1)** Set request type (GET/POST/PUT/DELETE) and URL. **(2)** Add **Auth** (Basic Auth, Bearer Token, or API Key headers). **(3)** Add **Headers** (`Content-Type: application/json`, `X-Auth-Token`). **(4)** Add **Body** (JSON payload for POST/PUT). **(5)** Send and inspect response. **Collections**: group related API calls. **Environments**: store variables (base URL, tokens). **Pre-request Scripts**: JavaScript to obtain auth token before each call. Postman is used to explore DNA Center, vManage, APIC, Meraki APIs before automating with Python.",
      tags: ["postman", "rest", "api", "testing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-200",
      domain: "6.0",
      subdomain: "Automation Concepts",
      front: "What is the difference between **Configuration Management** and **Network Orchestration** tools?",
      back: "**Configuration Management** (Ansible, Salt, Puppet): manages the **desired state of individual devices** — pushes config, ensures compliance, remediates drift. Focused on device-level operations. **Network Orchestration** (NSO, Cisco DNA Center, Cisco Crosswork): coordinates **end-to-end service workflows** across multiple devices and systems — provisions L3VPN (touching PE, CE, route reflectors simultaneously), manages lifecycle (create/modify/delete), integrates with ITSM, IPAM, AAA. Orchestration uses configuration management as a building block. Think: Ansible = **how to configure one device**; NSO/DNA Center = **how to deliver a service across the network**.",
      tags: ["orchestration", "configuration-management", "nso", "ansible", "automation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-08T00:00:00.000Z",
      sr_data: { easiness_factor: 2.5, interval_days: 0, repetitions: 0, next_review_date: new Date().toISOString().split('T')[0], last_review_date: null },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // VLAN / TRUNKING — domain 3.0, Switching
    // =========================================================
    {
      id: "starter-201",
      domain: "3.0",
      subdomain: "Switching",
      front: "What happens when two switches have a **native VLAN mismatch** on a trunk link?",
      back: "CDP reports the mismatch and STP may place the port in an inconsistent state. Frames sent untagged (native VLAN) by one switch are placed into the wrong VLAN on the other side, causing **traffic leaking between VLANs** — a security and forwarding issue. Always match native VLANs on both ends.",
      tags: ["vlan", "trunk", "native-vlan", "troubleshooting"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-202",
      domain: "3.0",
      subdomain: "Switching",
      front: "What is the difference between DTP modes **dynamic desirable** and **dynamic auto**?",
      back: "**Dynamic desirable** actively tries to negotiate a trunk — it sends DTP frames. **Dynamic auto** passively waits and only forms a trunk if the other side initiates. Two 'auto' ports will NOT form a trunk (both wait, neither initiates). Best practice: disable DTP with `switchport nonegotiate` on all trunks.",
      tags: ["dtp", "trunk", "negotiation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-203",
      domain: "3.0",
      subdomain: "Switching",
      front: "In VTP, what does a switch in **transparent mode** do with VTP advertisements?",
      back: "It **forwards** VTP advertisements to other switches but does **not** update its own VLAN database from them. Transparent mode allows local VLAN configuration independent of the VTP domain. In VTPv3, transparent mode also supports extended VLANs (1006–4094).",
      tags: ["vtp", "transparent-mode", "vlan"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-204",
      domain: "3.0",
      subdomain: "Switching",
      front: "Which frames are sent **untagged** on an 802.1Q trunk?",
      back: "Only **native VLAN** frames are sent untagged. All other VLAN frames carry a 4-byte 802.1Q tag inserted after the source MAC. The receiving switch assigns untagged frames to its configured native VLAN — which is why both sides must agree on the native VLAN number.",
      tags: ["802.1q", "trunk", "native-vlan", "tagging"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // NAT / PAT — domain 3.0, IP Services
    // =========================================================
    {
      id: "starter-205",
      domain: "3.0",
      subdomain: "IP Services",
      front: "In NAT terminology, what is the **inside local** address vs the **inside global** address?",
      back: "**Inside local** = the private IP on the internal host (e.g., 10.1.1.5). **Inside global** = the public IP that represents that host to the outside world (e.g., 203.0.113.1). Think: 'local' = how the inside network sees it, 'global' = how the internet sees it.",
      tags: ["nat", "inside-local", "inside-global", "terminology"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-206",
      domain: "3.0",
      subdomain: "IP Services",
      front: "For traffic going from **inside to outside**, what is the NAT order of operations?",
      back: "**Routing first, then translation.** The router makes the forwarding decision using the original (inside local) source IP, then translates it to the inside global address before sending it out. For **outside to inside**, the order reverses: translate first, then route.",
      tags: ["nat", "order-of-operations", "routing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-207",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What happens when a **dynamic NAT pool** is exhausted?",
      back: "New translation requests are **silently dropped** — the traffic does not get queued or buffered. The router cannot create a mapping because no addresses are available. This is why **PAT (overload)** is preferred: it uses port numbers to multiplex ~65,535 sessions per IP.",
      tags: ["nat", "dynamic-nat", "pool-exhaustion", "pat"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-208",
      domain: "3.0",
      subdomain: "IP Services",
      front: "What does the **overload** keyword do in `ip nat inside source list 1 interface Gi0/1 overload`?",
      back: "It enables **PAT (Port Address Translation)**. Without 'overload', you get 1:1 dynamic NAT. With 'overload', many inside hosts share the single outside interface IP, distinguished by unique **source port numbers** (creating unique sockets).",
      tags: ["pat", "overload", "nat"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // NTP — domain 4.0, Network Assurance
    // =========================================================
    {
      id: "starter-209",
      domain: "4.0",
      subdomain: "Network Assurance",
      front: "What is an NTP **stratum** level and why does it matter?",
      back: "Stratum indicates distance from the reference clock. **Stratum 0** = atomic clock (reference). **Stratum 1** = directly connected to stratum 0. Each hop adds 1. A router synced to stratum 2 becomes **stratum 3**. Lower stratum = more accurate. Maximum is stratum 15; stratum 16 means unsynchronized.",
      tags: ["ntp", "stratum", "time-sync"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-210",
      domain: "4.0",
      subdomain: "Network Assurance",
      front: "Why is **NTP** critical for network assurance and troubleshooting?",
      back: "Consistent timestamps across all devices enable **syslog correlation**, accurate **NetFlow records**, valid **SNMP trap sequencing**, and reliable **certificate validation**. Without NTP, correlating events across a multi-device network during an outage becomes nearly impossible.",
      tags: ["ntp", "network-assurance", "syslog", "correlation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-211",
      domain: "4.0",
      subdomain: "Network Assurance",
      front: "What is the difference between `ntp server` and `ntp peer`?",
      back: "`ntp server` = **client mode** — the router syncs FROM the specified server (one-way). `ntp peer` = **symmetric active** — both devices can sync from each other (mutual). Use `ntp server` for hierarchical designs (most common). Use `ntp peer` between equal-stratum devices that need mutual backup.",
      tags: ["ntp", "server", "peer", "configuration"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // EtherChannel — domain 3.0, Switching
    // =========================================================
    {
      id: "starter-212",
      domain: "3.0",
      subdomain: "Switching",
      front: "What are the LACP mode combinations that **will** and **will NOT** form an EtherChannel?",
      back: "**Will form:** Active–Active, Active–Passive. **Will NOT form:** Passive–Passive (both wait, neither initiates). At least one side must be **active**. Same logic applies to PAgP: Desirable–Desirable or Desirable–Auto works; Auto–Auto does not.",
      tags: ["lacp", "etherchannel", "modes", "negotiation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-213",
      domain: "3.0",
      subdomain: "Switching",
      front: "What conditions must ALL member ports meet to form an EtherChannel?",
      back: "All ports must match: **speed**, **duplex**, **VLAN assignment** (access) or **trunk configuration** (allowed VLANs, native VLAN), **STP port cost**, and **port type** (L2 or L3). Any mismatch causes the channel to not form or puts individual ports into suspended state.",
      tags: ["etherchannel", "requirements", "misconfiguration"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-214",
      domain: "3.0",
      subdomain: "Switching",
      front: "How does LACP provide **redundancy** that PAgP and static EtherChannel cannot?",
      back: "LACP supports up to **16 links**: 8 active + 8 in hot-standby. If an active link fails, a standby link automatically takes over. PAgP and static mode support only **8 links maximum** with no standby capability.",
      tags: ["lacp", "hot-standby", "redundancy", "etherchannel"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // Port Security — domain 5.0, Security
    // =========================================================
    {
      id: "starter-215",
      domain: "5.0",
      subdomain: "Security",
      front: "What are the three port security **violation modes** and how do they differ?",
      back: "**Shutdown** (default): err-disables the port, sends SNMP trap + syslog. **Restrict**: drops offending frames, increments violation counter, sends syslog — port stays UP. **Protect**: silently drops offending frames — no log, no counter, port stays UP. Exam tip: 'restrict' and 'protect' both keep the port up, but only 'restrict' logs violations.",
      tags: ["port-security", "violation-modes", "shutdown", "restrict", "protect"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-216",
      domain: "5.0",
      subdomain: "Security",
      front: "What does `switchport port-security mac-address sticky` do?",
      back: "It **dynamically learns** the MAC address of connected devices and converts them into **static secure MAC entries** in the running config. This avoids manually typing MACs. Important: you must `copy running-config startup-config` to persist sticky MACs across reboots — otherwise they're lost on reload.",
      tags: ["port-security", "sticky-mac", "dynamic-learning"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-217",
      domain: "5.0",
      subdomain: "Security",
      front: "A port enters **err-disabled** state from a port security violation. How do you recover it?",
      back: "Manually: `shutdown` then `no shutdown` the interface. Automatically: `errdisable recovery cause psecure-violation` + `errdisable recovery interval <seconds>`. The auto-recovery timer checks periodically and re-enables the port if the violation condition is cleared.",
      tags: ["port-security", "err-disabled", "recovery"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // CoPP — domain 5.0, Security
    // =========================================================
    {
      id: "starter-218",
      domain: "5.0",
      subdomain: "Security",
      front: "What is **Control Plane Policing (CoPP)** and what does it protect?",
      back: "CoPP protects the **router's CPU** from being overwhelmed by excessive control-plane traffic (routing protocols, SNMP, SSH, ICMP, etc.). It uses a **QoS policy-map** applied to the `control-plane` (not interfaces). Traffic exceeding the rate limit is dropped, preventing CPU exhaustion during floods or attacks.",
      tags: ["copp", "control-plane", "cpu-protection", "rate-limiting"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-219",
      domain: "5.0",
      subdomain: "Security",
      front: "How does CoPP differ from an interface ACL?",
      back: "An **interface ACL** filters **transit (data-plane) traffic** passing through the router. **CoPP** filters traffic destined **to the router itself** (control/management plane — the 'punt path' to CPU). CoPP uses **rate-limiting** (police action) rather than permit/deny, allowing a controlled rate of necessary control traffic while preventing floods.",
      tags: ["copp", "acl", "data-plane", "control-plane", "comparison"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // QoS — domain 3.0, QoS
    // =========================================================
    {
      id: "starter-220",
      domain: "3.0",
      subdomain: "QoS",
      front: "What is the difference between **LLQ** and **CBWFQ**?",
      back: "**LLQ** (Low Latency Queuing) provides a **strict priority queue** — packets are sent immediately, policed to a ceiling to prevent starvation. Used for **real-time traffic** (voice = DSCP EF). **CBWFQ** provides **minimum bandwidth guarantees** per class but no strict priority. LLQ + CBWFQ are typically deployed together: LLQ for voice, CBWFQ for data classes.",
      tags: ["qos", "llq", "cbwfq", "priority", "bandwidth"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-221",
      domain: "3.0",
      subdomain: "QoS",
      front: "What are the key DSCP markings for **voice** and **video** traffic?",
      back: "**Voice (bearer)**: DSCP **EF** (46) — Expedited Forwarding, gets LLQ strict priority. **Voice signaling**: DSCP **CS3** (24) or **AF31** (26). **Video conferencing**: DSCP **AF41** (34). **Network control** (routing protocols): DSCP **CS6** (48). Exam tip: EF = voice is the most tested marking.",
      tags: ["qos", "dscp", "ef", "voice", "marking"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    // =========================================================
    // EXPANSION WAVE — DOMAIN 1.0 + 2.0 (Architecture + Virtualization)
    // =========================================================
    // =========================================================
    // DOMAIN 1.0 — ARCHITECTURE (41 cards)
    // =========================================================

    // --- CEF (Cisco Express Forwarding) ---

    {
      id: "starter-222",
      domain: "1.0",
      subdomain: "CEF",
      front: "What are the two main data structures used by CEF (Cisco Express Forwarding)?",
      back: "**FIB (Forwarding Information Base)** and the **Adjacency Table**. The FIB is a mirror of the routing table optimized for fast lookup. The adjacency table stores Layer 2 rewrite (next-hop MAC) information. Together they allow hardware-rate forwarding without involving the CPU for each packet.",
      tags: ["cef", "fib", "adjacency-table"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-223",
      domain: "1.0",
      subdomain: "CEF",
      front: "Compare **process switching**, **fast switching**, and **CEF**. Which is used on modern IOS-XE platforms?",
      back: "**Process switching**: Every packet handled by CPU; slowest. **Fast switching** (route cache): First packet process-switched, subsequent packets use a cache; moderate speed but cache can go stale. **CEF**: Pre-built FIB and adjacency table populated proactively from RIB/ARP; fastest, hardware-rate forwarding. Modern IOS-XE platforms use **CEF by default** and it cannot typically be disabled on hardware-forwarding platforms.",
      tags: ["cef", "process-switching", "fast-switching"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-224",
      domain: "1.0",
      subdomain: "CEF",
      front: "What is CEF **polarization** and how is it mitigated?",
      back: "CEF polarization occurs when **all switches in a multi-tier topology hash to the same link** for load balancing because they use the same hash algorithm and inputs. This results in uneven link utilization. Mitigation: use **unique hash seeds** on each switch (IOS-XE: `ip cef load-sharing algorithm universal <id>`) or alternate between source-based and destination-based hashing at each tier.",
      tags: ["cef", "load-balancing", "polarization"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-225",
      domain: "1.0",
      subdomain: "CEF",
      front: "What happens when CEF encounters a **glean adjacency** in the adjacency table?",
      back: "A **glean adjacency** means the destination is on a directly connected subnet but the specific host's L2 address is not yet known. CEF punts the packet to the CPU so it can send an **ARP request** (IPv4) or **Neighbor Solicitation** (IPv6). Once the ARP/ND reply is received, a complete adjacency is built and subsequent packets are CEF-switched at hardware rate.",
      tags: ["cef", "adjacency-table", "glean"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-226",
      domain: "1.0",
      subdomain: "CEF",
      front: "What is the difference between **per-destination** and **per-packet** CEF load balancing?",
      back: "**Per-destination** (default): All packets to the same src/dst pair follow the same path, preserving packet order. Uses a hash of src+dst IP. **Per-packet**: Packets to the same destination are round-robined across equal-cost paths. Maximizes link utilization but can cause **out-of-order delivery**, which breaks many applications. Per-destination is recommended in almost all cases.",
      tags: ["cef", "load-balancing", "ecmp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- TCAM ---

    {
      id: "starter-227",
      domain: "1.0",
      subdomain: "TCAM",
      front: "What is **TCAM** and why is it important in Catalyst switches?",
      back: "**Ternary Content-Addressable Memory** stores ACLs, QoS policies, and routing entries for **single-clock-cycle lookups**. Unlike binary CAM (which matches 0 or 1), TCAM supports a third state: **don't care (X)**, enabling wildcard/mask matching. This allows ACLs and longest-prefix-match routing to be evaluated in hardware at line rate.",
      tags: ["tcam", "cam", "hardware-forwarding"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-228",
      domain: "1.0",
      subdomain: "TCAM",
      front: "What are the three fields stored in each TCAM entry?",
      back: "Each TCAM entry has three fields: **1) Value** — the pattern to match (e.g., IP prefix, ACL fields), **2) Mask** — which bits to compare (supports 0, 1, and don't-care), **3) Result** — the action to take (permit/deny, next-hop, QoS marking, etc.). The result is stored in an associated **action RAM**. TCAM entries are evaluated in order; first match wins.",
      tags: ["tcam", "value-mask-result"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-229",
      domain: "1.0",
      subdomain: "TCAM",
      front: "What happens when a switch runs out of **TCAM space** for ACLs?",
      back: "When TCAM is exhausted, the switch can no longer program new ACL entries in hardware. Depending on the platform, packets may be **punted to the CPU for software processing** (dramatically reducing throughput) or the ACL may **fail to apply entirely**. Mitigation: use `show platform tcam utilization` to monitor usage, consolidate ACEs with object-groups, and use SDM templates to reallocate TCAM between features.",
      tags: ["tcam", "acl", "sdm-template"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-230",
      domain: "1.0",
      subdomain: "TCAM",
      front: "What is an **SDM (Switching Database Manager) template** and when would you change it?",
      back: "SDM templates control how **TCAM resources are partitioned** among features (routing, ACLs, QoS, multicast, etc.) on Catalyst switches. You change it when your deployment needs more of one resource, e.g., `sdm prefer routing` for a Layer 3 heavy network or `sdm prefer access` for ACL-heavy deployments. A **reboot is required** for the new template to take effect.",
      tags: ["tcam", "sdm-template", "catalyst"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Enterprise Network Design ---

    {
      id: "starter-231",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      front: "What are the three layers of the traditional **3-tier enterprise campus design**?",
      back: "**1) Access Layer** — connects endpoints (PCs, phones, printers); provides port security, VLANs, PoE. **2) Distribution Layer** — aggregates access switches; performs policy enforcement, inter-VLAN routing, summarization. **3) Core Layer** — high-speed backbone connecting distribution blocks; provides fast transport with minimal latency and no policy processing.",
      tags: ["enterprise-design", "three-tier", "campus"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-232",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      front: "When is a **collapsed core (2-tier)** design appropriate instead of 3-tier?",
      back: "A collapsed core merges the **core and distribution layers** into a single tier. It is appropriate for **small to medium campuses** (typically fewer than 3-4 distribution blocks or ~2,000-5,000 users) where a dedicated core would add cost without significant benefit. The key tradeoff: simpler/cheaper but **less scalable** and the failure domain is larger.",
      tags: ["enterprise-design", "collapsed-core", "two-tier"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-233",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      front: "What is a **spine-leaf** data center topology and what problem does it solve?",
      back: "Spine-leaf (Clos fabric): Every **leaf** switch connects to every **spine** switch. No leaf-to-leaf or spine-to-spine links. Benefits: **predictable latency** (every host is exactly 2 hops apart), easy horizontal scaling (add spines for bandwidth, leaves for ports), and **ECMP** load balancing across all spine paths. Solves the oversubscription and unpredictable latency problems of traditional 3-tier data center designs.",
      tags: ["enterprise-design", "spine-leaf", "data-center"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-234",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      front: "In spine-leaf, why is **Layer 3 to the leaf** (routed access) preferred over Layer 2 spanning the fabric?",
      back: "Routed access (L3 at each leaf) enables **ECMP across all spine uplinks**, eliminates STP, reduces broadcast domains, and prevents L2 loops. If a host needs L2 adjacency across leaves, an **overlay** like VXLAN extends L2 segments over the L3 underlay without the drawbacks of traditional STP-based fabrics.",
      tags: ["spine-leaf", "routed-access", "ecmp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- StackWise / StackWise Virtual ---

    {
      id: "starter-235",
      domain: "1.0",
      subdomain: "StackWise",
      front: "What is **StackWise Virtual** and how does it differ from traditional StackWise?",
      back: "**StackWise Virtual (SVL)** combines two physical Catalyst switches (e.g., 9500) into a **single logical switch** using standard Ethernet uplinks as the SVL (StackWise Virtual Link). Traditional **StackWise** uses proprietary stacking cables/ports to stack up to 8-9 access-layer switches. Key difference: SVL targets **distribution/core** with two chassis; StackWise targets **access** with many chassis.",
      tags: ["stackwise", "stackwise-virtual", "high-availability"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-236",
      domain: "1.0",
      subdomain: "StackWise",
      front: "What is a **Dual-Active Detection (DAD)** link in StackWise Virtual?",
      back: "DAD is a dedicated link between the two SVL chassis that detects **split-brain** scenarios. If the SVL fails, both switches could become active simultaneously (dual-active). The DAD link allows the standby chassis to detect this condition and **put its interfaces into a recovery state** (err-disabled) to prevent network disruption. DAD can use a direct Ethernet link or Enhanced PAgP (ePAgP) over downstream switches.",
      tags: ["stackwise-virtual", "dual-active-detection", "high-availability"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-237",
      domain: "1.0",
      subdomain: "StackWise",
      front: "What is **VSS (Virtual Switching System)** and how does it relate to StackWise Virtual?",
      back: "VSS was the predecessor to StackWise Virtual, used on Catalyst 6500/6800 series. It combined two physical chassis into one logical switch using **VSL (Virtual Switch Link)**. StackWise Virtual is the modern replacement for Catalyst 9000 series, using **SVL links** instead of VSL. Both achieve the same goal: a single management plane, single control plane, and MEC (Multi-chassis EtherChannel) to downstream switches.",
      tags: ["vss", "stackwise-virtual", "catalyst"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- SD-Access (deeper) ---

    {
      id: "starter-238",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "What are the three main **fabric node roles** in SD-Access?",
      back: "**1) Fabric Edge Node** — connects endpoints, performs VXLAN encap/decap, registers EID-to-RLOC with control plane. **2) Fabric Border Node** — connects the fabric to external networks (internet, WAN, data center), translates between overlay and traditional routing. **3) Fabric Control Plane Node** — runs LISP Map-Server/Map-Resolver (MS/MR), maintains the EID-to-RLOC database for the entire fabric.",
      tags: ["sd-access", "fabric-roles", "edge", "border", "control-plane"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-239",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "What is the difference between **macro-segmentation** and **micro-segmentation** in SD-Access?",
      back: "**Macro-segmentation** isolates at the **VN (Virtual Network)** level — equivalent to VRFs. Traffic between VNs is blocked by default and requires a firewall/fusion router to cross. **Micro-segmentation** provides fine-grained control **within** a VN using **SGTs (Scalable Group Tags)** and **SGACLs**. Example: within the corporate VN, developers can reach dev servers but not HR servers.",
      tags: ["sd-access", "segmentation", "sgt", "virtual-network"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-240",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "What role does **ISE (Identity Services Engine)** play in SD-Access?",
      back: "ISE is the **policy engine** for SD-Access. It performs: **1) Authentication** — 802.1X, MAB, or Web Auth for endpoint identity. **2) Authorization** — assigns VN and SGT based on identity/posture. **3) SGT Policy** — defines SGACLs (source-group to destination-group policies) enforced at fabric edge nodes. ISE integrates with DNA Center, which pushes fabric policy configurations.",
      tags: ["sd-access", "ise", "authentication", "sgt"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-241",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "In SD-Access, what is a **fusion router** and when is it needed?",
      back: "A fusion router sits at the boundary of the SD-Access fabric and provides **inter-VN routing** (route leaking between Virtual Networks). Since VNs are isolated by default (separate VRFs), traffic between them must exit through the border node to a fusion router that has routes for both VNs. The fusion router applies policy (typically a firewall) before leaking routes between VRFs.",
      tags: ["sd-access", "fusion-router", "inter-vn", "route-leaking"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- SD-WAN (deeper) ---

    {
      id: "starter-242",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "What is **OMP (Overlay Management Protocol)** in SD-WAN and what does it advertise?",
      back: "OMP is the **control-plane routing protocol** between vSmart controllers and WAN Edge routers. It advertises three types of routes: **1) OMP routes (vRoutes)** — prefix/next-hop with TLOC binding, **2) TLOCs** — Transport Locators (color + encap type per WAN interface), **3) Service routes** — for service chaining (firewall, IDS). OMP runs over DTLS/TLS tunnels and is analogous to BGP in concept.",
      tags: ["sd-wan", "omp", "tloc", "control-plane"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-243",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "What is the difference between **control policies** and **data policies** in SD-WAN?",
      back: "**Control policies** are applied on vSmart and manipulate OMP route advertisements — they affect the **routing decisions** (e.g., prefer one TLOC over another, filter routes). **Data policies** are pushed to WAN Edge routers and affect actual **packet forwarding** — they match on L3/L4 fields and can set next-hop, DSCP, or redirect to a service chain. Control policies influence route selection; data policies override forwarding per-packet.",
      tags: ["sd-wan", "control-policy", "data-policy", "vsmart"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-244",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "How does **app-aware routing** work in Cisco SD-WAN?",
      back: "App-aware routing uses **BFD probes** between WAN Edge routers to continuously measure **latency, jitter, and packet loss** on each tunnel. An SLA policy defines thresholds (e.g., latency < 150ms, loss < 1%). When a tunnel violates its SLA, traffic for affected applications is **automatically re-routed** to a tunnel that meets the SLA. This is configured via app-aware routing policies in vManage.",
      tags: ["sd-wan", "app-aware-routing", "sla", "bfd"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-245",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "What is a **TLOC (Transport Locator)** in SD-WAN?",
      back: "A TLOC uniquely identifies a WAN Edge router's transport interface. It is a tuple of: **system-ip + color + encapsulation type** (IPsec or GRE). Each WAN interface gets a TLOC. Example: a router with MPLS and Internet links has two TLOCs. vSmart uses TLOCs to resolve where OMP routes can be reached and to build the full-mesh IPsec data plane between WAN Edge routers.",
      tags: ["sd-wan", "tloc", "omp", "ipsec"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-246",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "In the SD-WAN architecture, which component must have a **public IP address** (or static 1:1 NAT) and why?",
      back: "**vBond** must be publicly reachable. It is the **first point of contact** for all SD-WAN components during the bootstrap process. WAN Edge routers, vSmart controllers, and vManage all initially connect to vBond using its public IP (configured via chassis config). vBond then orchestrates mutual authentication and tells each component how to reach vManage and vSmart.",
      tags: ["sd-wan", "vbond", "bootstrap", "nat"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- QoS (deeper) ---

    {
      id: "starter-247",
      domain: "1.0",
      subdomain: "QoS",
      front: "What are the six **DSCP Per-Hop Behaviors (PHBs)** commonly tested on ENCOR?",
      back: "**1) EF (Expedited Forwarding)** — DSCP 46, for voice (low latency, low loss). **2) AF (Assured Forwarding)** — 4 classes (AF1x-AF4x), each with 3 drop precedences. **3) CS (Class Selector)** — CS0-CS7, backward compatible with IP Precedence. **4) Default/BE** — DSCP 0, best effort. AF notation: AFxy where x=class (1-4), y=drop precedence (1-3). Higher class = higher priority; higher drop = dropped first under congestion.",
      tags: ["qos", "dscp", "phb", "assured-forwarding"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-248",
      domain: "1.0",
      subdomain: "QoS",
      front: "What is the difference between **classification** and **marking** in QoS?",
      back: "**Classification** identifies traffic into categories using match criteria (ACL, NBAR, DSCP, CoS, protocol). It is **read-only** — no packet modification. **Marking** writes a QoS value into the packet/frame header (DSCP in IP header, CoS in 802.1Q tag) so downstream devices can classify based on the mark without re-inspecting the payload. Best practice: **classify and mark as close to the source as possible** (trust boundary at the access layer).",
      tags: ["qos", "classification", "marking", "trust-boundary"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-249",
      domain: "1.0",
      subdomain: "QoS",
      front: "Compare **LLQ**, **CBWFQ**, and **WFQ** queuing mechanisms.",
      back: "**WFQ (Weighted Fair Queuing)** — automatically classifies flows and assigns bandwidth proportionally; no admin configuration. **CBWFQ (Class-Based WFQ)** — admin defines classes and assigns guaranteed bandwidth per class; excess is shared. **LLQ (Low Latency Queuing)** — CBWFQ + a **strict priority queue** for real-time traffic (voice/video). LLQ de-queues the priority class first, with a policer to prevent starvation of other classes.",
      tags: ["qos", "llq", "cbwfq", "wfq", "queuing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-250",
      domain: "1.0",
      subdomain: "QoS",
      front: "What is the difference between **policing** and **shaping** in QoS?",
      back: "**Policing** drops or re-marks excess traffic immediately when the rate is exceeded — **no buffering**, bursty output. Applied inbound or outbound. **Shaping** buffers excess traffic and sends it out at a smoothed rate — introduces **delay** but no drops (until buffer fills). Applied **outbound only**. Shaping is preferred when connecting to a provider with a committed rate (e.g., shape to CIR). Policing is used at trust boundaries or by ISPs.",
      tags: ["qos", "policing", "shaping", "traffic-management"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-251",
      domain: "1.0",
      subdomain: "QoS",
      front: "What is **WRED (Weighted Random Early Detection)** and what problem does it solve?",
      back: "WRED is a **congestion avoidance** mechanism that randomly drops packets **before** a queue is full, preventing **tail drop** and **TCP global synchronization** (where all TCP flows back off simultaneously, then ramp up together, causing oscillation). WRED uses different drop thresholds based on DSCP/IP Precedence — higher-priority traffic has higher thresholds (dropped less aggressively). WRED is most effective for **TCP traffic** (which responds to drops by reducing its window).",
      tags: ["qos", "wred", "congestion-avoidance", "tail-drop"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-252",
      domain: "1.0",
      subdomain: "QoS",
      front: "What is the relationship between **CoS (Class of Service)** and **DSCP**?",
      back: "**CoS** is a 3-bit field in the **802.1Q VLAN tag** (Layer 2, values 0-7). **DSCP** is a 6-bit field in the **IP header ToS byte** (Layer 3, values 0-63). CoS only exists on trunk links (tagged frames) and is lost when a frame is routed. DSCP persists end-to-end through L3 hops. Best practice: map CoS to DSCP at the access switch and use DSCP for all subsequent QoS decisions.",
      tags: ["qos", "cos", "dscp", "layer2", "layer3"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- High Availability ---

    {
      id: "starter-253",
      domain: "1.0",
      subdomain: "HighAvailability",
      front: "What is **NSF (Non-Stop Forwarding)** and how does it work with SSO?",
      back: "NSF allows a router/switch to continue **forwarding traffic** during a control-plane switchover (supervisor failover). It works with **SSO (Stateful Switchover)**, which synchronizes the control plane state to the standby supervisor. During failover: SSO brings up the standby sup with pre-synced state, while NSF keeps the data plane (CEF/FIB) forwarding based on the **stale routing table**. NSF-aware neighbors (via graceful restart) hold their routes during the restart period.",
      tags: ["high-availability", "nsf", "sso", "graceful-restart"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-254",
      domain: "1.0",
      subdomain: "HighAvailability",
      front: "What is **SSO (Stateful Switchover)** and what state does it synchronize?",
      back: "SSO keeps the standby supervisor in a **hot standby** state by continuously synchronizing: **interface states** (up/down), **Layer 2 protocols** (STP, EtherChannel, LACP), **line card states**, and **forwarding tables** (CEF FIB/adjacency). During failover, the standby takes over in **sub-second time** without resetting line cards. SSO is a prerequisite for NSF and ISSU.",
      tags: ["high-availability", "sso", "stateful-switchover"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-255",
      domain: "1.0",
      subdomain: "HighAvailability",
      front: "What is **ISSU (In-Service Software Upgrade)** and what are its prerequisites?",
      back: "ISSU allows upgrading IOS-XE **without a network outage**. The process upgrades the standby supervisor first, performs an SSO switchover, then upgrades the former active. Prerequisites: **1) Dual supervisors**, **2) SSO mode enabled**, **3) NSF enabled**, **4) Compatible IOS-XE version pairs** (not all versions support ISSU between them). On Catalyst 9K with StackWise Virtual, ISSU upgrades one chassis at a time.",
      tags: ["high-availability", "issu", "upgrade", "sso"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-256",
      domain: "1.0",
      subdomain: "HighAvailability",
      front: "What is **BFD (Bidirectional Forwarding Detection)** and why is it used?",
      back: "BFD is a **lightweight, sub-second failure detection** protocol that works alongside routing protocols. Instead of waiting for OSPF dead timers (40s default) or EIGRP hold timers (15s), BFD detects link failures in **milliseconds** (50ms intervals x 3 = 150ms detection). BFD runs in the **data plane** (often hardware-assisted), making it faster than control-plane keepalives. Supported with OSPF, EIGRP, BGP, IS-IS, and static routes.",
      tags: ["high-availability", "bfd", "failure-detection"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-257",
      domain: "1.0",
      subdomain: "HighAvailability",
      front: "What is **graceful restart** (GR) and how does it differ from NSF?",
      back: "Graceful restart is the **protocol-level mechanism** that enables NSF. When a router performs SSO, it signals GR to its neighbors. **GR-capable** = the restarting router; **GR-aware/helper** = the neighbor that continues using stale routes during the restart. OSPF uses a grace LSA; BGP uses End-of-RIB marker + restart timer. NSF is the **platform feature** that keeps CEF forwarding; GR is the **protocol extension** that prevents neighbors from tearing down adjacencies.",
      tags: ["high-availability", "graceful-restart", "nsf", "ospf", "bgp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 2.0 — VIRTUALIZATION (24 cards)
    // =========================================================

    // --- VRF-Lite vs Full VRF ---

    {
      id: "starter-258",
      domain: "2.0",
      subdomain: "VRF",
      front: "What is the difference between **VRF-Lite** and **full VRF with MP-BGP**?",
      back: "**VRF-Lite** creates isolated routing tables per VRF but requires **separate physical or sub-interfaces** between every pair of routers for each VRF — no label signaling. **Full VRF (MPLS VPN)** uses **MP-BGP** to carry VPNv4/v6 routes with RD/RT attributes and **MPLS labels** for forwarding, so a single link between PE routers can carry all VRFs. VRF-Lite is simpler but does not scale for many VRFs.",
      tags: ["vrf", "vrf-lite", "mp-bgp", "mpls"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-259",
      domain: "2.0",
      subdomain: "VRF",
      front: "What are **RD (Route Distinguisher)** and **RT (Route Target)** in MPLS VPN?",
      back: "**RD** (64-bit, format ASN:nn or IP:nn) makes IPv4 prefixes globally unique in the BGP table by prepending to the prefix to form a VPNv4 route. RD is **locally significant** — it just prevents collisions. **RT** (BGP extended community) controls **import/export policy**: the exporting PE attaches an RT, and the importing PE only installs routes whose RT matches its import policy. RT is what creates the VPN topology (full-mesh, hub-spoke, etc.).",
      tags: ["vrf", "route-distinguisher", "route-target", "mp-bgp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-260",
      domain: "2.0",
      subdomain: "VRF",
      front: "How does **VRF route leaking** work and when would you use it?",
      back: "Route leaking allows controlled sharing of routes between VRFs. Methods: **1) Static route** pointing to a next-hop in another VRF (`ip route vrf A 10.0.0.0/8 vrf B next-hop`). **2) MP-BGP import/export RT** — export a route from VRF-A with an RT that VRF-B imports. **3) Route-map filtering** to selectively leak. Use case: shared services (DNS, DHCP, internet) accessible from multiple isolated VRFs without full connectivity between them.",
      tags: ["vrf", "route-leaking", "shared-services"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- GRE Tunnels ---

    {
      id: "starter-261",
      domain: "2.0",
      subdomain: "GRE",
      front: "What is **GRE recursive routing** and how do you prevent it?",
      back: "Recursive routing occurs when the tunnel destination is reachable **via the tunnel itself**. The router tries to route to the tunnel endpoint through the GRE tunnel, creating an infinite loop that flaps the tunnel interface. Prevention: **1) Use a static route** for the tunnel destination pointing to a physical next-hop. **2) Use a more specific route** to the tunnel endpoint in the global table. **3) Place the tunnel destination route in a separate VRF**.",
      tags: ["gre", "recursive-routing", "tunnel"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-262",
      domain: "2.0",
      subdomain: "GRE",
      front: "What is the MTU impact of **GRE** and **GRE over IPsec** encapsulation?",
      back: "GRE adds a **24-byte header** (4-byte GRE + 20-byte new IP header), reducing the effective MTU from 1500 to **1476 bytes**. GRE over IPsec (tunnel mode) adds an additional **~52-73 bytes** (ESP header+trailer+IV, depending on cipher), reducing the effective MTU to roughly **1400-1424 bytes**. Solutions: **1) Set `ip mtu` on the tunnel interface, **2) Use `ip tcp adjust-mss` to clamp TCP MSS, **3) Enable PMTUD. Fragmentation before encapsulation is preferred (`tunnel path-mtu-discovery`).",
      tags: ["gre", "mtu", "ipsec", "fragmentation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-263",
      domain: "2.0",
      subdomain: "GRE",
      front: "What is the key advantage of **GRE over IPsec** compared to plain IPsec?",
      back: "Plain IPsec (tunnel mode) can only transport **unicast IP traffic**. GRE over IPsec encapsulates traffic in GRE first (which can carry **multicast, broadcast, and non-IP protocols**), then encrypts the GRE packet with IPsec. This allows running **routing protocols** (OSPF, EIGRP) over the encrypted tunnel, which is not possible with native IPsec alone.",
      tags: ["gre", "ipsec", "multicast", "routing-protocols"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- DMVPN / mGRE ---

    {
      id: "starter-264",
      domain: "2.0",
      subdomain: "DMVPN",
      front: "What is **NHRP (Next Hop Resolution Protocol)** and what role does it play in DMVPN?",
      back: "NHRP is a client/server protocol where **spokes (NHC — Next Hop Client)** register their NBMA (public IP) to tunnel IP mappings with the **hub (NHS — Next Hop Server)**. When a spoke needs to communicate with another spoke, it sends an **NHRP resolution request** through the hub to learn the remote spoke's public IP, enabling **direct spoke-to-spoke tunnels**. NHRP is essential for dynamic tunnel creation in DMVPN Phases 2 and 3.",
      tags: ["dmvpn", "nhrp", "mgre", "spoke-to-spoke"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-265",
      domain: "2.0",
      subdomain: "DMVPN",
      front: "Compare **DMVPN Phase 1, Phase 2, and Phase 3** — what is the key difference in traffic flow?",
      back: "**Phase 1**: All traffic goes **hub-and-spoke** only. Hub uses regular GRE; spokes use mGRE. No spoke-to-spoke tunnels. **Phase 2**: Spokes use mGRE and can build **direct spoke-to-spoke tunnels** via NHRP resolution. Routing must preserve the original spoke next-hop (no summarization at hub). **Phase 3**: Adds **NHRP shortcuts and redirects**. Hub sends NHRP redirect to the source spoke, which then resolves the destination spoke directly. Allows summarization/default routes at the hub while still enabling spoke-to-spoke.",
      tags: ["dmvpn", "phase1", "phase2", "phase3", "nhrp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-266",
      domain: "2.0",
      subdomain: "DMVPN",
      front: "Why can't you summarize routes at the hub in **DMVPN Phase 2**, but you can in **Phase 3**?",
      back: "In Phase 2, spoke-to-spoke routing relies on the **next-hop IP being the remote spoke's tunnel IP** (preserved from the hub's routing advertisement). If the hub summarizes, the next-hop becomes the hub itself, and all traffic routes through the hub — defeating spoke-to-spoke. In Phase 3, the hub sends **NHRP redirects** to the initiating spoke, which triggers an **NHRP shortcut** that overrides the routing table next-hop. So the hub can summarize freely because NHRP handles the spoke-to-spoke resolution.",
      tags: ["dmvpn", "phase2", "phase3", "summarization", "nhrp-redirect"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-267",
      domain: "2.0",
      subdomain: "DMVPN",
      front: "What is **mGRE (Multipoint GRE)** and how does it differ from point-to-point GRE?",
      back: "**Point-to-point GRE** has a single fixed `tunnel destination` — one tunnel interface per remote peer. **mGRE** uses `tunnel mode gre multipoint` with **no tunnel destination** configured — the destination is resolved dynamically via **NHRP**. A single mGRE interface can communicate with **hundreds of peers**, making it the foundation of DMVPN scalability.",
      tags: ["dmvpn", "mgre", "gre", "scalability"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- LISP ---

    {
      id: "starter-268",
      domain: "2.0",
      subdomain: "LISP",
      front: "What are the two namespaces in **LISP (Locator/ID Separation Protocol)**?",
      back: "**EID (Endpoint Identifier)** — the IP address of the host (identity, stays the same regardless of location). **RLOC (Routing Locator)** — the IP address of the LISP router (topology-dependent, changes as the host moves). LISP decouples identity from location: the EID-to-RLOC mapping is maintained in a **mapping system**. This separation enables host mobility, multi-homing, and traffic engineering without renumbering endpoints.",
      tags: ["lisp", "eid", "rloc", "mapping"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-269",
      domain: "2.0",
      subdomain: "LISP",
      front: "What are the roles of **Map-Server**, **Map-Resolver**, **ITR**, and **ETR** in LISP?",
      back: "**ITR (Ingress Tunnel Router)** — receives non-LISP packets from sources, performs EID-to-RLOC lookup, encapsulates in LISP header, forwards to ETR. **ETR (Egress Tunnel Router)** — decapsulates LISP packets and delivers to the local EID. **Map-Server (MS)** — accepts EID-to-RLOC registrations from ETRs; stores the mapping database. **Map-Resolver (MR)** — accepts map-requests from ITRs and forwards them to the appropriate MS/ETR for resolution. Often MS and MR run on the same device.",
      tags: ["lisp", "map-server", "map-resolver", "itr", "etr"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-270",
      domain: "2.0",
      subdomain: "LISP",
      front: "What is a **PxTR (Proxy Tunnel Router)** in LISP?",
      back: "A PxTR acts as both a **PITR (Proxy ITR)** and **PETR (Proxy ETR)** to enable communication between LISP sites and **non-LISP sites**. The PITR attracts traffic destined for LISP EIDs from the non-LISP internet (by advertising EID prefixes into BGP) and encapsulates it toward the ETR. The PETR accepts LISP-encapsulated traffic from ITRs that need to reach non-LISP destinations and de-encapsulates it into the public internet.",
      tags: ["lisp", "pxtr", "pitr", "petr", "interworking"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-271",
      domain: "2.0",
      subdomain: "LISP",
      front: "How does LISP handle **host mobility** (e.g., a VM moving between data centers)?",
      back: "When a host moves to a new site, the new ETR sends an updated **Map-Register** to the Map-Server with the EID mapped to the new RLOC. The Map-Server sends a **Map-Notify** to the old ETR, which sends a **Solicit Map-Request (SMR)** to all ITRs that have a cached mapping. Those ITRs then re-query the mapping system and get the **new RLOC**. This process converges in seconds, enabling seamless VM mobility without DNS or IP address changes.",
      tags: ["lisp", "mobility", "map-register", "smr"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- VXLAN ---

    {
      id: "starter-272",
      domain: "2.0",
      subdomain: "VXLAN",
      front: "What is **VXLAN** and what problem does it solve?",
      back: "**VXLAN (Virtual Extensible LAN)** is a MAC-in-UDP encapsulation that extends Layer 2 segments over a Layer 3 underlay. It solves the **4,094 VLAN limit** by using a **24-bit VNI (VXLAN Network Identifier)**, supporting up to ~16 million logical segments. It also eliminates STP dependency by using the L3 underlay's ECMP for load balancing. VXLAN is the standard overlay in modern data centers and SD-Access fabrics.",
      tags: ["vxlan", "vni", "overlay", "data-center"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-273",
      domain: "2.0",
      subdomain: "VXLAN",
      front: "What is a **VTEP (VXLAN Tunnel Endpoint)** and where does it run?",
      back: "A VTEP performs VXLAN **encapsulation and decapsulation**. It has two interfaces: a **local LAN interface** (facing VMs/hosts) and an **IP interface** in the underlay (the tunnel source). The ingress VTEP encapsulates the original L2 frame in a VXLAN+UDP+IP header using the VNI; the egress VTEP strips the encapsulation. VTEPs typically run on **leaf switches** (hardware VTEPs) or **hypervisor vSwitches** (software VTEPs).",
      tags: ["vxlan", "vtep", "encapsulation", "leaf"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-274",
      domain: "2.0",
      subdomain: "VXLAN",
      front: "Compare **flood-and-learn** vs **BGP EVPN** control plane for VXLAN.",
      back: "**Flood-and-learn** (data plane learning): Unknown unicast is flooded via multicast in the underlay. VTEPs learn remote MAC addresses from received VXLAN frames. Simple but generates **excessive flooding** and requires multicast in the underlay. **BGP EVPN** (control plane learning): MAC/IP reachability is distributed via **MP-BGP EVPN address family** (Route Type 2 for MAC/IP, Type 3 for VTEP discovery). Eliminates flooding, enables **ARP suppression**, and supports integrated routing/bridging (asymmetric/symmetric IRB).",
      tags: ["vxlan", "bgp-evpn", "flood-and-learn", "control-plane"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-275",
      domain: "2.0",
      subdomain: "VXLAN",
      front: "What is the overhead added by VXLAN encapsulation and how does it affect MTU?",
      back: "VXLAN adds a **50-byte overhead**: 8-byte VXLAN header + 8-byte UDP header + 20-byte outer IP header + 14-byte outer Ethernet header. This reduces the effective MTU from 1500 to **1450 bytes** for the inner frame. Best practice: configure the underlay to support **jumbo frames (MTU 9216)** so the 50-byte overhead does not cause fragmentation of standard 1500-byte frames.",
      tags: ["vxlan", "mtu", "overhead", "jumbo-frames"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-276",
      domain: "2.0",
      subdomain: "VXLAN",
      front: "What is a **VNI (VXLAN Network Identifier)** and how does it map to VLANs?",
      back: "The VNI is a **24-bit identifier** in the VXLAN header that identifies the logical L2 segment (like a VLAN ID but with ~16 million possible values). Each VTEP maintains a **VNI-to-VLAN mapping** — the local VLAN ID can differ between VTEPs as long as they share the same VNI. For L3 VNI (used in VXLAN routing), the VNI maps to a **VRF** rather than a VLAN, enabling inter-subnet routing across the fabric.",
      tags: ["vxlan", "vni", "vlan-mapping", "l3vni"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional VRF cards ---

    {
      id: "starter-277",
      domain: "2.0",
      subdomain: "VRF",
      front: "What command creates a VRF on IOS-XE and assigns an interface to it? What happens to existing IP config?",
      back: "Create VRF: `vrf definition NAME` then `address-family ipv4` (or ipv6). Assign interface: `interface GigX/X` then `vrf forwarding NAME`. **Important**: Assigning a VRF to an interface **removes the existing IP address** — you must re-apply it after the `vrf forwarding` command. This is a classic exam trap. Verify with `show vrf` and `show ip route vrf NAME`.",
      tags: ["vrf", "ios-xe", "configuration", "exam-trap"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional DMVPN cards ---

    {
      id: "starter-278",
      domain: "2.0",
      subdomain: "DMVPN",
      front: "What routing protocol considerations exist for **DMVPN** and why is EIGRP commonly used?",
      back: "**EIGRP** is popular because it is simple and supports DMVPN well, but requires `no ip split-horizon eigrp` on the hub's tunnel interface (since all spokes are on the same subnet). **OSPF** works but the hub tunnel must be **point-to-multipoint** (not broadcast) to avoid DR election issues. **BGP** scales best for large DMVPN (iBGP with hub as route reflector). For all protocols: the hub must not change the **next-hop** in Phase 2, or spoke-to-spoke breaks.",
      tags: ["dmvpn", "eigrp", "ospf", "bgp", "routing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional LISP card ---

    {
      id: "starter-279",
      domain: "2.0",
      subdomain: "LISP",
      front: "What transport does LISP use for data-plane encapsulation and control-plane messaging?",
      back: "**Data plane**: LISP encapsulates the original IP packet in a **LISP header + UDP (port 4341)**. **Control plane**: Map-Requests and Map-Replies use **UDP port 4342**. Map-Register and Map-Notify also use UDP 4342 between ETRs and Map-Servers. The LISP header includes a 24-bit **Instance ID** (equivalent to VNI in VXLAN) for multi-tenancy/segmentation.",
      tags: ["lisp", "encapsulation", "udp", "instance-id"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional GRE card ---

    {
      id: "starter-280",
      domain: "2.0",
      subdomain: "GRE",
      front: "What is the GRE **Protocol Type** field and what value does it carry for IPv4 passenger traffic?",
      back: "The GRE header contains a **Protocol Type** field (16 bits) that identifies the encapsulated payload protocol, using **EtherType values**. For IPv4 passenger traffic, the value is **0x0800**. For IPv6, it is **0x86DD**. This is what makes GRE **protocol-agnostic** — it can carry any protocol by specifying the appropriate EtherType, unlike IPsec which natively only handles IP.",
      tags: ["gre", "protocol-type", "encapsulation", "ethertype"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional HA cards ---

    {
      id: "starter-281",
      domain: "1.0",
      subdomain: "HighAvailability",
      front: "What are the two BFD modes and which is most commonly deployed?",
      back: "**Asynchronous mode**: Both ends send BFD control packets at negotiated intervals; if a configured number of consecutive packets are missed, the session is declared down. **Demand mode**: BFD packets are only sent when explicitly requested (polling). **Asynchronous mode** is the most commonly deployed because it provides continuous monitoring without requiring explicit polling. Most IOS-XE platforms only support asynchronous mode.",
      tags: ["bfd", "asynchronous", "demand-mode", "high-availability"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional QoS card ---

    {
      id: "starter-282",
      domain: "1.0",
      subdomain: "QoS",
      front: "What DSCP value does **EF (Expedited Forwarding)** map to, and what traffic typically uses it?",
      back: "EF maps to **DSCP 46** (binary 101110, decimal 46). It is used for **voice bearer traffic** (RTP media streams) that requires low latency, low jitter, and low loss. EF traffic is placed in the **strict priority queue** (LLQ) and is always serviced first. The priority queue must be **policed** to prevent starvation of other classes — excess EF traffic is dropped, not queued.",
      tags: ["qos", "ef", "dscp", "voice", "priority-queue"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional SD-WAN card ---

    {
      id: "starter-283",
      domain: "1.0",
      subdomain: "SD-WAN",
      front: "What is the **vManage** component in SD-WAN and what does it provide?",
      back: "vManage is the **centralized management platform** (single pane of glass) for the SD-WAN fabric. It provides: **1) GUI and REST API** for configuration, monitoring, and troubleshooting. **2) Template-based device provisioning** (feature templates + device templates). **3) Centralized policy configuration** that is pushed to vSmart (control/data policies) or directly to WAN Edges (app-aware routing, localized policies). **4) Real-time monitoring** of tunnel health, app performance, and device status.",
      tags: ["sd-wan", "vmanage", "management-plane", "templates"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional CEF card ---

    {
      id: "starter-284",
      domain: "1.0",
      subdomain: "CEF",
      front: "What are the different **adjacency types** in the CEF adjacency table?",
      back: "**1) Complete adjacency** — full L2 rewrite info available (MAC address known via ARP/ND). **2) Glean** — destination is directly connected but L2 address unknown; triggers ARP. **3) Null** — packet is destined for a null route; silently dropped. **4) Drop** — packet cannot be forwarded (e.g., no route); dropped and may generate ICMP unreachable. **5) Punt** — packet requires CPU processing (e.g., routing protocol, TTL=1, options in header).",
      tags: ["cef", "adjacency-table", "punt", "glean", "drop"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional Enterprise Design cards ---

    {
      id: "starter-285",
      domain: "1.0",
      subdomain: "EnterpriseDesign",
      front: "What is the **oversubscription ratio** in a spine-leaf topology and how do you calculate it?",
      back: "Oversubscription ratio = **total server-facing bandwidth : total uplink bandwidth per leaf**. Example: a leaf with 48x10G server ports (480G) and 6x40G spine uplinks (240G) has a **2:1** oversubscription ratio. A **1:1 ratio** (non-blocking) means the leaf can push all server traffic to the spines simultaneously. Data center design typically targets **3:1 or better** for general workloads, **1:1** for high-performance computing or storage.",
      tags: ["spine-leaf", "oversubscription", "data-center", "design"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional SD-Access card ---

    {
      id: "starter-286",
      domain: "1.0",
      subdomain: "SD-Access",
      front: "What is the **underlay** in SD-Access and which protocol builds it?",
      back: "The SD-Access underlay is the **physical IP transport network** (routed infrastructure) that carries encapsulated overlay traffic. DNA Center automates the underlay using **IS-IS** as the routing protocol (chosen for its simplicity, scalability, and protocol-agnostic nature). The underlay provides IP reachability between all fabric node loopbacks (used as RLOC addresses). All fabric overlay protocols (LISP, VXLAN) ride on top of this IS-IS routed underlay.",
      tags: ["sd-access", "underlay", "is-is", "dna-center"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // EXPANSION WAVE — DOMAIN 3.0 + 4.0 (Infrastructure + Network Assurance)
    // =========================================================
    // =========================================================
    // DOMAIN 3.0 — INFRASTRUCTURE (~51 cards)
    // =========================================================

    // --- OSPF Advanced (10 cards) ---
    {
      id: "starter-287",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What are the five OSPF LSA types tested on ENCOR, and what does each describe?",
      back: "**Type 1 (Router LSA)** — Every router's directly connected links within an area.\n**Type 2 (Network LSA)** — Generated by the DR on multi-access networks; lists all routers on that segment.\n**Type 3 (Summary LSA)** — ABR advertises inter-area prefixes into other areas.\n**Type 4 (ASBR Summary LSA)** — ABR advertises reachability to an ASBR.\n**Type 5 (External LSA)** — ASBR advertises external (redistributed) routes domain-wide.\n**Type 7 (NSSA External LSA)** — External routes within an NSSA; converted to Type 5 at the ABR.",
      tags: ["ospf", "lsa-types", "link-state"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-288",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is the difference between a **stub area** and a **totally stubby area** in OSPF?",
      back: "A **stub area** blocks Type 5 (external) LSAs and replaces them with a default route (Type 3) injected by the ABR. It still receives inter-area Type 3 LSAs.\n\nA **totally stubby area** (Cisco proprietary) blocks **both** Type 3 (inter-area) and Type 5 LSAs, injecting only a single default route. Configured with `area X stub no-summary` on the ABR.",
      tags: ["ospf", "stub-areas", "totally-stubby"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-289",
      domain: "3.0",
      subdomain: "OSPF",
      front: "How does an NSSA differ from a regular stub area, and what happens to Type 7 LSAs at the ABR?",
      back: "An **NSSA (Not-So-Stubby Area)** allows an ASBR within the stub area to redistribute external routes as **Type 7 LSAs**. The ABR converts Type 7 LSAs into **Type 5 LSAs** before flooding them into the backbone (Area 0). Regular stub areas cannot have ASBRs. Configure with `area X nssa`.",
      tags: ["ospf", "nssa", "type-7-lsa"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-290",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is an OSPF **virtual link**, and when is it required?",
      back: "A **virtual link** is a logical link through a transit area that connects a disconnected area to Area 0. It is required when:\n- An area cannot be physically connected to the backbone\n- Area 0 becomes partitioned\n\nConfigured on both ABRs: `area <transit-area> virtual-link <remote-router-id>`. The transit area cannot be a stub area.",
      tags: ["ospf", "virtual-link", "area-0"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-291",
      domain: "3.0",
      subdomain: "OSPF",
      front: "Describe the OSPF path selection order of preference.",
      back: "OSPF selects paths in this order:\n1. **Intra-area routes (O)** — always preferred\n2. **Inter-area routes (O IA)** — next preference\n3. **External Type 1 (E1)** — metric = internal cost + external cost\n4. **External Type 2 (E2)** — metric = external cost only (default)\n5. **NSSA Type 1 (N1)** and **NSSA Type 2 (N2)** follow the same E1/E2 logic within the NSSA\n\nWithin the same route type, lowest **cost** wins.",
      tags: ["ospf", "path-selection", "route-types"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-292",
      domain: "3.0",
      subdomain: "OSPF",
      front: "How does the OSPF SPF algorithm work at a high level?",
      back: "The **Dijkstra SPF algorithm** builds a shortest-path tree:\n1. The router places itself as the **root** of the tree\n2. It examines all Type 1 (Router) and Type 2 (Network) LSAs in its LSDB\n3. It calculates the cumulative **cost** to reach every destination\n4. The shortest-cost path to each destination is installed in the routing table\n5. When an LSA changes, SPF is re-run (throttled by `timers throttle spf` — initial/hold/max intervals)",
      tags: ["ospf", "spf", "dijkstra"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-293",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is a **totally NSSA** in OSPF, and how is it configured?",
      back: "A **totally NSSA** blocks Type 3 (inter-area) and Type 5 (external) LSAs but allows Type 7 (NSSA external) LSAs from a local ASBR. The ABR injects only a default route as a Type 3 LSA.\n\nConfigured on the ABR: `area X nssa no-summary`\nOther routers in the area: `area X nssa`\n\nThis combines the benefits of totally stubby (minimal LSDB) with the ability to redistribute locally.",
      tags: ["ospf", "totally-nssa", "stub-areas"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-294",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What are the key differences between OSPFv2 and OSPFv3?",
      back: "**OSPFv3** (RFC 5340) supports IPv6 (and IPv4 with address families):\n- Runs over **link-local** IPv6 addresses (not global unicast for hellos)\n- Uses **instance IDs** to support multiple instances per link\n- Authentication moved to **IPsec** (AH/ESP) instead of OSPF-native auth\n- LSAs carry **prefix information** separately from topology (new LSA types: Link-LSA, Intra-Area-Prefix-LSA)\n- Neighbors identified by **Router ID** only, not IP address\n- Configured per-interface: `ipv6 ospf <process> area <X>` (classic) or via address-family in router mode",
      tags: ["ospf", "ospfv3", "ipv6"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-295",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What is the OSPF **forwarding address** in a Type 5 LSA, and when is it set to a non-zero value?",
      back: "The **forwarding address** in a Type 5 LSA tells routers where to send traffic for the external route. It is set to a **non-zero** value when:\n1. The ASBR's next-hop interface to the external source is an OSPF-enabled **non-passive** broadcast/NBMA network\n2. The external next-hop is reachable through that OSPF interface\n\nWhen non-zero, routers forward traffic directly to the forwarding address (potentially a different router), enabling more optimal routing. When zero, traffic goes to the ASBR.",
      tags: ["ospf", "forwarding-address", "type-5-lsa"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-296",
      domain: "3.0",
      subdomain: "OSPF",
      front: "What LSA types are allowed in each OSPF area type? (Normal, Stub, Totally Stubby, NSSA, Totally NSSA)",
      back: "| Area Type | Type 1 | Type 2 | Type 3 | Type 4 | Type 5 | Type 7 |\n|---|---|---|---|---|---|---|\n| **Normal** | Yes | Yes | Yes | Yes | Yes | No |\n| **Stub** | Yes | Yes | Yes | No | No | No |\n| **Totally Stubby** | Yes | Yes | Default only | No | No | No |\n| **NSSA** | Yes | Yes | Yes | No | No | Yes |\n| **Totally NSSA** | Yes | Yes | Default only | No | No | Yes |\n\nType 1 and Type 2 are always present. Stub variants block Type 5; NSSA variants allow Type 7 instead.",
      tags: ["ospf", "area-types", "lsa-types"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- EIGRP Advanced (7 cards) ---
    {
      id: "starter-297",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "Explain the EIGRP **feasibility condition** and why it prevents routing loops.",
      back: "The **feasibility condition (FC)** states: a neighbor qualifies as a **feasible successor** if its **reported distance (RD)** to the destination is **less than** the current **feasible distance (FD)** of the successor route.\n\nFD = best total metric to the destination\nRD = the neighbor's total metric to the destination (advertised distance)\n\nThis guarantees the neighbor is **downstream** (closer to the destination), which mathematically prevents routing loops without needing to query further.",
      tags: ["eigrp", "dual", "feasibility-condition"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-298",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What is the difference between a **successor** and a **feasible successor** in EIGRP?",
      back: "**Successor** — The neighbor with the **lowest total metric** (feasible distance) to the destination. This is the primary path installed in the routing table.\n\n**Feasible Successor (FS)** — A backup neighbor whose **reported distance (RD) < the current feasible distance (FD)**. It meets the feasibility condition and can be used **immediately** if the successor fails — no DUAL query needed. This enables sub-second convergence.",
      tags: ["eigrp", "successor", "feasible-successor"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-299",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What causes EIGRP routes to become **stuck-in-active (SIA)**, and how does IOS-XE mitigate it?",
      back: "**SIA** occurs when a router sends a DUAL query for a route and does not receive a reply within the **active timer** (default 3 minutes). The neighbor is declared SIA and the adjacency is torn down.\n\nCauses: query propagation across large networks, slow/congested links, CPU-bound routers.\n\nMitigation:\n- **SIA-Query/SIA-Reply** messages (halfway through the timer) give the neighbor a chance to report progress\n- **Stub routing** — stub routers are never queried, limiting query scope\n- **Route summarization** — queries stop at summary boundaries\n- **Distribute-lists** — limit query scope",
      tags: ["eigrp", "stuck-in-active", "dual"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-300",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What advantages does EIGRP **named mode** have over classic mode?",
      back: "**EIGRP named mode** (`router eigrp <name>`) advantages:\n- **Single hierarchical config** — all settings (address-family, af-interface, topology) under one router stanza\n- **Per-AF configuration** — IPv4 and IPv6 under the same EIGRP instance\n- **Wide metrics** — 64-bit metric calculation using `rib-scale` to fit into the 32-bit RIB; prevents metric ties on high-bandwidth links\n- **Per-interface settings** under `af-interface` — hello/hold timers, authentication, split-horizon, etc.\n- **Cleaner troubleshooting** — `show eigrp address-family ipv4` commands",
      tags: ["eigrp", "named-mode", "wide-metrics"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-301",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "How do EIGRP **wide metrics** differ from classic metrics, and why are they needed?",
      back: "**Classic metrics** use 32-bit values with the formula: `256 × (K1×BW + K3×Delay)` (default K values). Bandwidth is scaled as `10^7 / BW_kbps`. Links above **10 Gbps** all resolve to the same metric.\n\n**Wide metrics** (named mode) use **64-bit** values:\n- Throughput = `65,536 × 10^7 / BW_kbps`\n- Latency = `65,536 × delay_picoseconds / 10^6`\n- Can differentiate 10G, 40G, 100G+ interfaces\n- A **rib-scale** divisor (default 128) scales the 64-bit composite metric down to fit the 32-bit RIB",
      tags: ["eigrp", "wide-metrics", "named-mode"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-302",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "How does the EIGRP **DUAL algorithm** handle a topology change when no feasible successor exists?",
      back: "When the successor is lost and **no feasible successor** meets the FC:\n1. The route transitions from **Passive** to **Active** state\n2. The router sends **Query** packets to all neighbors (except the lost successor)\n3. Neighbors either reply with their own distance or propagate the query further\n4. Once **all replies** are received, DUAL selects the best path and the route returns to **Passive**\n5. If replies aren't received within the active timer (3 min), the route goes **SIA**\n\nDuring Active state, the route remains in the routing table but is marked as active.",
      tags: ["eigrp", "dual", "active-state"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-303",
      domain: "3.0",
      subdomain: "EIGRP",
      front: "What is an EIGRP **stub router**, and what route types can it advertise?",
      back: "An EIGRP **stub router** advertises itself as a stub to its neighbors, which then **never send queries** to it — drastically reducing SIA risk and query scope.\n\nAllowed advertisement types (configured with `eigrp stub`):\n- **connected** — directly connected networks (default)\n- **summary** — summary routes (default)\n- **static** — redistributed static routes\n- **redistributed** — all redistributed routes\n- **receive-only** — advertises nothing, only receives routes\n\nDefault: `eigrp stub connected summary`",
      tags: ["eigrp", "stub", "query-scope"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- BGP Advanced (9 cards) ---
    {
      id: "starter-304",
      domain: "3.0",
      subdomain: "BGP",
      front: "List the BGP path selection algorithm steps (top 8 most critical).",
      back: "BGP selects the best path using these criteria (in order):\n1. **Highest Weight** (Cisco-local, default 0; 32768 for locally originated)\n2. **Highest Local Preference** (default 100, shared within iBGP)\n3. **Locally originated** (network/redistribute/aggregate)\n4. **Shortest AS_PATH**\n5. **Lowest Origin type** (IGP < EGP < Incomplete)\n6. **Lowest MED** (compared only among paths from the same neighboring AS, by default)\n7. **eBGP over iBGP**\n8. **Lowest IGP metric** to the BGP next-hop\n\nTiebreakers continue: oldest eBGP route, lowest neighbor RID, shortest cluster list, lowest neighbor IP.",
      tags: ["bgp", "path-selection", "best-path"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-307",
      domain: "3.0",
      subdomain: "BGP",
      front: "What are the four well-known BGP communities and what does each do?",
      back: "**Well-known BGP communities:**\n\n1. **no-export** — Do not advertise to eBGP peers (stays within the AS or confederation sub-AS)\n2. **no-advertise** — Do not advertise to **any** peer (iBGP or eBGP)\n3. **local-AS** (no-export-subconfed) — Do not advertise outside the local confederation sub-AS (or local AS if not using confederations)\n4. **internet** — Advertise to all peers (default behavior; rarely explicitly set)\n\nCommunities are set via route-maps: `set community no-export` and sent with `neighbor X send-community`.",
      tags: ["bgp", "communities", "no-export"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-308",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is BGP **soft reconfiguration**, and how does the route-refresh capability make it easier?",
      back: "**Soft reconfiguration** allows you to re-apply inbound/outbound policies without tearing down the BGP session.\n\n**Legacy method:** `neighbor X soft-reconfiguration inbound` — stores a copy of all received routes **before** policy (uses extra memory).\n\n**Route-refresh capability** (RFC 2918): The router sends a ROUTE-REFRESH message asking the peer to re-send its full Adj-RIB-Out. No stored copy needed. Supported by all modern IOS-XE. Check with `show bgp neighbors | include refresh`.\n\nTrigger: `clear bgp ipv4 unicast X soft in/out`",
      tags: ["bgp", "soft-reconfiguration", "route-refresh"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-310",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is the purpose of BGP **Weight**, and how does it differ from Local Preference?",
      back: "**Weight** is a **Cisco-proprietary** attribute, local to the router — it is never advertised to any peer.\n- Default: **0** for learned routes, **32768** for locally originated routes\n- **Highest weight wins** (Step 1 of best path selection)\n\n**Local Preference** is a standard attribute shared across all **iBGP** peers within the AS.\n- Default: **100**\n- **Highest LP wins** (Step 2)\n\nUse Weight to influence path selection on a **single router** without affecting the rest of the AS. Use Local Preference to influence the **entire AS**.",
      tags: ["bgp", "weight", "local-preference"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-311",
      domain: "3.0",
      subdomain: "BGP",
      front: "What is the BGP **MED** (Multi-Exit Discriminator), and how is it used?",
      back: "**MED** suggests to an external AS which entry point to prefer when multiple links exist between two ASes.\n- Lower MED is preferred (Step 6 of best path)\n- **Non-transitive optional** — not passed beyond the receiving AS\n- By default, MED is compared **only** among paths from the **same neighboring AS**\n- `bgp always-compare-med` forces comparison across different ASes (use with caution)\n- `bgp bestpath med missing-as-worst` treats missing MED as infinity instead of 0\n- Default MED value: **0** (best possible) when not set",
      tags: ["bgp", "med", "path-selection"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-312",
      domain: "3.0",
      subdomain: "BGP",
      front: "How do BGP **route reflector** loop prevention mechanisms work?",
      back: "Two BGP attributes prevent RR loops:\n\n**Originator ID:**\n- Set by the RR to the **Router ID** of the route originator\n- If a router receives a route with its own RID as Originator ID, it **discards** the route\n\n**Cluster List:**\n- Each RR prepends its **Cluster ID** when reflecting a route\n- If a RR receives a route with its own Cluster ID already in the Cluster List, it **discards** the route\n- Prevents loops in multi-level RR hierarchies\n\nCluster ID defaults to the RR's Router ID but can be manually set with `bgp cluster-id`.",
      tags: ["bgp", "route-reflector", "loop-prevention"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Route Redistribution (5 cards) ---
    {
      id: "starter-313",
      domain: "3.0",
      subdomain: "Redistribution",
      front: "What is a **seed metric** in route redistribution, and what are the defaults for OSPF and EIGRP?",
      back: "The **seed metric** is the initial metric assigned to redistributed routes in the receiving protocol.\n\n**OSPF:** Default seed metric = **20** for most sources (1 for BGP). Redistributed routes become **E2** (Type 2 external) by default. Override with `redistribute <proto> metric <value> metric-type <1|2>`.\n\n**EIGRP:** Default seed metric = **infinity** (routes are unreachable) — you **must** set a seed metric. Use `redistribute <proto> metric <bw> <dly> <rel> <load> <mtu>` or `default-metric` command.\n\nForgetting the EIGRP seed metric is one of the most common redistribution mistakes.",
      tags: ["redistribution", "seed-metric", "ospf", "eigrp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-314",
      domain: "3.0",
      subdomain: "Redistribution",
      front: "How can **route tags** be used to prevent routing loops during mutual redistribution?",
      back: "In mutual redistribution (two protocols redistributing into each other), routes can be **re-redistributed** back into their source protocol with different metrics, causing loops or suboptimal routing.\n\n**Tag-based prevention:**\n1. When redistributing from Protocol A → B, **tag** the routes (e.g., tag 100)\n2. On the reverse redistribution (B → A), use a **route-map** to **deny** routes with tag 100\n3. This prevents routes from being fed back into their original protocol\n\nExample: `set tag 100` on redistribute into OSPF; `deny tag 100` on redistribute back into EIGRP.",
      tags: ["redistribution", "route-tags", "loop-prevention"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-315",
      domain: "3.0",
      subdomain: "Redistribution",
      front: "What are the common pitfalls of **mutual redistribution** between OSPF and EIGRP?",
      back: "Common pitfalls:\n1. **Suboptimal routing** — Redistributed routes may have lower AD than internal routes (EIGRP external AD=170, OSPF E2 AD=110)\n2. **Routing loops** — Routes re-redistributed back into their source protocol\n3. **Metric mismatch** — EIGRP needs explicit seed metrics; OSPF defaults may not reflect reality\n4. **Administrative distance overlap** — OSPF internal (110) < EIGRP internal (90), but OSPF external (110) < EIGRP external (170)\n5. **Missing subnets keyword** — OSPF needs `subnets` keyword or only classful networks are redistributed\n\nMitigation: Use route-maps with tags, adjust AD, and filter carefully.",
      tags: ["redistribution", "mutual-redistribution", "pitfalls"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-316",
      domain: "3.0",
      subdomain: "Redistribution",
      front: "What role do **route-maps** play in redistribution, and what can they match/set?",
      back: "**Route-maps** act as the policy engine for redistribution, controlling which routes are redistributed and modifying their attributes.\n\n**Common match criteria:**\n- `match ip address <ACL/prefix-list>` — filter by prefix\n- `match tag <value>` — filter by route tag\n- `match route-type <external/internal>` — OSPF route types\n- `match metric <value>` — source metric\n\n**Common set actions:**\n- `set metric <value>` — seed metric in the target protocol\n- `set metric-type type-1` — OSPF E1 vs E2\n- `set tag <value>` — loop prevention tag\n- `set community` — BGP community\n\nImplicit deny at the end blocks unmatched routes.",
      tags: ["redistribution", "route-maps", "filtering"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-317",
      domain: "3.0",
      subdomain: "Redistribution",
      front: "When redistributing into OSPF, what is the difference between **E1** and **E2** external routes?",
      back: "**E2 (External Type 2)** — Default. The metric is the **external cost only** and does NOT increase as the route propagates through the OSPF domain. All routers see the same metric regardless of their distance from the ASBR.\n\n**E1 (External Type 1)** — The metric is the **external cost + internal OSPF cost** to reach the ASBR. This allows OSPF to prefer closer ASBRs when multiple redistribution points exist.\n\n**Best practice:** Use E1 when you have multiple redistribution points to enable optimal path selection within OSPF. E1 routes are always preferred over E2 routes for the same prefix.",
      tags: ["redistribution", "ospf", "e1-e2"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Route Filtering (3 cards) ---
    {
      id: "starter-318",
      domain: "3.0",
      subdomain: "RouteFiltering",
      front: "What is a **prefix-list** and how does it differ from a standard ACL for route filtering?",
      back: "A **prefix-list** matches routes based on **network address AND prefix length** — much more precise than ACLs.\n\n**Key advantages over ACLs:**\n- Can match exact prefix length: `ip prefix-list PL permit 10.0.0.0/8`\n- Can match a range of prefix lengths: `ip prefix-list PL permit 10.0.0.0/8 ge 16 le 24`\n- **ge** (greater-or-equal) and **le** (less-or-equal) provide length range matching\n- More efficient processing (stored as a tree structure)\n- Cannot accidentally match traffic (ACLs can be confused with packet filtering)\n\nUsed with: `distribute-list prefix`, `neighbor prefix-list`, route-map `match ip address prefix-list`.",
      tags: ["route-filtering", "prefix-list", "acl"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-319",
      domain: "3.0",
      subdomain: "RouteFiltering",
      front: "How do **distribute-lists** work in OSPF vs. distance vector protocols?",
      back: "**Distance vector (EIGRP, RIP):** distribute-lists filter routes **sent and received** on interfaces. They affect the routing table directly because the protocol shares its routing table.\n\n**OSPF:** distribute-lists **only filter inbound** and only affect what enters the **local routing table** — they do NOT prevent LSA flooding. The LSDB remains complete; the router just doesn't install the filtered route.\n\nFor OSPF, to truly filter routes from propagating, use:\n- `area X filter-list prefix` on an ABR (filters Type 3 LSAs between areas)\n- Stub/NSSA area configuration\n- Route summarization with `not-advertise`",
      tags: ["route-filtering", "distribute-list", "ospf"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-320",
      domain: "3.0",
      subdomain: "RouteFiltering",
      front: "How can you use a **route-map** for BGP route filtering, and what are the match/set options?",
      back: "Route-maps applied with `neighbor X route-map <name> in|out` filter and modify BGP routes.\n\n**Filtering (match + permit/deny):**\n- `match ip address prefix-list <name>` — match prefixes\n- `match as-path <acl>` — match AS_PATH with regex\n- `match community <list>` — match BGP communities\n- Deny clause = filter (drop) the route\n\n**Modification (set in permit clause):**\n- `set local-preference <value>`\n- `set weight <value>`\n- `set as-path prepend <ASN ASN...>`\n- `set community <value> [additive]`\n- `set metric <value>` (MED)\n\nImplicit deny at end blocks unmatched routes — always add a final `permit` clause if needed.",
      tags: ["route-filtering", "bgp", "route-maps"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- STP Advanced (5 cards) ---
    {
      id: "starter-321",
      domain: "3.0",
      subdomain: "STP",
      front: "What are the RSTP (802.1w) port roles and states, and how do they differ from 802.1D?",
      back: "**RSTP Port Roles:**\n- **Root Port** — Best path to root bridge (same as 802.1D)\n- **Designated Port** — Best port on a segment toward the root (same)\n- **Alternate Port** — Backup path to root (replaces 802.1D blocking; provides rapid failover for root port)\n- **Backup Port** — Redundant path to the same segment via same switch (rare, shared media)\n\n**RSTP States (3 vs 5):**\n- **Discarding** — combines 802.1D Disabled, Blocking, Listening\n- **Learning** — populating MAC table\n- **Forwarding** — fully operational\n\nRSTP converges in seconds via proposal/agreement mechanism, not timers.",
      tags: ["stp", "rstp", "port-roles"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-322",
      domain: "3.0",
      subdomain: "STP",
      front: "What is **MST (Multiple Spanning Tree)** and how does it reduce STP overhead compared to PVST+?",
      back: "**MST (802.1s)** maps multiple VLANs to a reduced number of **spanning tree instances**.\n\nKey concepts:\n- **MST Region** — Switches with the same **name**, **revision number**, and **VLAN-to-instance mapping** form a region\n- **IST (Instance 0)** — Internal Spanning Tree; always exists, handles BPDUs between regions\n- **MSTI (1-15)** — Additional instances you configure; each handles a group of VLANs\n\n**Advantage over PVST+:** Instead of running one STP instance per VLAN (1000 VLANs = 1000 instances), MST might use 3-4 instances for load balancing. Dramatically reduces CPU and memory on switches.",
      tags: ["stp", "mst", "pvst"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-323",
      domain: "3.0",
      subdomain: "STP",
      front: "Explain BPDU Guard, BPDU Filter, Root Guard, and Loop Guard — what does each protect against?",
      back: "**BPDU Guard:** Shuts down (err-disable) a PortFast port if **any** BPDU is received. Protects against rogue switches. Enable: `spanning-tree bpduguard enable`.\n\n**BPDU Filter:** Stops sending BPDUs and ignores received BPDUs on the port. **Dangerous** — effectively disables STP. Per-port only with PortFast. Global `spanning-tree portfast bpdufilter default` only filters on PortFast ports and reverts to normal STP if BPDU received.\n\n**Root Guard:** Prevents a port from becoming root port. If a superior BPDU is received, port enters **root-inconsistent** state (blocking). Protects root bridge placement.\n\n**Loop Guard:** Prevents a port from transitioning to forwarding if BPDUs stop arriving (e.g., unidirectional link). Port enters **loop-inconsistent** state. Complements UDLD.",
      tags: ["stp", "bpdu-guard", "root-guard", "loop-guard"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-324",
      domain: "3.0",
      subdomain: "STP",
      front: "What is **UDLD (Unidirectional Link Detection)** and how does it complement Loop Guard?",
      back: "**UDLD** detects **unidirectional links** where one fiber strand fails but the other still works. Without detection, STP may forward on a port that can't actually receive BPDUs, causing loops.\n\n**Modes:**\n- **Normal mode** — Marks port as \"undetermined\" but does not shut it down\n- **Aggressive mode** — Sends 8 UDLD probes; if no response, puts port in **err-disable**\n\n**Complements Loop Guard:** UDLD works at Layer 1 (detects physical unidirectional issues); Loop Guard works at the STP level (detects missing BPDUs). Both should be enabled on fiber uplinks for defense in depth.\n\nEnable: `udld enable` (normal) or `udld aggressive` per interface.",
      tags: ["stp", "udld", "loop-prevention"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-325",
      domain: "3.0",
      subdomain: "STP",
      front: "How does the RSTP **proposal/agreement** mechanism achieve rapid convergence?",
      back: "When a switch connects to a new segment or a topology change occurs:\n1. The **designated port** sends a BPDU with the **Proposal** bit set\n2. The downstream switch recognizes the superior BPDU and **blocks all non-edge ports** (sync)\n3. The downstream switch sends a BPDU with the **Agreement** bit back\n4. The upstream designated port immediately transitions to **Forwarding** — no timer wait\n\nThis \"proposal/agreement handshake\" propagates through the network in a wave, achieving convergence in **seconds** instead of the 30-50 seconds of 802.1D. Only works on **point-to-point** links (full-duplex).",
      tags: ["stp", "rstp", "proposal-agreement"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- EtherChannel (3 cards) ---
    {
      id: "starter-326",
      domain: "3.0",
      subdomain: "EtherChannel",
      front: "What are the differences between **LACP** and **PAgP** for EtherChannel negotiation?",
      back: "**LACP (802.3ad/802.1AX)** — Open standard:\n- Modes: **active** (initiates) and **passive** (responds only)\n- Both sides passive = no channel formed\n- Supports up to **16 links** (8 active + 8 standby/hot-standby)\n- Uses **system priority** and **port priority** for selection\n\n**PAgP** — Cisco proprietary:\n- Modes: **desirable** (initiates) and **auto** (responds only)\n- Both sides auto = no channel formed\n- Max **8 links**, no standby\n\n**On** mode = forced, no negotiation protocol. Both sides must be \"on\" — no mismatch detection.\n\n**Best practice:** Use LACP for multi-vendor compatibility and hot-standby capability.",
      tags: ["etherchannel", "lacp", "pagp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-327",
      domain: "3.0",
      subdomain: "EtherChannel",
      front: "What are the EtherChannel **load balancing** methods, and which should you choose?",
      back: "EtherChannel distributes frames across member links using a hash algorithm. Methods include:\n- **src-mac** / **dst-mac** / **src-dst-mac** — Layer 2 hashing\n- **src-ip** / **dst-ip** / **src-dst-ip** — Layer 3 hashing (most common)\n- **src-port** / **dst-port** / **src-dst-port** — Layer 4 hashing\n- **src-dst-mixed-ip-port** — Combined L3+L4 (best distribution)\n\nConfigure globally: `port-channel load-balance <method>`\nVerify: `show etherchannel load-balance`\n\n**Best practice:** Use **src-dst-ip** or **src-dst-mixed-ip-port** for the most even distribution. Pure src-mac is poor when traffic from many hosts traverses a single router MAC.",
      tags: ["etherchannel", "load-balancing", "hashing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-328",
      domain: "3.0",
      subdomain: "EtherChannel",
      front: "What is the difference between a **Layer 2** and **Layer 3** EtherChannel?",
      back: "**Layer 2 EtherChannel:**\n- Port-channel interface acts as a **switchport**\n- Carries VLANs (access or trunk)\n- Used between switches\n- Config: `interface port-channel 1` → `switchport mode trunk`\n\n**Layer 3 EtherChannel:**\n- Port-channel interface is a **routed interface** with an IP address\n- No switchport, no VLANs on the channel itself\n- Used for routed links between L3 switches or routers\n- Config: member interfaces need `no switchport` → `channel-group 1 mode active`; port-channel gets `no switchport` + `ip address`\n\n**Key:** All member ports must match speed, duplex, and switchport/no-switchport configuration.",
      tags: ["etherchannel", "layer2", "layer3"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- VLANs / VTP / Private VLANs (4 cards) ---
    {
      id: "starter-329",
      domain: "3.0",
      subdomain: "VLANs",
      front: "What are the VTP modes in VTPv1/v2 vs VTPv3, and how does VTPv3 improve security?",
      back: "**VTPv1/v2 Modes:**\n- **Server** — Create/modify/delete VLANs; send and forward VTP ads; sync from higher revision\n- **Client** — Cannot modify VLANs; syncs from higher revision; forwards VTP ads\n- **Transparent** — Does not participate in VTP; forwards VTP ads (v2); local VLAN changes only\n\n**VTPv3 additions:**\n- **Off** mode — Does not forward VTP ads at all\n- **Primary server** — Only ONE switch can be the active primary (use `vtp primary`); prevents accidental overwrites\n- Supports **extended VLANs** (1006-4094), **private VLANs**, and **MST** propagation\n- **Domain password** is not sent in clear text\n- Revision number resets on domain change and when becoming primary",
      tags: ["vtp", "vlans", "vtpv3"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-330",
      domain: "3.0",
      subdomain: "VLANs",
      front: "What are the **native VLAN** security risks on trunk ports, and how do you mitigate them?",
      back: "**Risks:**\n- **VLAN hopping via double-tagging** — An attacker sends frames with two 802.1Q tags; the switch strips the outer tag (native VLAN) and forwards the inner-tagged frame to the target VLAN\n- Only works from the native VLAN and only in one direction\n\n**Mitigations:**\n1. `switchport trunk native vlan 999` — Use an **unused VLAN** as native (not VLAN 1)\n2. `vlan dot1q tag native` — Force 802.1Q tagging on the native VLAN (global command)\n3. `switchport mode trunk` + `switchport nonegotiate` — Disable DTP to prevent trunk negotiation attacks\n4. Prune the native VLAN from trunks if not needed\n5. Place no user ports in the native VLAN",
      tags: ["vlans", "native-vlan", "security", "vlan-hopping"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-331",
      domain: "3.0",
      subdomain: "VLANs",
      front: "What are **Private VLANs (PVLANs)**, and what are the port types?",
      back: "**Private VLANs** subdivide a primary VLAN into isolated segments while sharing a common gateway.\n\n**VLAN Types:**\n- **Primary VLAN** — The parent VLAN associated with the promiscuous port\n- **Isolated VLAN** — Ports cannot communicate with each other, only with promiscuous ports\n- **Community VLAN** — Ports can communicate within the same community and with promiscuous ports, but not with other communities or isolated ports\n\n**Port Types:**\n- **Promiscuous** — Communicates with ALL ports (typically the default gateway/router)\n- **Isolated** — Communicates ONLY with promiscuous ports\n- **Community** — Communicates with same-community ports + promiscuous ports\n\nUseful in DMZs and multi-tenant environments.",
      tags: ["pvlan", "private-vlan", "isolated", "community"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-332",
      domain: "3.0",
      subdomain: "VLANs",
      front: "What is **VTP pruning** and how does it improve network efficiency?",
      back: "**VTP pruning** prevents unnecessary flooding of broadcast, unknown unicast, and multicast traffic across trunk links for VLANs that have no active ports on the remote switch.\n\n**How it works:**\n- Switches advertise which VLANs have active ports via **VTP Join messages**\n- The sending switch then prunes VLANs from the trunk that have no listeners on the other side\n- Only works in **VTP Server/Client** mode (not transparent)\n- VLANs 2-1001 are eligible; VLAN 1 is never pruned\n\nEnable: `vtp pruning` (on the VTP server; propagates to clients)\n\nAlternative: Manual pruning with `switchport trunk allowed vlan` is preferred in modern networks for explicit control.",
      tags: ["vtp", "pruning", "trunk"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- FHRP (3 cards) ---
    {
      id: "starter-333",
      domain: "3.0",
      subdomain: "FHRP",
      front: "Compare **HSRPv1 vs HSRPv2 vs VRRP vs GLBP** — protocol type, multicast address, and key features.",
      back: "| Feature | HSRPv1 | HSRPv2 | VRRP (v2/v3) | GLBP |\n|---|---|---|---|---|\n| **Standard** | Cisco proprietary | Cisco proprietary | IETF (RFC 5798) | Cisco proprietary |\n| **Multicast** | 224.0.0.2 | 224.0.0.102 | 224.0.0.18 | 224.0.0.102 |\n| **Groups** | 0-255 | 0-4095 | 0-255 | 0-1023 |\n| **Virtual MAC** | 0000.0c07.acXX | 0000.0c9f.fXXX | 0000.5e00.01XX | 0007.b400.XXYY |\n| **Load Balance** | No (active/standby) | No | No | **Yes** (multiple AVFs) |\n| **Preempt default** | Disabled | Disabled | **Enabled** | Disabled |\n| **IPv6** | No | Yes | Yes (v3) | Yes |",
      tags: ["fhrp", "hsrp", "vrrp", "glbp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-334",
      domain: "3.0",
      subdomain: "FHRP",
      front: "How does HSRP **object tracking** work to trigger failover?",
      back: "**Object tracking** links HSRP priority to the state of a tracked object (interface, IP SLA, route).\n\nConfig example:\n```\ntrack 1 interface GigabitEthernet0/1 line-protocol\ninterface Vlan10\n  standby 10 priority 110\n  standby 10 preempt\n  standby 10 track 1 decrement 20\n```\n\n**How it works:**\n1. If the tracked interface goes down, HSRP **decrements** the priority by the configured value (20)\n2. Priority drops from 110 to 90, below the standby router's default 100\n3. With **preempt** enabled, the standby router takes over as active\n4. When the tracked object recovers, priority is restored and the original active preempts back\n\nYou can track: line-protocol, IP routing, IP SLA results.",
      tags: ["fhrp", "hsrp", "tracking", "preemption"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-335",
      domain: "3.0",
      subdomain: "FHRP",
      front: "How does **GLBP** achieve load balancing across multiple routers, unlike HSRP/VRRP?",
      back: "**GLBP (Gateway Load Balancing Protocol)** uses one **Active Virtual Gateway (AVG)** and up to four **Active Virtual Forwarders (AVFs)**.\n\n**How it works:**\n1. All routers share a single **virtual IP** address\n2. The AVG responds to ARP requests with **different virtual MAC addresses**, directing different clients to different AVFs\n3. Each AVF has its own virtual MAC and forwards traffic independently\n\n**Load-balancing methods:**\n- **Round-robin** (default) — Each ARP reply gives the next AVF's MAC\n- **Weighted** — Traffic distributed proportionally based on configured weights\n- **Host-dependent** — Each host always gets the same AVF MAC (session persistence)\n\nIf an AVF fails, another router takes over its virtual MAC.",
      tags: ["fhrp", "glbp", "load-balancing"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Multicast (4 cards) ---
    {
      id: "starter-336",
      domain: "3.0",
      subdomain: "Multicast",
      front: "What is the **RPF (Reverse Path Forwarding) check** in multicast, and why is it necessary?",
      back: "The **RPF check** is the fundamental loop-prevention mechanism in multicast routing.\n\n**How it works:**\n1. When a multicast packet arrives, the router checks: does the **incoming interface** match the interface in the **unicast routing table** that would be used to reach the **source address**?\n2. If YES → RPF check passes → packet is forwarded out the outgoing interface list (OIL)\n3. If NO → RPF check fails → packet is **dropped**\n\n**Why necessary:** Multicast traffic can arrive on multiple interfaces (redundant paths). Without RPF, the same packet could loop endlessly. RPF ensures packets are only accepted from the topologically correct direction toward the source.\n\nVerify: `show ip rpf <source-ip>`",
      tags: ["multicast", "rpf", "loop-prevention"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-337",
      domain: "3.0",
      subdomain: "Multicast",
      front: "Compare **PIM Sparse Mode** and **PIM Dense Mode** — when is each used?",
      back: "**PIM Dense Mode (PIM-DM):**\n- **Flood and prune** model — assumes all routers want the traffic\n- Source floods multicast to all neighbors; routers with no receivers send **Prune** messages\n- Prune state times out (3 min default) and flooding restarts\n- Suitable for small networks with many receivers (rarely used today)\n\n**PIM Sparse Mode (PIM-SM):**\n- **Explicit join** model — traffic only sent where requested\n- Receivers join via **(*,G)** joins toward the **Rendezvous Point (RP)**\n- Source registers with the RP; RP forwards to receivers\n- SPT switchover: receivers can switch from shared tree (*,G) to source tree (S,G) for optimal paths\n- **Standard for enterprise and service provider networks**",
      tags: ["multicast", "pim", "sparse-mode", "dense-mode"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-338",
      domain: "3.0",
      subdomain: "Multicast",
      front: "What are the three methods for RP (Rendezvous Point) discovery in PIM-SM, and which is preferred?",
      back: "**1. Static RP:**\n- Manually configured on every router: `ip pim rp-address <IP> [ACL]`\n- Simple but doesn't scale; no redundancy without Anycast RP\n\n**2. Auto-RP (Cisco proprietary):**\n- Candidate RPs announce via **224.0.1.39**; Mapping Agent selects and distributes via **224.0.1.40**\n- Uses dense-mode flooding for RP discovery (chicken-and-egg problem — solved with `ip pim autorp listener` or sparse-dense mode)\n\n**3. BSR (Bootstrap Router — RFC 5059):**\n- Standards-based; BSR floods RP information hop-by-hop in **BSR messages**\n- Candidate RPs register with the BSR; all routers learn RP mapping\n- **Preferred** for multi-vendor environments\n\nBSR is the recommended approach for modern networks.",
      tags: ["multicast", "rp", "auto-rp", "bsr"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-339",
      domain: "3.0",
      subdomain: "Multicast",
      front: "What are the key differences between **IGMP v2** and **IGMP v3**?",
      back: "**IGMPv2 (RFC 2236):**\n- Hosts join groups with **Membership Report** (join) and **Leave Group** messages\n- Supports **any-source multicast (ASM)** only — joins group (*,G) regardless of source\n- Uses **General Query** and **Group-Specific Query**\n- One **Querier** elected per subnet (lowest IP)\n\n**IGMPv3 (RFC 3376):**\n- Adds **Source-Specific Multicast (SSM)** support\n- Hosts can specify **INCLUDE** or **EXCLUDE** source lists: \"I want group G from source S only\"\n- Enables (S,G) joins directly, bypassing the RP for SSM groups (232.0.0.0/8)\n- Uses **Group-and-Source-Specific Queries**\n- Required for SSM deployment",
      tags: ["multicast", "igmp", "ssm"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- IP Services (4 cards) ---
    {
      id: "starter-340",
      domain: "3.0",
      subdomain: "IPServices",
      front: "Explain NTP **stratum levels** and **authentication** — how do they ensure accurate, trusted time?",
      back: "**NTP Stratum:**\n- **Stratum 0** — Authoritative time sources (atomic clocks, GPS) — not directly on the network\n- **Stratum 1** — Servers directly connected to Stratum 0 (primary time servers)\n- **Stratum 2-15** — Each level synchronized to the one above (stratum = hops from Stratum 0)\n- **Stratum 16** — Unsynchronized (invalid)\n- Lower stratum = more accurate; clients prefer lower stratum servers\n\n**NTP Authentication:**\n```\nntp authenticate\nntp authentication-key 1 md5 <key>\nntp trusted-key 1\nntp server <IP> key 1\n```\nPrevents rogue NTP servers from injecting incorrect time. Both peers must share the same key. Critical for log correlation, certificate validation, and DNSSEC.",
      tags: ["ntp", "stratum", "authentication"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-341",
      domain: "3.0",
      subdomain: "IPServices",
      front: "How does **DHCP relay** (ip helper-address) work, and what UDP ports does it forward?",
      back: "**DHCP Relay** forwards DHCP broadcast messages from clients to a remote DHCP server as unicast.\n\n**Process:**\n1. Client sends DHCPDISCOVER as broadcast on the local subnet\n2. Router with `ip helper-address <server-IP>` on the interface intercepts the broadcast\n3. Router relays it as **unicast** to the DHCP server, inserting the **GIADDR** (Gateway IP Address) = the router's interface IP\n4. Server uses GIADDR to determine the correct scope/pool\n5. Server replies to the relay agent, which forwards to the client\n\n**Default forwarded UDP ports:** 67/68 (DHCP), 69 (TFTP), 53 (DNS), 37 (Time), 137/138 (NetBIOS), 49 (TACACS), 161/162 (SNMP — removed in newer IOS). Configure per-interface: `ip helper-address <IP>`.",
      tags: ["dhcp", "relay", "ip-helper"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-342",
      domain: "3.0",
      subdomain: "IPServices",
      front: "What are the differences between **Static NAT**, **Dynamic NAT**, and **PAT (NAT Overload)**?",
      back: "**Static NAT:** One-to-one permanent mapping between an inside local and inside global address. Used for servers that need consistent external addressing.\n`ip nat inside source static 192.168.1.10 203.0.113.10`\n\n**Dynamic NAT:** Many-to-many mapping from a pool of global addresses. First-come-first-served; when the pool is exhausted, new translations fail.\n`ip nat pool POOL 203.0.113.1 203.0.113.254 netmask 255.255.255.0`\n`ip nat inside source list 1 pool POOL`\n\n**PAT (Overload):** Many-to-one mapping using **source port numbers** to distinguish sessions. Most common for internet access.\n`ip nat inside source list 1 interface GigabitEthernet0/1 overload`\n\nAll require interfaces marked `ip nat inside` and `ip nat outside`.",
      tags: ["nat", "pat", "overload"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-343",
      domain: "3.0",
      subdomain: "IPServices",
      front: "What is **dual-stack** IPv6 deployment, and what are the key considerations for routing protocols?",
      back: "**Dual-stack** runs IPv4 and IPv6 simultaneously on the same interfaces and devices. Each protocol stack operates independently with its own routing table.\n\n**Key considerations:**\n- Each interface has both IPv4 and IPv6 addresses\n- Routing protocols run separately: OSPFv2 (IPv4) + OSPFv3 (IPv6), or EIGRP with address-families\n- DNS returns both A (IPv4) and AAAA (IPv6) records; clients prefer IPv6 by default\n- ACLs/firewalls must cover **both** stacks — a common security oversight\n- NAT64/DNS64 may be needed for IPv6-only hosts to reach IPv4-only servers\n- **IPv6 RA Guard** should be enabled to prevent rogue router advertisements\n\nDual-stack is the recommended transition mechanism for enterprise networks.",
      tags: ["ipv6", "dual-stack", "transition"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- IPv6 (3 cards) ---
    {
      id: "starter-344",
      domain: "3.0",
      subdomain: "IPv6",
      front: "What is **IPv6 RA Guard**, and why is it important in dual-stack environments?",
      back: "**RA Guard** prevents rogue **Router Advertisement** messages from unauthorized devices on a switch port.\n\n**The threat:** Any IPv6 host can send RAs, potentially becoming the default gateway and intercepting traffic (man-in-the-middle) or causing denial of service.\n\n**How it works:**\n- Applied as a **switch port policy** that inspects incoming ICMPv6 RA messages\n- Only ports connected to **legitimate routers** are allowed to send RAs\n- Other ports have RAs **dropped** at the switch\n\n**Config:**\n```\nipv6 nd raguard policy HOSTS\n  device-role host\ninterface range GigabitEthernet1/0/1-48\n  ipv6 nd raguard attach-policy HOSTS\n```\nRouter-facing ports use `device-role router`.",
      tags: ["ipv6", "ra-guard", "security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-345",
      domain: "3.0",
      subdomain: "IPv6",
      front: "How does **EIGRPv6** (EIGRP for IPv6) differ from EIGRP for IPv4?",
      back: "**EIGRPv6** key differences:\n- Configured per-interface: `ipv6 eigrp <AS>` (classic mode) or under `address-family ipv6` in named mode\n- Uses **link-local** addresses as next-hop (not global unicast)\n- Requires a **Router ID** (32-bit, IPv4 format) even without IPv4 config — must be manually set if no IPv4 interface exists\n- Must be explicitly enabled: `no shutdown` under the EIGRP IPv6 address-family or router process\n- Same DUAL algorithm, same metric calculation\n- **Named mode** is preferred — manages both IPv4 and IPv6 under one configuration block\n\nIn named mode:\n```\nrouter eigrp MYNET\n  address-family ipv6 autonomous-system 100\n    no shutdown\n```",
      tags: ["eigrp", "ipv6", "eigrpv6"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-346",
      domain: "3.0",
      subdomain: "IPv6",
      front: "How does **OSPFv3** support both IPv4 and IPv6 using address families?",
      back: "OSPFv3 (RFC 5838) can carry **both** IPv4 and IPv6 prefixes using **address families (AFs)**.\n\n**Config:**\n```\nrouter ospfv3 1\n  address-family ipv4 unicast\n    router-id 1.1.1.1\n  address-family ipv6 unicast\n    router-id 1.1.1.1\ninterface GigabitEthernet0/0\n  ospfv3 1 ipv4 area 0\n  ospfv3 1 ipv6 area 0\n```\n\n**Key points:**\n- Each AF maintains its own LSDB and SPF calculation\n- Different **instance IDs** separate AF traffic on the same link (0-31 for IPv6, 64-95 for IPv4)\n- Still uses **link-local** addresses for neighbor adjacencies\n- Replaces the need to run OSPFv2 (IPv4) + OSPFv3 (IPv6) separately\n- Authentication via **IPsec**",
      tags: ["ospf", "ospfv3", "ipv6", "address-family"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 4.0 — NETWORK ASSURANCE (~15 cards)
    // =========================================================

    // --- ERSPAN (2 cards) ---
    {
      id: "starter-347",
      domain: "4.0",
      subdomain: "ERSPAN",
      front: "What is **ERSPAN**, and how does it differ from SPAN and RSPAN?",
      back: "**SPAN (Switched Port Analyzer):** Mirrors traffic from source port(s)/VLAN(s) to a local destination port on the **same switch**.\n\n**RSPAN (Remote SPAN):** Mirrors traffic to a destination port on a **different switch** via a dedicated RSPAN VLAN carried across trunks. Limited to the L2 domain.\n\n**ERSPAN (Encapsulated RSPAN):** Encapsulates mirrored traffic in **GRE** and sends it across a **routed (L3) network** to a remote destination. No RSPAN VLAN needed.\n\n**ERSPAN details:**\n- Uses GRE encapsulation (outer **IP protocol 47**; GRE Protocol Type field 0x88BE for ERSPAN Type II, 0x22EB for Type III)\n- Source and destination can be in different subnets/sites\n- Supports ERSPAN Type II (original) and Type III (with timestamps and hardware sequence numbers)\n- Higher overhead due to GRE encapsulation",
      tags: ["erspan", "span", "rspan", "monitoring"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-348",
      domain: "4.0",
      subdomain: "ERSPAN",
      front: "How do you configure an **ERSPAN** source session on IOS-XE?",
      back: "**ERSPAN Source Session config:**\n```\nmonitor session 1 type erspan-source\n  source interface GigabitEthernet1/0/1 both\n  destination\n    erspan-id 100\n    ip address 10.1.1.100      ! Remote collector IP\n    origin ip address 10.2.2.1  ! Source router IP (GRE source)\n  no shutdown\n```\n\n**Key parameters:**\n- **erspan-id** — Identifies the ERSPAN session (must match at destination)\n- **ip address** — Destination IP where the GRE-encapsulated mirror goes\n- **origin ip address** — Source IP for the GRE tunnel\n- **source** — Interfaces or VLANs to monitor (rx, tx, or both)\n\nThe destination device must have a corresponding `erspan-destination` session or a packet capture tool that can de-encapsulate GRE.",
      tags: ["erspan", "configuration", "ios-xe"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Conditional Debug (2 cards) ---
    {
      id: "starter-349",
      domain: "4.0",
      subdomain: "ConditionalDebug",
      front: "What is **conditional debugging** in IOS-XE, and why is it important in production?",
      back: "**Conditional debugging** restricts debug output to specific traffic matching defined conditions, preventing debug commands from overwhelming the CPU and console.\n\n**Common conditions:**\n- `debug condition interface GigabitEthernet0/1` — Only debug traffic on this interface\n- `debug condition ip <address>` — Only debug traffic involving this IP\n- `debug condition vlan <id>` — Only debug traffic on this VLAN\n- `debug condition username <name>` — Only debug this user's sessions\n\n**Usage:**\n1. Set the condition first: `debug condition ip 10.1.1.1`\n2. Enable the debug: `debug ip ospf adj`\n3. Only OSPF adjacency debugs involving 10.1.1.1 appear\n4. Remove: `undebug all` and `no debug condition all`\n\nAlways set conditions **before** enabling debugs in production.",
      tags: ["debug", "conditional-debug", "troubleshooting"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-350",
      domain: "4.0",
      subdomain: "ConditionalDebug",
      front: "How do you verify active debug conditions and safely remove them?",
      back: "**Verify active conditions:**\n- `show debug condition` — Lists all currently active debug conditions\n- `show debug` — Shows which debugs are enabled and whether conditions are applied\n\n**Remove conditions:**\n- `no debug condition all` — Removes ALL conditions (debugs revert to unfiltered if still enabled!)\n- `no debug condition <number>` — Remove a specific condition by its ID\n- `undebug all` — Disables all debug output\n\n**Best practice sequence for cleanup:**\n1. `undebug all` — Stop all debug output first\n2. `no debug condition all` — Then remove conditions\n\n**Warning:** If you remove conditions while debugs are still active, debugs become **unfiltered** and can overwhelm the router. Always disable debugs before removing conditions.",
      tags: ["debug", "conditional-debug", "best-practice"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- DNA Center Assurance (3 cards) ---
    {
      id: "starter-351",
      domain: "4.0",
      subdomain: "DNACAssurance",
      front: "What is **Cisco DNA Center Assurance**, and what are its three main dashboards?",
      back: "**DNA Center Assurance** provides AI/ML-driven network analytics and troubleshooting.\n\n**Three main dashboards:**\n\n1. **Network Health** — Overall health scores for network devices (switches, routers). Shows device reachability, CPU/memory utilization, link errors, and fabric health.\n\n2. **Client Health** — Health scores for wired and wireless clients. Tracks onboarding success (DHCP, AAA, association), connectivity issues, and client counts.\n\n3. **Application Health** — Monitors application performance using NBAR2-based application recognition. Shows throughput, latency, and application experience scores.\n\nAdditional capabilities:\n- **Path Trace** — Visualizes the L2/L3 path between two endpoints\n- **Intelligent Capture** — Packet-level troubleshooting\n- **AI-Driven Insights** — ML-based anomaly detection and recommendations",
      tags: ["dna-center", "assurance", "network-health"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-352",
      domain: "4.0",
      subdomain: "DNACAssurance",
      front: "How does DNA Center **Path Trace** work, and what information does it provide?",
      back: "**Path Trace** visualizes the complete network path between a source and destination.\n\n**How it works:**\n1. Enter source and destination (IP, MAC, or hostname)\n2. DNA Center queries network devices for forwarding table information\n3. It reconstructs the complete L2/L3 path hop-by-hop\n\n**Information provided:**\n- Every device and interface in the path\n- VLAN assignments at each hop\n- ACLs applied along the path (and whether they permit or deny)\n- QoS policies applied\n- Interface statistics and errors\n- SGT (if SD-Access) and VRF information\n\n**Use cases:**\n- Troubleshooting connectivity issues\n- Verifying ACL/QoS policy application\n- Validating network segmentation\n- Confirming traffic takes the expected path",
      tags: ["dna-center", "path-trace", "troubleshooting"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- SNMP (2 cards) ---
    {
      id: "starter-355",
      domain: "4.0",
      subdomain: "SNMP",
      front: "Compare **SNMPv2c** and **SNMPv3** — what security improvements does v3 provide?",
      back: "**SNMPv2c:**\n- Uses **community strings** for authentication (plain text)\n- No encryption — data visible in transit\n- Community string = password; RO (read-only) and RW (read-write)\n- Widely deployed but inherently insecure\n\n**SNMPv3 Security Models:**\n- **noAuthNoPriv** — Username only, no authentication hash, no encryption\n- **authNoPriv** — Username + **authentication** (MD5 or SHA) but no encryption\n- **authPriv** — Authentication + **encryption** (DES, 3DES, AES-128/192/256)\n\n**SNMPv3 additional features:**\n- **User-based Security Model (USM)** — per-user credentials\n- **View-based Access Control (VACM)** — restricts which MIB objects each user can access\n- **Engine ID** — uniquely identifies each SNMP entity\n\n**Best practice:** Use SNMPv3 authPriv with AES encryption.",
      tags: ["snmp", "snmpv3", "security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-356",
      domain: "4.0",
      subdomain: "SNMP",
      front: "What is the difference between SNMP **traps** and **informs**?",
      back: "**Traps:**\n- Asynchronous notifications from agent to manager\n- **Unreliable** — sent over UDP with **no acknowledgment**\n- If the trap is lost (network issue, manager down), the agent never knows\n- Lower overhead on the agent\n\n**Informs:**\n- Also asynchronous notifications, but with **acknowledgment**\n- The agent **retransmits** if no acknowledgment received (reliable delivery)\n- Available in SNMPv2c and SNMPv3\n- Higher overhead (agent must track pending informs and retransmit)\n\n**Config:**\n- Trap: `snmp-server host 10.1.1.100 traps version 2c COMMUNITY`\n- Inform: `snmp-server host 10.1.1.100 informs version 2c COMMUNITY`\n\n**Best practice:** Use **informs** for critical events to ensure delivery; traps for high-volume, less critical events.",
      tags: ["snmp", "traps", "informs"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- IP SLA (4 cards) ---
    {
      id: "starter-357",
      domain: "4.0",
      subdomain: "IPSLA",
      front: "What is **IP SLA**, and what are the most common operation types for ENCOR?",
      back: "**IP SLA (Service Level Agreement)** is an active monitoring feature that generates synthetic traffic to measure network performance.\n\n**Common operation types:**\n- **ICMP Echo** — Measures round-trip time and reachability (like ping)\n- **UDP Jitter** — Measures jitter, latency, and packet loss (VoIP quality)\n- **HTTP** — Measures web server response time and availability\n- **TCP Connect** — Measures TCP handshake time to a port\n- **DNS** — Measures DNS resolution time\n- **DHCP** — Measures DHCP response time\n\n**Key use cases:**\n- Trigger failover with **object tracking** (HSRP, static routes, PBR)\n- Baseline and monitor WAN link performance\n- Verify SLA compliance with service providers\n- Pre-emptive detection of degraded paths",
      tags: ["ip-sla", "monitoring", "performance"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-358",
      domain: "4.0",
      subdomain: "IPSLA",
      front: "How do you configure an **IP SLA ICMP echo** operation with tracking for static route failover?",
      back: "**IP SLA ICMP Echo + Tracking config:**\n```\n! Define the SLA operation\nip sla 1\n  icmp-echo 8.8.8.8 source-interface GigabitEthernet0/0\n  frequency 10\n  threshold 1000\nip sla schedule 1 start-time now life forever\n\n! Create a tracking object\ntrack 1 ip sla 1 reachability\n\n! Floating static route with tracking\nip route 0.0.0.0 0.0.0.0 10.0.0.1 track 1\nip route 0.0.0.0 0.0.0.0 10.0.1.1 10   ! Backup with higher AD\n```\n\n**How it works:** IP SLA pings 8.8.8.8 every 10 seconds. If it fails, track 1 goes DOWN, the primary default route is removed, and the backup route (AD 10) takes over.",
      tags: ["ip-sla", "tracking", "failover"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-359",
      domain: "4.0",
      subdomain: "IPSLA",
      front: "What does the IP SLA **UDP jitter** operation measure, and why is it important for VoIP?",
      back: "**UDP Jitter** sends a series of UDP packets and measures:\n- **Round-trip time (RTT)** — total delay\n- **One-way latency** — source-to-dest and dest-to-source (requires NTP sync)\n- **Jitter** — variation in packet arrival times (both directions)\n- **Packet loss** — source-to-dest and dest-to-source\n- **MOS (Mean Opinion Score)** — calculated voice quality score (1.0-5.0)\n\n**Why critical for VoIP:**\n- Cisco recommends: latency <150ms, jitter <30ms, packet loss <1%\n- UDP jitter operations simulate VoIP-like traffic patterns\n- Results can trigger failover to backup WAN links before users notice degradation\n\n**Config:**\n```\nip sla 2\n  udp-jitter 10.1.1.100 16384 codec g729a\n  frequency 30\n```\nRequires an **IP SLA responder** on the target: `ip sla responder`",
      tags: ["ip-sla", "udp-jitter", "voip", "monitoring"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-360",
      domain: "4.0",
      subdomain: "IPSLA",
      front: "How does IP SLA **HTTP** operation work, and what does it measure?",
      back: "**IP SLA HTTP** sends real HTTP GET or RAW requests to a web server and measures:\n- **DNS lookup time** — Time to resolve the hostname\n- **TCP connect time** — Time to establish the TCP session\n- **HTTP transaction time** — Time to receive the HTTP response\n- **Total RTT** — Sum of all phases\n- **HTTP return code** — Success (200) or error codes\n\n**Config:**\n```\nip sla 3\n  http get http://www.example.com\n  frequency 60\nip sla schedule 3 start-time now life forever\n```\n\n**Use cases:**\n- Monitor web application availability and response time\n- Track CDN or cloud service performance\n- Trigger alerts when HTTP response time exceeds thresholds\n- Combine with tracking objects for automated failover",
      tags: ["ip-sla", "http", "web-monitoring"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // EXPANSION WAVE — DOMAIN 5.0 + 6.0 (Security + Automation)
    // =========================================================
    // =========================================================
    // DOMAIN 5.0 — SECURITY (~43 cards)
    // =========================================================

    // --- ACLs ---
    {
      id: "starter-361",
      domain: "5.0",
      subdomain: "ACLs",
      front: "What is the key difference between a **standard** ACL and an **extended** ACL in IOS-XE?",
      back: "**Standard ACLs** (1-99, 1300-1999) filter based on **source IP only**. **Extended ACLs** (100-199, 2000-2699) filter on **source IP, destination IP, protocol, and port numbers**. Standard ACLs should be placed close to the destination; extended ACLs close to the source.",
      tags: ["acl", "standard", "extended"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-362",
      domain: "5.0",
      subdomain: "ACLs",
      front: "What happens to traffic that does not match any explicit ACE in an IOS-XE ACL?",
      back: "It is **denied** by the **implicit deny all** (`deny any` / `deny ip any any`) at the end of every ACL. This is invisible in the running config but always present. You must add an explicit `permit` statement for any traffic you want to allow.",
      tags: ["acl", "implicit-deny"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-363",
      domain: "5.0",
      subdomain: "ACLs",
      front: "What is a **named ACL** and what advantage does it offer over numbered ACLs?",
      back: "A named ACL uses a descriptive string instead of a number (e.g., `ip access-list extended BLOCK_TELNET`). Advantages: **individual ACEs can be deleted or inserted by sequence number**, more descriptive, and both standard and extended types are supported. Named ACLs also support editing without removing the entire ACL.",
      tags: ["acl", "named-acl"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-364",
      domain: "5.0",
      subdomain: "ACLs",
      front: "How do **time-based ACLs** work in IOS-XE?",
      back: "Time-based ACLs use a **time-range** object (`time-range NAME`) with `periodic` or `absolute` statements. The ACE references the time-range: `permit tcp any any eq 80 time-range BUSINESS_HOURS`. The ACE is only active when the time-range condition is true. Requires accurate NTP synchronization.",
      tags: ["acl", "time-based"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-365",
      domain: "5.0",
      subdomain: "ACLs",
      front: "What are **reflexive ACLs** and what problem do they solve?",
      back: "Reflexive ACLs create **temporary, session-based permit entries** for return traffic. When an outbound session starts, a dynamic ACE is created to allow the corresponding inbound reply. They use `reflect` on the outbound ACL and `evaluate` on the inbound ACL. This provides basic **stateful filtering** without a full firewall.",
      tags: ["acl", "reflexive"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- CoPP ---
    {
      id: "starter-366",
      domain: "5.0",
      subdomain: "CoPP",
      front: "What is **Control Plane Policing (CoPP)** and why is it important?",
      back: "CoPP protects the **route processor (CPU)** from excessive traffic by applying QoS policies to the control plane. It uses **MQC (Modular QoS CLI)** — class-maps match traffic types (BGP, OSPF, SSH, ICMP), policy-maps set rate limits (`police`), and the service-policy is applied to the `control-plane` interface. Without CoPP, DoS attacks can overwhelm the CPU and crash the router.",
      tags: ["copp", "control-plane", "mqc"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-367",
      domain: "5.0",
      subdomain: "CoPP",
      front: "In CoPP, what are the three MQC components and how are they applied?",
      back: "1. **class-map** — matches traffic using ACLs (e.g., match BGP on TCP 179).\n2. **policy-map** — defines actions per class (typically `police rate` with conform/exceed actions).\n3. **service-policy input** — applied under `control-plane` configuration mode.\n\nExample: `control-plane` → `service-policy input CoPP-Policy`.\n\nConforming traffic is **transmitted**; exceeding traffic is **dropped**.",
      tags: ["copp", "mqc", "class-map", "policy-map"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- AAA ---
    {
      id: "starter-368",
      domain: "5.0",
      subdomain: "AAA",
      front: "Compare **RADIUS** vs **TACACS+** — transport protocol and encryption differences.",
      back: "**RADIUS**: Uses **UDP** (ports 1812/1813 or 1645/1646). Encrypts **only the password** in the Access-Request. Combines authentication and authorization.\n\n**TACACS+**: Uses **TCP** (port 49). Encrypts the **entire packet body**. Separates authentication, authorization, and accounting into independent functions.\n\nTACACS+ is preferred for **device administration** (granular command authorization). RADIUS is common for **network access** (802.1X).",
      tags: ["aaa", "radius", "tacacs"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-369",
      domain: "5.0",
      subdomain: "AAA",
      front: "What is an AAA **method list** and how does fallback work?",
      back: "A method list is a **named or default sequence** of authentication methods tried in order. Example: `aaa authentication login default group tacacs+ local` tries TACACS+ first, then falls back to **local** database.\n\nFallback occurs only if the server is **unreachable** (timeout/error), NOT if the server rejects the credentials. If TACACS+ explicitly denies, the local method is **not** tried.",
      tags: ["aaa", "method-list", "fallback"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-370",
      domain: "5.0",
      subdomain: "AAA",
      front: "What are the three AAA authorization types relevant to ENCOR?",
      back: "1. **Exec authorization** — controls access to EXEC mode (privilege levels).\n2. **Command authorization** — controls which CLI commands a user can execute (TACACS+ only, per-command check).\n3. **Network authorization** — controls network service access (e.g., PPP, VPDN, 802.1X VLAN assignment).\n\nTACACS+ supports all three independently. RADIUS bundles auth/authz together in the Access-Accept response.",
      tags: ["aaa", "authorization"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- 802.1X ---
    {
      id: "starter-371",
      domain: "5.0",
      subdomain: "802.1X",
      front: "Name the three main **EAP types** tested on ENCOR and their key differences.",
      back: "1. **PEAP** (Protected EAP) — Server certificate only; client uses MSCHAPv2 inside a TLS tunnel. Most common for username/password.\n2. **EAP-TLS** — Mutual certificate authentication (both server AND client have certs). Most secure but requires PKI.\n3. **EAP-FAST** — Cisco proprietary; uses a **PAC (Protected Access Credential)** instead of certificates. Designed as a replacement for LEAP.",
      tags: ["802.1x", "eap", "peap", "eap-tls", "eap-fast"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-372",
      domain: "5.0",
      subdomain: "802.1X",
      front: "What are the three 802.1X **host modes** on a switch port?",
      back: "1. **Single-host** — Only one MAC address allowed; port shuts if a second device is detected.\n2. **Multi-host** — First MAC authenticates, then all other MACs on the port are allowed (open).\n3. **Multi-domain** — One device per **data** domain and one per **voice** domain (IP phone + PC).\n\nThere is also **Multi-auth** — allows **multiple authenticated devices** on the data VLAN independently (each device must authenticate).",
      tags: ["802.1x", "host-mode", "multi-auth"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-373",
      domain: "5.0",
      subdomain: "802.1X",
      front: "What are the three **802.1X port states** on the authenticator (switch)?",
      back: "1. **Force-authorized** — Port is always open (no authentication required). Default on most switch ports.\n2. **Force-unauthorized** — Port is always blocked regardless of authentication.\n3. **Auto** — Port uses 802.1X for authentication. Starts unauthorized and transitions to authorized upon successful EAP exchange.\n\nConfigured with: `authentication port-control auto`.",
      tags: ["802.1x", "port-control"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- MAB ---
    {
      id: "starter-374",
      domain: "5.0",
      subdomain: "MAB",
      front: "What is **MAC Authentication Bypass (MAB)** and when is it used?",
      back: "MAB authenticates devices using their **MAC address** as credentials (both username and password). Used for devices that **cannot run an 802.1X supplicant** — printers, cameras, IoT sensors, etc.\n\nThe switch sends the MAC address to the RADIUS server, which checks it against a database. MAB is **less secure** than 802.1X because MAC addresses can be spoofed.",
      tags: ["mab", "802.1x", "network-access"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-375",
      domain: "5.0",
      subdomain: "MAB",
      front: "What is the typical **order of authentication methods** when 802.1X, MAB, and WebAuth are all configured on a port?",
      back: "1. **802.1X** is tried first (waits for EAP from supplicant).\n2. If no EAP response (timeout), falls back to **MAB** (sends MAC to RADIUS).\n3. If MAB fails, falls back to **WebAuth** (redirects HTTP to a login portal).\n\nConfigured with: `authentication order dot1x mab` and `authentication priority dot1x mab`. The `order` sets the sequence; `priority` allows a higher-priority method to preempt.",
      tags: ["mab", "802.1x", "webauth", "authentication-order"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- WebAuth ---
    {
      id: "starter-376",
      domain: "5.0",
      subdomain: "WebAuth",
      front: "What is the difference between **local WebAuth** and **centralized WebAuth** (CWA)?",
      back: "**Local WebAuth (LWA)** — The login portal is hosted on the **switch itself**. The switch intercepts HTTP and presents a login page. Simple but limited customization.\n\n**Centralized WebAuth (CWA)** — The login portal is hosted on **ISE (or another RADIUS server)**. The switch redirects the client to the ISE portal URL via a RADIUS `url-redirect` attribute. More flexible, supports guest workflows, and allows centralized policy management.",
      tags: ["webauth", "lwa", "cwa", "ise"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- TrustSec / SGT ---
    {
      id: "starter-377",
      domain: "5.0",
      subdomain: "TrustSec",
      front: "What is a **Scalable Group Tag (SGT)** and how is it assigned?",
      back: "An SGT is a **16-bit tag** (0-65535) assigned to a user/device/resource that represents its **security group**. Assignment methods:\n1. **Dynamic** — ISE assigns via RADIUS during 802.1X/MAB authentication.\n2. **Static** — Manually configured per IP/subnet on the switch (`cts role-based sgt-map`).\n3. **SXP** — Propagated via SGT Exchange Protocol to devices that don't support inline tagging.\n\nSGTs enable **topology-independent micro-segmentation**.",
      tags: ["trustsec", "sgt", "segmentation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-378",
      domain: "5.0",
      subdomain: "TrustSec",
      front: "What is the difference between **inline tagging** and **SXP** in TrustSec?",
      back: "**Inline tagging** — The SGT is embedded in the **Ethernet frame** (CMD/meta-data TLV in the 802.1AE header). Requires hardware support on both ends. Most efficient method.\n\n**SXP (SGT Exchange Protocol)** — A **TCP-based control protocol** (TCP 64999) that propagates **IP-to-SGT bindings** between devices. Used when a device in the path does not support inline tagging. SXP uses speaker/listener roles.",
      tags: ["trustsec", "sgt", "sxp", "inline-tagging"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-379",
      domain: "5.0",
      subdomain: "TrustSec",
      front: "What is an **SGACL** and how does it enforce policy?",
      back: "An **SGACL (Scalable Group ACL)** is a policy that controls traffic between **source SGT** and **destination SGT** pairs. Unlike traditional ACLs that use IP addresses, SGACLs are applied based on security group membership.\n\nEnforced at the **egress device** (closest to the destination). Policies are defined in ISE and downloaded to enforcement points. The SGACL matrix is a grid of source SGT (rows) × destination SGT (columns) with permit/deny rules.",
      tags: ["trustsec", "sgacl", "policy"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- DHCP Snooping ---
    {
      id: "starter-380",
      domain: "5.0",
      subdomain: "DHCPSnooping",
      front: "Explain **trusted** vs **untrusted** ports in DHCP snooping.",
      back: "**Trusted ports** — Connected to legitimate DHCP servers or uplinks to other switches. All DHCP messages are allowed. Configured with `ip dhcp snooping trust`.\n\n**Untrusted ports** — Connected to end hosts. Only DHCP **client messages** (DISCOVER, REQUEST) are allowed. DHCP **server messages** (OFFER, ACK) from untrusted ports are **dropped**. This prevents rogue DHCP servers.\n\nBy default, all ports are **untrusted** when snooping is enabled.",
      tags: ["dhcp-snooping", "trusted", "untrusted"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-381",
      domain: "5.0",
      subdomain: "DHCPSnooping",
      front: "What is the **DHCP snooping binding table** and what uses it?",
      back: "The binding table stores: **MAC address, IP address, lease time, VLAN, and interface** for each DHCP client on untrusted ports. Built dynamically from snooped DHCP exchanges.\n\nUsed by:\n- **Dynamic ARP Inspection (DAI)** — validates ARP packets against bindings\n- **IP Source Guard** — filters traffic not matching IP/MAC bindings\n\nView with: `show ip dhcp snooping binding`. Can persist across reboots with `ip dhcp snooping database`.",
      tags: ["dhcp-snooping", "binding-table", "dai"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- DAI ---
    {
      id: "starter-382",
      domain: "5.0",
      subdomain: "DAI",
      front: "How does **Dynamic ARP Inspection (DAI)** prevent ARP poisoning?",
      back: "DAI intercepts **all ARP packets on untrusted ports** and validates them against the **DHCP snooping binding table** (or static ARP ACLs). If the source MAC-to-IP mapping in the ARP packet doesn't match a valid binding, the packet is **dropped**.\n\nThis prevents **ARP spoofing/poisoning** attacks where an attacker sends gratuitous ARPs with a forged source IP to redirect traffic. Enabled per VLAN: `ip arp inspection vlan 10`.",
      tags: ["dai", "arp", "layer2-security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-383",
      domain: "5.0",
      subdomain: "DAI",
      front: "What are DAI **trust states** and **rate limiting**?",
      back: "**Trust states**: Trusted ports (uplinks, trunks) bypass ARP validation. Untrusted ports have all ARP packets inspected. Configured with `ip arp inspection trust`.\n\n**Rate limiting**: Limits ARP packets per second on untrusted ports to prevent DoS. Default is **15 pps**. If exceeded, the port enters **err-disabled** state. Configured with `ip arp inspection limit rate <pps>`. Trusted ports have **no rate limit** by default.",
      tags: ["dai", "rate-limit", "err-disabled"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Port Security ---
    {
      id: "starter-384",
      domain: "5.0",
      subdomain: "PortSecurity",
      front: "What are the three **port security violation modes** and how do they differ?",
      back: "1. **Shutdown** (default) — Port enters **err-disabled** state, sends SNMP trap and syslog. Requires manual `shutdown`/`no shutdown` or errdisable recovery to restore.\n2. **Restrict** — Drops violating frames, **increments the violation counter**, sends SNMP trap and syslog. Port stays up.\n3. **Protect** — Drops violating frames **silently**. No syslog, no SNMP trap, no violation counter increment. Port stays up.\n\nOnly Shutdown and Restrict generate notifications.",
      tags: ["port-security", "violation-modes"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-385",
      domain: "5.0",
      subdomain: "PortSecurity",
      front: "What is **sticky MAC** in port security?",
      back: "Sticky MAC dynamically learns MAC addresses and adds them to the **running configuration** as static secure MAC entries. Configured with `switchport port-security mac-address sticky`.\n\nThe switch converts dynamically learned MACs into `switchport port-security mac-address sticky <MAC>` lines. If you **save** the config (`write memory`), they persist across reboots. This avoids manually configuring allowed MACs while maintaining persistence.",
      tags: ["port-security", "sticky-mac"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Zone-Based Firewall ---
    {
      id: "starter-386",
      domain: "5.0",
      subdomain: "ZBF",
      front: "What are the key concepts in a **Zone-Based Firewall (ZBF)**?",
      back: "1. **Zone** — A group of interfaces with the same security level. Created with `zone security NAME`.\n2. **Zone-pair** — Defines the direction of traffic flow between two zones (source → destination). `zone-pair security NAME source Z1 destination Z2`.\n3. **Class-map** — Matches traffic using ACLs, protocols, or NBAR.\n4. **Policy-map** — Defines actions: **inspect** (stateful), **pass** (stateless permit), or **drop**.\n\nInterfaces in the **same zone** can communicate freely. Traffic between zones is **denied by default** until a policy is applied.",
      tags: ["zbf", "zone", "zone-pair", "firewall"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-387",
      domain: "5.0",
      subdomain: "ZBF",
      front: "What is the **self zone** in a Zone-Based Firewall?",
      back: "The **self zone** represents the router itself — traffic **to or from** the router's own interfaces (SSH, SNMP, routing protocols, ping, etc.).\n\nBy default, traffic **to and from the self zone is permitted**. To restrict it, you must create explicit zone-pairs involving the self zone (e.g., `zone-pair security TO-SELF source OUTSIDE destination self`).\n\nThe self zone cannot be deleted or renamed. It is system-defined.",
      tags: ["zbf", "self-zone", "firewall"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- VPN / IPsec ---
    {
      id: "starter-388",
      domain: "5.0",
      subdomain: "IPsec",
      front: "What are the **two phases** of IKE (Internet Key Exchange)?",
      back: "**Phase 1 (IKE SA)** — Establishes a secure, authenticated channel between peers. Negotiates: encryption, hashing, authentication method, DH group, and SA lifetime. Results in an **IKE SA** (bidirectional). Modes: Main Mode (6 messages) or Aggressive Mode (3 messages).\n\n**Phase 2 (IPsec SA)** — Uses the Phase 1 tunnel to negotiate the actual data protection. Negotiates: transform set (ESP/AH, encryption, hashing). Results in **two unidirectional IPsec SAs** (one per direction). Uses Quick Mode.",
      tags: ["ipsec", "ike", "vpn"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-389",
      domain: "5.0",
      subdomain: "IPsec",
      front: "What are the key differences between **IKEv1** and **IKEv2**?",
      back: "**IKEv2** improvements over IKEv1:\n- Fewer messages: **4 messages** to establish SA (vs 6+3 in IKEv1)\n- Built-in **NAT traversal** (NAT-T)\n- Built-in **EAP authentication** support\n- **MOBIKE** support for IP address changes\n- Anti-replay with **sequence numbers per SA**\n- More reliable: built-in **dead peer detection (DPD)**\n- No Aggressive Mode (eliminates identity exposure)\n\nIKEv2 uses UDP 500 and 4500 (NAT-T).",
      tags: ["ipsec", "ikev1", "ikev2", "vpn"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-390",
      domain: "5.0",
      subdomain: "IPsec",
      front: "Compare **crypto maps** vs **VTI (Virtual Tunnel Interface)** for IPsec.",
      back: "**Crypto maps** (legacy): Policy-based VPN. ACLs define interesting traffic. Applied to a physical interface. Complex with multiple peers. No support for dynamic routing over the tunnel.\n\n**VTI (Virtual Tunnel Interface)**: Route-based VPN. Creates a logical `Tunnel` interface. Supports **dynamic routing** (OSPF, EIGRP, BGP over the tunnel). Simpler configuration. Supports both static VTI (SVTI) and dynamic VTI (DVTI).\n\nVTI is the **modern preferred method** and is required for DMVPN.",
      tags: ["ipsec", "crypto-map", "vti", "vpn"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-391",
      domain: "5.0",
      subdomain: "IPsec",
      front: "How does **DMVPN** use IPsec, and what are the three DMVPN phases?",
      back: "DMVPN combines **mGRE + NHRP + IPsec** to build scalable hub-and-spoke VPNs with dynamic spoke-to-spoke tunnels.\n\n**Phase 1** — Hub-and-spoke only. All spoke traffic goes through the hub.\n**Phase 2** — Spoke-to-spoke tunnels via NHRP resolution. But routing forces spoke-to-spoke via hub (next-hop is hub).\n**Phase 3** — Spoke-to-spoke with **NHRP shortcuts and redirects**. Hub sends NHRP redirect, spokes build direct tunnels. Most scalable.\n\nIPsec protects the GRE tunnels using a shared IPsec profile.",
      tags: ["dmvpn", "ipsec", "nhrp", "vpn"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- MACsec ---
    {
      id: "starter-392",
      domain: "5.0",
      subdomain: "MACsec",
      front: "What is **MACsec (802.1AE)** and what does it protect?",
      back: "MACsec provides **hop-by-hop Layer 2 encryption** on Ethernet links. It encrypts everything after the Ethernet header (payload + EtherType) and adds an **integrity check value (ICV)**.\n\nProtects against: eavesdropping, tampering, and replay attacks at Layer 2. Unlike IPsec (Layer 3), MACsec operates **between adjacent devices** on the same LAN segment.\n\nKey management is handled by **MKA (MACsec Key Agreement) protocol** — IEEE 802.1X-2010.",
      tags: ["macsec", "802.1ae", "mka", "encryption"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-393",
      domain: "5.0",
      subdomain: "MACsec",
      front: "What is the difference between **should-secure** and **must-secure** MACsec policies?",
      back: "**Should-secure** — MACsec encryption is **preferred but not required**. If the peer does not support MACsec, traffic flows **unencrypted** (cleartext fallback). Useful during migration.\n\n**Must-secure** — MACsec encryption is **mandatory**. If MKA session fails or peer doesn't support MACsec, the link goes **down** (traffic is blocked). Used in high-security environments.\n\nConfigured under the MKA policy: `macsec-policy should-secure` or `must-secure`.",
      tags: ["macsec", "should-secure", "must-secure", "mka"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- uRPF ---
    {
      id: "starter-394",
      domain: "5.0",
      subdomain: "uRPF",
      front: "Compare **strict mode** vs **loose mode** uRPF (Unicast Reverse Path Forwarding).",
      back: "**Strict mode** — Source IP must be reachable via the **same interface** the packet arrived on (checks FIB for incoming interface match). Best for **single-homed** links. Drops asymmetric traffic.\n\n**Loose mode** — Source IP just needs to **exist in the FIB** (any interface). Doesn't care which interface. Best for **multi-homed** routers where asymmetric routing is expected. Blocks packets with completely bogus/unroutable source IPs.\n\nBoth prevent **IP spoofing**. Configured with: `ip verify unicast source reachable-via {rx|any}`.",
      tags: ["urpf", "antispoofing", "strict", "loose"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- IPv6 Security ---
    {
      id: "starter-395",
      domain: "5.0",
      subdomain: "IPv6Security",
      front: "What is **IPv6 RA Guard** and what attack does it prevent?",
      back: "RA Guard filters **Router Advertisement** messages on switch ports. It prevents **rogue RA attacks** where a malicious device sends fake RAs to become the default gateway or advertise incorrect prefixes/DNS.\n\nApplied per-port with a policy: ports connected to legitimate routers are set to `device-role router` (RAs allowed). Host ports use `device-role host` (RAs blocked).\n\nConfig: `ipv6 nd raguard policy NAME` → `device-role host` → apply to interface with `ipv6 nd raguard attach-policy NAME`.",
      tags: ["ipv6", "ra-guard", "ndp-security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-396",
      domain: "5.0",
      subdomain: "IPv6Security",
      front: "What are **DHCPv6 Guard** and **IPv6 ND Inspection**?",
      back: "**DHCPv6 Guard** — Filters DHCPv6 server messages on untrusted ports (similar to DHCP snooping for IPv4). Prevents rogue DHCPv6 servers. Policy-based: `device-role server` on trusted ports.\n\n**IPv6 ND Inspection** (also called IPv6 Snooping) — Validates **Neighbor Discovery** messages (NS/NA) against a binding table. Prevents **ND spoofing** (IPv6 equivalent of ARP poisoning). Builds a binding table from DAD, DHCP, and RA messages.\n\nBoth are part of the **IPv6 First Hop Security (FHS)** suite.",
      tags: ["ipv6", "dhcpv6-guard", "nd-inspection", "first-hop-security"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional Security cards ---
    {
      id: "starter-397",
      domain: "5.0",
      subdomain: "ACLs",
      front: "What is the purpose of the `established` keyword in an extended ACL?",
      back: "The `established` keyword (TCP only) matches packets with the **ACK or RST** bit set. It permits **return TCP traffic** for sessions that were initiated from the other direction.\n\nExample: `permit tcp any any established` allows inbound TCP replies but blocks inbound TCP SYN (new connections). This is a **stateless** approximation of return-traffic filtering. Reflexive ACLs provide a more robust stateful alternative.",
      tags: ["acl", "established", "tcp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-398",
      domain: "5.0",
      subdomain: "ZBF",
      front: "What are the three actions available in a ZBF policy-map, and how do they differ?",
      back: "1. **Inspect** — Enables **stateful** packet inspection. Creates session state and allows return traffic automatically. Supports application-layer inspection (HTTP, FTP, etc.).\n2. **Pass** — **Stateless** permit. Forwards packets without tracking sessions. Return traffic is NOT automatically allowed.\n3. **Drop** — Silently discards matching packets (default action for inter-zone traffic).\n\nFor most production deployments, **inspect** is preferred because it tracks sessions and dynamically permits return traffic.",
      tags: ["zbf", "inspect", "pass", "drop"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-399",
      domain: "5.0",
      subdomain: "IPsec",
      front: "What is the role of a **transform set** in IPsec?",
      back: "A transform set defines the **encryption and integrity algorithms** used to protect IPsec data traffic (Phase 2). It specifies:\n- **Protocol**: ESP (Encapsulating Security Payload) or AH (Authentication Header)\n- **Encryption**: AES-128, AES-256, etc. (ESP only)\n- **Integrity**: SHA-256, SHA-512, etc.\n\nExample: `crypto ipsec transform-set MY-TS esp-aes 256 esp-sha256-hmac`\n\nESP is used almost exclusively because AH doesn't support encryption and breaks with NAT.",
      tags: ["ipsec", "transform-set", "esp"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-400",
      domain: "5.0",
      subdomain: "DHCPSnooping",
      front: "How does DHCP snooping **rate limiting** work?",
      back: "Rate limiting restricts the number of **DHCP packets per second** on untrusted ports to prevent DHCP starvation attacks. Configured with: `ip dhcp snooping limit rate <pps>` (e.g., `rate 15`).\n\nIf the rate is exceeded, the port enters **err-disabled** state. Recovery requires manual `shutdown`/`no shutdown` or automatic errdisable recovery: `errdisable recovery cause dhcp-rate-limit`.\n\nTrusted ports are **not rate-limited** by default.",
      tags: ["dhcp-snooping", "rate-limit", "starvation"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-401",
      domain: "5.0",
      subdomain: "PortSecurity",
      front: "What is the default maximum number of MAC addresses allowed with port security, and how do you change it?",
      back: "The default maximum is **1 MAC address**. Change it with: `switchport port-security maximum <number>`.\n\nYou can specify different maximums per VLAN on trunk ports: `switchport port-security maximum 5 vlan 10`.\n\nPort security requires the port to be in **access** or **trunk** mode (not dynamic). Enable with: `switchport port-security` after setting `switchport mode access` or `switchport mode trunk`.",
      tags: ["port-security", "maximum", "mac"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-402",
      domain: "5.0",
      subdomain: "AAA",
      front: "Why is TACACS+ preferred over RADIUS for **device administration**?",
      back: "TACACS+ advantages for device administration:\n1. **Per-command authorization** — can permit/deny individual CLI commands (RADIUS cannot).\n2. **Full packet encryption** — entire body is encrypted (RADIUS only encrypts the password).\n3. **Separate AAA functions** — authentication, authorization, and accounting are independent; you can use different servers for each.\n4. **TCP transport** — reliable delivery, connection state awareness.\n\nRADIUS is better for **network access control** (802.1X) due to wider vendor support and attribute extensions (RADIUS VSAs).",
      tags: ["aaa", "tacacs", "device-admin"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-403",
      domain: "5.0",
      subdomain: "MACsec",
      front: "What is the **MKA (MACsec Key Agreement)** protocol?",
      back: "MKA is defined in **IEEE 802.1X-2010** and manages key negotiation for MACsec. It:\n- Discovers MACsec-capable peers\n- Elects a **Key Server** (lowest priority wins)\n- Generates and distributes the **SAK (Secure Association Key)** used for data encryption\n- Handles key rotation (rekeying)\n\nMKA uses **EAPoL-MKA** frames (EtherType 0x888E). The pre-shared key or 802.1X-derived key (MSK) is used as the **CAK (Connectivity Association Key)** which feeds MKA.",
      tags: ["macsec", "mka", "key-management"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // =========================================================
    // DOMAIN 6.0 — AUTOMATION (~20 cards)
    // =========================================================

    // --- JSON vs XML vs YAML ---
    {
      id: "starter-404",
      domain: "6.0",
      subdomain: "DataFormats",
      front: "Compare **JSON**, **XML**, and **YAML** — key syntax differences.",
      back: "**JSON**: Curly braces `{}` for objects, brackets `[]` for arrays. Keys in double quotes. No comments. Widely used with REST APIs.\n\n**XML**: Tag-based `<element>value</element>`. Verbose. Supports attributes and namespaces. Used by NETCONF.\n\n**YAML**: Indentation-based (no braces/brackets). Uses `:` for key-value, `-` for lists. Supports comments (`#`). Most human-readable. Used by Ansible playbooks.\n\nAll three are used for network automation data interchange.",
      tags: ["json", "xml", "yaml", "data-formats"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-405",
      domain: "6.0",
      subdomain: "DataFormats",
      front: "Which data format does each network management protocol typically use?",
      back: "- **NETCONF** → **XML** (exclusively; data encoded in XML, uses YANG models)\n- **RESTCONF** → **JSON or XML** (client specifies via Accept header: `application/yang-data+json` or `+xml`)\n- **gNMI** → **Protocol Buffers (protobuf)** over gRPC (binary encoding, very efficient)\n- **REST APIs** (DNA Center, Meraki) → **JSON** (most common for modern APIs)\n- **Ansible** → **YAML** (playbooks and inventory files)",
      tags: ["json", "xml", "yaml", "netconf", "restconf", "gnmi"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- DNA Center APIs ---
    {
      id: "starter-408",
      domain: "6.0",
      subdomain: "DNACenterAPI",
      front: "What are the main **Cisco DNA Center API** categories?",
      back: "1. **Intent API** — Abstracted, northbound REST APIs for common tasks (e.g., provision device, get client health). Simplifies complex workflows into single API calls.\n2. **Integration API** — Westbound APIs for integrating with ITSM tools (ServiceNow, etc.) and event notifications (webhooks).\n3. **Multivendor SDK** — Southbound for managing non-Cisco devices.\n4. **Command Runner API** — Executes CLI commands on devices and retrieves output. Useful for troubleshooting.\n\nAll use REST with JSON payloads and token-based authentication.",
      tags: ["dna-center", "api", "intent-api"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-409",
      domain: "6.0",
      subdomain: "DNACenter",
      front: "What are the **Template Editor** and **SWIM** features in DNA Center?",
      back: "**Template Editor** — Creates reusable configuration templates with **Jinja2/Velocity** syntax and variables. Templates can be versioned, assigned to devices or sites, and deployed as part of provisioning workflows. Supports day-0 (onboarding) and day-N (ongoing) templates.\n\n**SWIM (Software Image Management)** — Manages IOS images across the network. Features: golden image designation, compliance checking, image staging, and scheduled upgrades. Ensures all devices run the approved software version.",
      tags: ["dna-center", "template-editor", "swim"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Python depth ---
    {
      id: "starter-410",
      domain: "6.0",
      subdomain: "Python",
      front: "Compare **Paramiko**, **Netmiko**, **NAPALM**, and **Nornir** for network automation.",
      back: "**Paramiko** — Low-level Python SSH library. Raw SSH connections. You handle all I/O and parsing.\n\n**Netmiko** — Built on Paramiko. Simplifies SSH to network devices. Handles prompts, enable mode, send_command/send_config. Supports 50+ vendors.\n\n**NAPALM** — Vendor-neutral abstraction. Unified getters (get_interfaces, get_bgp_neighbors). Supports config replace/merge with diff and rollback. Uses Netmiko or vendor APIs underneath.\n\n**Nornir** — Python automation **framework** (like Ansible but pure Python). Inventory, task execution, threading. Uses Netmiko/NAPALM as plugins.",
      tags: ["python", "paramiko", "netmiko", "napalm", "nornir"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-411",
      domain: "6.0",
      subdomain: "Python",
      front: "How is the Python **requests** library used for network REST APIs?",
      back: "The `requests` library is the standard for HTTP/REST calls in Python.\n\nCommon pattern for DNA Center:\n```python\nimport requests\nresponse = requests.post(url, auth=(user, pwd), verify=False)\ntoken = response.json()['Token']\nheaders = {'X-Auth-Token': token}\ndevices = requests.get(api_url, headers=headers).json()\n```\n\nKey methods: `requests.get()`, `.post()`, `.put()`, `.patch()`, `.delete()`\nKey params: `headers=`, `json=` (auto-serializes), `params=` (query string), `verify=False` (skip TLS verify).",
      tags: ["python", "requests", "rest-api"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Ansible depth ---
    {
      id: "starter-412",
      domain: "6.0",
      subdomain: "Ansible",
      front: "Describe the structure of an **Ansible playbook** for network automation.",
      back: "An Ansible playbook is a **YAML file** with:\n- **Plays** — Target a group of hosts, set connection type (`network_cli`)\n- **Tasks** — Individual actions using **modules** (e.g., `cisco.ios.ios_config`, `cisco.ios.ios_command`)\n- **Variables** — Defined in inventory, group_vars, host_vars, or inline\n- **Handlers** — Triggered by `notify` (e.g., save config after change)\n\nKey network modules:\n- `ios_config` — Push configuration lines\n- `ios_command` — Run show commands\n- `ios_facts` — Gather device facts\n\nAnsible is **agentless** and uses SSH (`network_cli`) or NETCONF for IOS-XE.",
      tags: ["ansible", "playbook", "ios_config"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-413",
      domain: "6.0",
      subdomain: "Ansible",
      front: "What are **Ansible inventory** and **roles**?",
      back: "**Inventory** — Defines managed devices. Can be INI or YAML format. Groups devices logically (e.g., `[routers]`, `[switches]`). Variables can be assigned per host (`host_vars/`) or per group (`group_vars/`). Dynamic inventory plugins can pull from DNA Center or other sources.\n\n**Roles** — Reusable, structured collections of tasks, variables, templates, and handlers. Standard directory structure: `tasks/`, `handlers/`, `templates/`, `vars/`, `defaults/`, `meta/`. Roles promote code reuse and are shared via **Ansible Galaxy**.",
      tags: ["ansible", "inventory", "roles"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- RESTCONF ---
    {
      id: "starter-414",
      domain: "6.0",
      subdomain: "RESTCONF",
      front: "How do **RESTCONF URLs** map to YANG models?",
      back: "RESTCONF uses a hierarchical URL path that maps directly to the YANG model tree:\n\n`https://<host>/restconf/data/<module>:<container>/<list>=<key>`\n\nExample: `https://10.1.1.1/restconf/data/ietf-interfaces:interfaces/interface=GigabitEthernet1`\n\nMedia types:\n- `application/yang-data+json` — JSON encoding\n- `application/yang-data+xml` — XML encoding\n\nHTTP methods map to CRUD: GET=read, POST=create, PUT=create/replace, PATCH=merge/update, DELETE=remove.\n\nRESTCONF uses **HTTPS** (TCP 443) vs NETCONF's SSH (TCP 830).",
      tags: ["restconf", "yang", "rest-api"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-415",
      domain: "6.0",
      subdomain: "RESTCONF",
      front: "Compare **RESTCONF** vs **NETCONF** — key differences.",
      back: "| Feature | NETCONF | RESTCONF |\n|---|---|---|\n| Transport | SSH (TCP 830) | HTTPS (TCP 443) |\n| Encoding | XML only | JSON or XML |\n| Operations | get, get-config, edit-config, copy-config, lock/unlock | HTTP GET, POST, PUT, PATCH, DELETE |\n| Transactions | Full candidate/running datastores, commit/rollback | Per-resource, no native transaction |\n| Streaming | Notifications (RFC 5277) | SSE (Server-Sent Events) for notifications |\n| Use case | Full config management, bulk changes | Quick reads, targeted changes, integration |\n\nBoth use **YANG data models**. NETCONF is more powerful; RESTCONF is simpler for developers.",
      tags: ["restconf", "netconf", "comparison"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Git / CI/CD ---
    {
      id: "starter-416",
      domain: "6.0",
      subdomain: "Git",
      front: "How is **Git** used for network configuration management?",
      back: "Git provides **version control** for network configs, enabling:\n- **Change tracking** — Full history of who changed what and when\n- **Branching** — Test config changes in feature branches before merging to main\n- **Diff/review** — Review config changes via pull requests before deployment\n- **Rollback** — Revert to any previous config version with `git revert` or `git checkout`\n\n**CI/CD pipeline** for networking: Git push triggers automated validation (syntax check, compliance), then deployment tools (Ansible, Terraform, NSO) push configs to devices. This is **Infrastructure as Code (IaC)** / **NetDevOps**.",
      tags: ["git", "cicd", "netdevops", "iac"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

    // --- Additional automation cards ---
    {
      id: "starter-418",
      domain: "6.0",
      subdomain: "Python",
      front: "What is the difference between `send_command()` and `send_config_set()` in **Netmiko**?",
      back: "**`send_command()`** — Sends a single **exec/show** command and waits for the prompt to return. Used for read operations. Returns the command output as a string.\n\n**`send_config_set()`** — Sends a **list of configuration commands**. Automatically enters config mode (`configure terminal`), sends each line, and exits config mode. Used for write operations.\n\nExample:\n```python\noutput = net_connect.send_command('show ip route')\nnet_connect.send_config_set(['interface Gi1', 'description WAN'])\n```",
      tags: ["python", "netmiko", "send_command"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-419",
      domain: "6.0",
      subdomain: "DNACenterAPI",
      front: "How does **authentication** work with the Cisco DNA Center API?",
      back: "DNA Center uses **token-based authentication**:\n1. Send a **POST** to `/dna/system/api/v1/auth/token` with HTTP Basic Auth (username:password in Base64).\n2. Receive a **JWT token** in the response.\n3. Include the token in all subsequent requests as: `X-Auth-Token: <token>` header.\n\nToken lifetime is **60 minutes** by default. After expiry, you must re-authenticate. All API calls use **HTTPS**.",
      tags: ["dna-center", "api", "authentication", "token"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-420",
      domain: "6.0",
      subdomain: "Ansible",
      front: "What connection type does Ansible use for IOS-XE devices and why is it different from server management?",
      back: "Ansible uses `ansible_connection: ansible.netcommon.network_cli` for IOS-XE devices (SSH-based, sends CLI commands).\n\nUnlike server management (which uses `ssh` + Python on the target), network devices **don't run Python locally**. So Ansible:\n- Runs all modules **on the control node** (not on the device)\n- Sends commands over SSH/NETCONF to the device\n- Can also use `ansible_connection: ansible.netcommon.netconf` for YANG-based management\n\nSet `ansible_network_os: cisco.ios.ios` to tell Ansible the platform.",
      tags: ["ansible", "network_cli", "connection"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-421",
      domain: "6.0",
      subdomain: "Git",
      front: "What are the basic **Git commands** relevant to network configuration management?",
      back: "Core workflow:\n- `git init` — Initialize a repo\n- `git clone` — Copy a remote repo\n- `git add` — Stage files for commit\n- `git commit -m` — Save staged changes with a message\n- `git push` — Upload commits to remote\n- `git pull` — Download and merge remote changes\n- `git branch` / `git checkout -b` — Create/switch branches\n- `git merge` — Combine branches\n- `git diff` — Show changes between commits/working tree\n- `git log` — View commit history\n\nFor network configs: each device config is a file, changes are tracked per-commit.",
      tags: ["git", "version-control", "commands"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-422",
      domain: "6.0",
      subdomain: "DataFormats",
      front: "What are the key differences between **JSON** and **YAML** for network automation?",
      back: "**JSON**:\n- Strict syntax: double quotes required, no trailing commas\n- No comments allowed\n- Native to JavaScript and REST APIs\n- Parsing is unambiguous\n- Used by: RESTCONF, DNA Center API, most REST APIs\n\n**YAML**:\n- Indentation-based (spaces only, no tabs)\n- Comments allowed (`#`)\n- More human-readable/writable\n- Superset of JSON (valid JSON is valid YAML)\n- Used by: Ansible playbooks, Docker Compose, Kubernetes\n\nFor ENCOR: know that Ansible uses YAML, APIs use JSON, NETCONF uses XML.",
      tags: ["json", "yaml", "data-formats", "comparison"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },
    {
      id: "starter-423",
      domain: "6.0",
      subdomain: "DNACenter",
      front: "What is the **Command Runner** API in DNA Center and when would you use it?",
      back: "Command Runner allows you to execute **read-only CLI commands** (show commands) on network devices via REST API. Useful for:\n- **Troubleshooting** without SSH access to individual devices\n- **Bulk data collection** across many devices programmatically\n- **Integration** with monitoring tools\n\nAPI flow:\n1. POST to `/dna/intent/api/v1/network-device-poller/cli/read-request` with device IDs and commands\n2. Returns a **task ID**\n3. Poll the task API until completion\n4. Retrieve output from the file API\n\nDoes NOT support config commands (write) for safety.",
      tags: ["dna-center", "command-runner", "api"],
      notes: "",
      source: "starter",
      created_at: "2026-04-09T00:00:00.000Z",
      sr_data: {
        easiness_factor: 2.5,
        interval_days: 0,
        repetitions: 0,
        next_review_date: new Date().toISOString().split('T')[0],
        last_review_date: null,
      },
      stats: { times_seen: 0, times_correct: 0, streak: 0 },
    },

  ];
}
